import { createContext, useContext, useState, ReactNode } from "react";

export type Language = "en" | "nl" | "fr" | "de";

export const languageOptions: { code: Language; label: string; flag: string }[] = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Dashboard sidebar
    "nav.calendar": "Calendar",
    "nav.reservations": "Reservations",
    "nav.services": "Services",
    "nav.customers": "Customers",
    "nav.blocked": "Blocked Times",
    "nav.upsells": "Upsells",
    "nav.settings": "Settings",
    // Shop booking
    "shop.title": "Book your appointment online",
    "shop.step.service": "Service",
    "shop.step.employee": "Employee",
    "shop.choose_employee": "Choose your specialist",
    "shop.no_preference": "No preference",
    "shop.no_preference_desc": "We'll assign the best available specialist.",
    "shop.no_employees_available": "No specialists available for this service.",
    "shop.step.datetime": "Date & Time",
    "shop.step.details": "Details",
    "shop.step.confirmed": "Confirmed",
    "shop.choose_service": "Choose a service",
    "shop.add_options": "Add options:",
    "shop.required_options": "Choose an option (required)",
    "shop.extra_cost_time": "+€{price} · +{duration} min",
    "shop.total": "Total",
    "shop.continue": "Continue",
    "shop.back": "Back",
    "shop.pick_datetime": "Pick a date & time",
    "shop.available_times": "Available times for",
    "shop.no_timeslots": "No available time slots. Please select another date.",
    "shop.slot_taken": "This timeslot is no longer available. Please choose another time.",
    "shop.your_details": "Your details",
    "shop.service": "Service",
    "shop.options": "Options",
    "shop.date_time": "Date & Time",
    "shop.first_name": "First name",
    "shop.last_name": "Last name",
    "shop.email": "Email",
    "shop.phone": "Phone",
    "shop.confirm_booking": "Confirm Booking",
    "shop.booking_confirmed": "Booking Confirmed!",
    "shop.thank_you": "Thank you, {name}! A confirmation has been sent to {email}.",
    "shop.reminder": "You'll receive a reminder on the day of your appointment.",
    "calendar.add_google": "Add to Google Calendar",
    "calendar.add_apple": "Add to Apple Calendar",
    "shop.book_another": "Book another appointment",
    "shop.rate_limit_error": "You've made too many bookings in a short time. Please wait a moment and try again.",
    "shop.booking_error": "Failed to create booking. Please try again.",
    "shop.payment_checking": "Checking payment status...",
    "shop.payment_success_title": "Payment Successful!",
    "shop.payment_success_desc": "Your booking has been confirmed. A confirmation email has been sent.",
    "shop.payment_failed_title": "Payment Failed",
    "shop.payment_failed_desc": "Your payment could not be processed. Please try again.",
    "shop.payment_try_again": "Try again",
    "shop.payment_pending_title": "Payment Processing",
    "shop.payment_pending_desc": "Your payment is still being processed. You'll receive a confirmation email once complete.",
    "shop.payment_unknown_title": "Something went wrong",
    "shop.payment_unknown_desc": "We couldn't verify your payment. Please contact us if you need assistance.",
    "shop.pay_and_book": "Pay & Book",
    "shop.upsell_step": "Add-ons",
    "shop.upsell_required": "Selection required",
    "shop.upsell_optional": "Optional",
    "shop.upsell_skip": "Skip",
    "shop.unavailable_title": "Shop unavailable",
    "shop.unavailable_description": "This booking page is currently not accepting new appointments.",
    // Settings
    "settings.title": "Settings",
    "settings.back": "Back to Settings",
     "settings.organisation": "Organisation settings",
    "settings.organisation_desc": "Company name, address, contact info, CoC & VAT numbers",
    "settings.opening_hours": "Opening Hours",
    "settings.opening_hours_desc": "Set your weekly schedule and closed days",
    "settings.booking": "Booking settings",
    "settings.booking_desc": "Time slot intervals and checkout field configuration",
    "settings.checkout": "Checkout Fields",
    "settings.checkout_desc": "Custom fields customers fill in during checkout",
    "settings.embed": "Embed Widget",
    "settings.embed_desc": "Get the iframe code to embed bookings on your website",
    "settings.shop": "Shop settings",
    "settings.shop_desc": "Logo, branding and shop appearance settings",
    "settings.billing": "Billing & Subscription",
    "settings.billing_desc": "Manage your subscription, payment method and invoices",
    "settings.language": "Language",
    "settings.profile": "Profile settings",
    "settings.profile_desc": "Personal details, password and appearance preferences",
    // Onboarding
    "onboarding.org_title": "Organisation Details",
    "onboarding.org_desc": "Tell us about your business",
    "onboarding.company_name": "Company Name *",
    "onboarding.company_placeholder": "Your business name",
    "onboarding.email": "Email",
    "onboarding.phone": "Phone",
    "onboarding.address": "Address",
    "onboarding.postal_code": "Postal Code",
    "onboarding.city": "City",
    "onboarding.country": "Country",
    "onboarding.coc": "CoC Number",
    "onboarding.vat": "VAT Number",
    "onboarding.continue": "Continue",
    "onboarding.hours_title": "Opening Hours",
    "onboarding.hours_desc": "Set your weekly schedule",
    "onboarding.to": "to",
    "onboarding.closed": "Closed",
    "onboarding.back": "Back",
    "onboarding.finishing": "Setting up…",
    "onboarding.finish": "Finish Setup",
    "onboarding.name_required": "Company name is required.",
    "onboarding.success": "Your organisation is set up!",
    "onboarding.error": "Something went wrong.",
    "onboarding.mon": "Monday",
    "onboarding.tue": "Tuesday",
    "onboarding.wed": "Wednesday",
    "onboarding.thu": "Thursday",
    "onboarding.fri": "Friday",
    "onboarding.sat": "Saturday",
    "onboarding.sun": "Sunday",
    // Auth pages
    "auth.sign_in": "Sign in",
    "auth.sign_in_desc": "Enter your credentials to access your dashboard",
    "auth.sign_up": "Create account",
    "auth.sign_up_desc": "Sign up to start managing your bookings",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.first_name": "First name",
    "auth.last_name": "Last name",
    "auth.signing_in": "Signing in…",
    "auth.creating": "Creating account…",
    "auth.no_account": "Don't have an account?",
    "auth.have_account": "Already have an account?",
    "auth.welcome_back": "Welcome back",
    "auth.welcome_back_desc": "Manage your bookings, services, and customers all in one place.",
    "auth.start_free": "Start for free",
    "auth.start_free_desc": "Set up your booking system in minutes. No credit card required.",
    "auth.verify_title": "Check your email",
    "auth.verify_desc": "We've sent you a verification link. Please check your inbox and click the link to verify your account.",
    "auth.back_to_login": "Back to Sign in",
    "auth.error_duplicate": "An account with this email already exists.",
    "auth.accept_terms_pre": "I agree to the",
    "auth.terms_link": "Terms & Conditions",
    "auth.accept_terms_and": "and",
    "auth.privacy_link": "Privacy Policy",
    "auth.accept_terms_error": "You must accept the Terms & Conditions and Privacy Policy to create an account.",
    "auth.forgot_password": "Forgot password?",
    "auth.forgot_title": "Reset password",
    "auth.forgot_desc": "Enter your email and we'll send you a reset link.",
    "auth.forgot_hero_desc": "Don't worry, it happens to the best of us.",
    "auth.send_reset_link": "Send reset link",
    "auth.sending": "Sending…",
    "auth.forgot_sent_title": "Check your email",
    "auth.forgot_sent_desc": "If an account exists with that email, we've sent a password reset link.",
    "auth.reset_title": "Set new password",
    "auth.reset_desc": "Enter your new password below.",
    "auth.reset_hero_desc": "Choose a strong password to keep your account secure.",
    "auth.new_password": "New password",
    "auth.confirm_password": "Confirm password",
    "auth.update_password": "Update password",
    "auth.updating": "Updating…",
    "auth.password_updated": "Password updated!",
    "auth.redirecting": "Redirecting to your dashboard…",
    "auth.passwords_mismatch": "Passwords do not match.",
    "auth.password_too_short": "Password must be at least 6 characters.",
    // Booking settings
    "booking.interval": "Time Slot Interval",
    "booking.days": "Days",
    "booking.hours": "Hours",
    "booking.minutes_label": "Minutes",
    "booking.window_title": "Booking Window",
    "booking.auto_confirm_label": "Auto-confirm bookings",
    "booking.auto_confirm_desc": "Automatically set new bookings to confirmed instead of pending.",
    "booking.window_desc": "Control how far in advance customers can book appointments.",
    "booking.min_lead_label": "Minimum lead time",
    "booking.min_lead_desc": "Customers must book at least this far in advance.",
    "booking.max_advance_label": "Maximum advance time",
    "booking.max_advance_desc": "Customers cannot book further ahead than this. Leave empty for no limit.",
    "booking.min_lead_snap": "If lead time crosses into a new day, open bookings from the start of that day",
    "booking.max_advance_snap": "If advance time ends on a day, allow bookings until the end of that day",
    "booking.save": "Save Settings",
    // Blocked times
    "blocked.title": "Blocked Times",
    "blocked.block_time": "Block Time",
    "blocked.edit": "Edit",
    "blocked.edit_title": "Edit Blocked Time",
    "blocked.back": "Back to Blocked Times",
    "blocked.no_items": "No blocked times yet.",
    "blocked.type_single_day": "Full Day",
    "blocked.type_date_range": "Date Range",
    "blocked.type_time_range": "Time Range",
    "blocked.type_datetime_range": "Date & Time Range",
    "blocked.type_daily_time_range": "Daily Time Block",
    "blocked.desc_single_day": "Block an entire day",
    "blocked.desc_date_range": "Block multiple consecutive days",
    "blocked.desc_time_range": "Block specific hours on a day",
    "blocked.desc_datetime_range": "Block hours across multiple days",
    "blocked.desc_daily_time_range": "Block the same hours on each day in a range",
    "blocked.block_type": "Block type",
    "blocked.select_date": "Select date",
    "blocked.select_dates": "Select dates",
    "blocked.select_time": "Select time",
    "blocked.start_time": "Start time",
    "blocked.end_time": "End time",
    "blocked.summary": "Summary",
    "blocked.type": "Type",
    "blocked.date": "Date",
    "blocked.start_date": "Start date",
    "blocked.end_date": "End date",
    "blocked.time": "Time",
    "blocked.select_date_hint": "Select a date to see the summary.",
    "blocked.status": "Status",
    "blocked.status_active": "This block is currently active",
    "blocked.status_inactive": "This block is inactive and won't apply",
    "blocked.saving": "Saving…",
    "blocked.save_changes": "Save Changes",
    "blocked.cancel": "Cancel",
    "blocked.created": "Blocked time created!",
    "blocked.updated": "Blocked time updated!",
    "blocked.activated": "Blocked time activated",
    "blocked.deactivated": "Blocked time deactivated",
    "blocked.deleted": "Blocked time deleted.",
    "blocked.delete_title": "Delete blocked time",
    "blocked.delete_desc": "Are you sure you want to delete this blocked time? This action cannot be undone.",
    "blocked.delete_confirm": "Delete",
    // Customers
    "customers.title": "Customers",
    "customers.total": "total",
    "customers.search": "Search customers...",
    "customers.no_items": "No customers yet. They'll appear here once bookings come in.",
    "customers.not_found": "Customer not found.",
    "customers.back": "Back to Customers",
    "customers.delete": "Delete",
    "customers.delete_title": "Delete customer",
    "customers.delete_desc_pre": "Are you sure you want to delete",
    "customers.delete_desc_post": "? This action cannot be undone.",
    "customers.deleted": "deleted.",
    "customers.cancel": "Cancel",
    "customers.total_bookings": "Total Bookings",
    "customers.total_spent": "Total Spent",
    "customers.customer_since": "Customer Since",
    "customers.reservation_history": "Reservation History",
    "customers.no_reservations": "No reservations found.",
    "customers.col_customer": "Customer",
    "customers.col_joined": "Joined",
    "customers.col_actions": "Actions",
    "customers.col_service": "Service",
    "customers.col_date": "Date",
    "customers.col_price": "Price",
    "customers.col_status": "Status",
    // Services
    "services.title": "Services",
    "services.add": "Add Service",
    "services.no_items": "No services yet. Create your first service to get started.",
    "services.col_service": "Service",
    "services.col_price": "Price",
    "services.col_duration": "Duration",
    "services.col_options": "Options",
    "services.col_status": "Status",
    "services.col_actions": "Actions",
    "services.active": "Active",
    "services.inactive": "Inactive",
    "services.edit": "Edit",
    "services.delete": "Delete",
    "services.delete_title": "Delete service",
    "services.delete_desc_pre": "Are you sure you want to delete",
    "services.delete_desc_post": "? This action cannot be undone.",
    "services.deleted": "has been deleted.",
    "services.cancel": "Cancel",
    "services.back": "Back to Services",
    "services.edit_title": "Edit Service",
    "services.create_title": "Create Service",
    "services.details": "Service Details",
    "services.name_label": "Service name *",
    "services.name_placeholder": "e.g. Haircut",
    "services.description_label": "Description",
    "services.description_placeholder": "Describe what this service includes...",
    "services.price_label": "Price (€) *",
    "services.duration_label": "Duration (minutes) *",
    "services.active_label": "Active",
    "services.active_desc": "This service is visible in the shop",
    "services.inactive_desc": "This service is hidden from the shop",
    "services.options_title": "Options",
    "services.options_desc": "Add optional extras customers can select during booking",
    "services.add_option": "Add Option",
    "services.selection_settings": "Selection settings",
    "services.selection_mode": "Selection mode",
    "services.mode_single": "Single — customer picks one option",
    "services.mode_multiple": "Multiple — customer picks any combination",
    "services.required": "Required",
    "services.required_desc": "Customer must select at least one option",
    "services.optional_desc": "Options are optional",
    "services.no_options": "No options yet. Add one to offer extras.",
    "services.option_label": "Option",
    "services.option_name": "Name",
    "services.option_name_placeholder": "e.g. Beard trim add-on",
    "services.option_price": "Extra price (€)",
    "services.option_duration": "Extra duration (min)",
    "services.saving": "Saving…",
    "services.save_changes": "Save Changes",
    "services.create": "Create Service",
    "services.updated": "Service updated!",
    "services.created": "Service created!",
    "services.fill_required": "Please fill in all required fields.",
    // Upsells
    "upsells.title": "Upsells",
    "upsells.add": "Add Upsell",
    "upsells.no_items": "No upsells yet. Create reusable upsell steps to attach to your services.",
    "upsells.back": "Back to Upsells",
    "upsells.edit_title": "Edit Upsell",
    "upsells.create_title": "Create Upsell",
    "upsells.name_label": "Upsell name *",
    "upsells.name_placeholder": "e.g. Hair treatment upgrade",
    "upsells.settings": "Settings",
    "upsells.selection_mode": "Selection mode",
    "upsells.mode_single": "Single — customer picks one option",
    "upsells.mode_multiple": "Multiple — customer picks any combination",
    "upsells.required": "Required",
    "upsells.required_desc": "Customer must select at least one option",
    "upsells.optional_desc": "Selection is optional",
    "upsells.options_title": "Options",
    "upsells.options_desc": "Add options customers can choose from in this upsell step",
    "upsells.add_option": "Add Option",
    "upsells.no_options": "No options yet. Add one to offer extras.",
    "upsells.option_label": "Option",
    "upsells.option_name": "Name",
    "upsells.option_name_placeholder": "e.g. Deep conditioning",
    "upsells.option_price": "Extra price (€)",
    "upsells.option_duration": "Extra duration (min)",
    "upsells.saving": "Saving…",
    "upsells.save_changes": "Save Changes",
    "upsells.create": "Create Upsell",
    "upsells.updated": "Upsell updated!",
    "upsells.created": "Upsell created!",
    "upsells.fill_required": "Please fill in the upsell name.",
    "upsells.delete": "Delete",
    "upsells.delete_title": "Delete upsell",
    "upsells.delete_desc_pre": "Are you sure you want to delete",
    "upsells.delete_desc_post": "? This will also remove it from all linked services.",
    "upsells.deleted": "has been deleted.",
    "upsells.cancel": "Cancel",
    "upsells.col_name": "Name",
    "upsells.col_options": "Options",
    "upsells.col_required": "Required",
    "upsells.col_mode": "Mode",
    "upsells.col_actions": "Actions",
    "upsells.edit": "Edit",
    "upsells.yes": "Yes",
    "upsells.no": "No",
    "upsells.single": "Single",
    "upsells.multiple": "Multiple",
    "upsells.used_by": "Used by {count} services",
    // Service upsell linking
    "services.upsells_title": "Upsell Steps",
    "services.upsells_desc": "Attach reusable upsell steps shown to customers after selecting this service",
    "services.add_upsell": "Add Upsell",
    "services.no_upsells": "No upsell steps attached. Add one from your upsell library.",
    "services.manage_upsells": "Manage Upsells",
    "services.select_upsell": "Select an upsell to add…",
    "services.upsell_already_added": "This upsell is already attached.",
    // Reservations
    "reservations.title": "Reservations",
    "reservations.total": "total",
    "reservations.search": "Search reservations...",
    "reservations.filter_date": "Filter by date",
    "reservations.no_items": "No reservations yet. They'll appear here once customers start booking.",
    "reservations.col_customer": "Customer",
    "reservations.col_service": "Service",
    "reservations.col_datetime": "Date & Time",
    "reservations.col_status": "Status",
    "reservations.col_price": "Price",
    "reservations.col_actions": "Actions",
    "reservations.status_all": "All Status",
    "reservations.status_confirmed": "Confirmed",
    "reservations.status_pending": "Pending",
    "reservations.status_cancelled": "Cancelled",
    "reservations.status_completed": "Completed",
    "reservations.status_no_show": "No Show",
    "reservations.status_awaiting_payment": "Awaiting Payment",
    "reservations.status_expired": "Expired",
    "reservations.delete_title": "Delete reservation",
    "reservations.delete_desc": "Are you sure you want to delete this reservation for",
    "reservations.delete_undone": "? This action cannot be undone.",
    "reservations.deleted": "Reservation deleted.",
    "reservations.cancel": "Cancel",
    "reservations.delete": "Delete",
    "reservations.reset_filters": "Reset filters",
    "reservations.back": "Back to Reservations",
    "reservations.not_found": "Reservation not found.",
    "reservations.booked_on": "Booked on",
    "reservations.at": "at",
    "reservations.reschedule": "Reschedule",
    "reservations.customer": "Customer",
    "reservations.view_profile": "View customer profile",
    "reservations.appointment": "Appointment",
    "reservations.date": "Date",
    "reservations.time": "Time",
    "reservations.duration": "Duration",
    "reservations.service": "Service",
    "reservations.options": "Options",
    "reservations.payment": "Payment",
    "reservations.total_amount": "Total",
    "reservations.payment_status": "Payment status",
    "reservations.free": "Free",
    "reservations.pay_at_location": "Pay at location",
    "reservations.paid_online": "Paid online",
    "reservations.notes": "Internal Notes",
    "reservations.notes_placeholder": "Add a note… (only visible to staff)",
    "reservations.no_notes": "No notes yet.",
    "reservations.note_added": "Note added",
    "reservations.note_updated": "Note updated",
    "reservations.note_deleted": "Note deleted",
    "reservations.activity": "Activity",
    "reservations.created_label": "Reservation created",
    "reservations.rescheduled_label": "Reservation rescheduled",
    "reservations.rescheduled_from": "From",
    "reservations.rescheduled_to": "to",
    "reservations.cancelled_label": "Reservation cancelled",
    "reservations.booked_by": "Booked by",
    "reservations.for": "for",
    "reservations.status_change_title": "Change reservation status",
    "reservations.status_change_desc_pre": "Are you sure you want to change the status from",
    "reservations.status_change_desc_to": "to",
    "reservations.confirm": "Confirm",
    "reservations.status_changed_to": "Status changed to",
    "reservations.changed_from": "Changed from",
    "reservations.status_updated_to": "Status updated to",
    "reservations.note_added_label": "Internal note added",
    "reservations.reschedule_title": "Reschedule Reservation",
    "reservations.reschedule_current": "Currently booked:",
    "reservations.reschedule_available": "Available on",
    "reservations.reschedule_no_slots": "No available time slots on this date.",
    "reservations.reschedule_confirm": "Confirm Reschedule",
    "reservations.reschedule_failed": "Failed to reschedule reservation.",
    "reservations.reschedule_success": "Reservation rescheduled to",
    "reservations.this_customer": "this customer",
    "calendar.title": "Calendar",
    "calendar.month": "Month",
    "calendar.week": "Week",
    "calendar.day": "Day",
    "calendar.today": "Today",
    "calendar.mon": "Mon",
    "calendar.tue": "Tue",
    "calendar.wed": "Wed",
    "calendar.thu": "Thu",
    "calendar.fri": "Fri",
    "calendar.sat": "Sat",
    "calendar.sun": "Sun",
    "calendar.more": "more",
    "calendar.close": "Close",
    "calendar.view_details": "View details",
    "banner.payment_failed": "Payment failed — your account is limited.",
    "banner.update_payment": "Update payment",
    "banner.cancelled": "Your subscription has been cancelled. Access ends in {days} day(s).",
    "banner.reactivate": "Reactivate",
    "banner.trial": "{days} day(s) left in your free trial.",
    "banner.trial_expired": "Your free trial has expired.",
    "banner.setup_payment": "Set up payment",
    // Settings - Organisation
    "org.title": "Organisation Details",
    "org.company_name": "Company Name",
    "org.email": "Email",
    "org.phone": "Phone",
    "org.address": "Address",
    "org.postal_code": "Postal Code",
    "org.city": "City",
    "org.country": "Country",
    "org.coc_number": "CoC Number",
    "org.vat_number": "VAT Number",
    "org.saved": "Organisation details saved!",
    "org.saving": "Saving…",
    "org.save": "Save Changes",
    // Settings - Opening Hours
    "hours.title": "Opening Hours",
    "hours.monday": "Monday",
    "hours.tuesday": "Tuesday",
    "hours.wednesday": "Wednesday",
    "hours.thursday": "Thursday",
    "hours.friday": "Friday",
    "hours.saturday": "Saturday",
    "hours.sunday": "Sunday",
    "hours.to": "to",
    "hours.closed": "Closed",
    "hours.saved": "Opening hours saved!",
    "hours.saving": "Saving…",
    "hours.save": "Save Hours",
    // Settings - Checkout
    "checkout.title": "Checkout Fields",
    "checkout.desc": "Define custom fields customers fill in during checkout.",
    "checkout.fields": "Fields",
    "checkout.add_field": "Add Field",
    "checkout.no_fields": "No checkout fields configured. Add one to collect extra info.",
    "checkout.field_num": "Field",
    "checkout.required": "Required",
    "checkout.label": "Label *",
    "checkout.label_placeholder": "e.g. Phone number",
    "checkout.type": "Type",
    "checkout.placeholder": "Placeholder",
    "checkout.placeholder_text": "Placeholder text...",
    "checkout.dropdown_options": "Dropdown options (comma-separated)",
    "checkout.dropdown_placeholder": "Option 1, Option 2, Option 3",
    "checkout.label_required": "All fields must have a label.",
    "checkout.saved": "Checkout fields saved!",
    "checkout.load_failed": "Failed to load checkout fields",
    "checkout.save": "Save Changes",
    "checkout.type_text": "Text",
    "checkout.type_email": "Email",
    "checkout.type_phone": "Phone number",
    "checkout.type_number": "Number",
    "checkout.type_textarea": "Long text",
    "checkout.type_select": "Dropdown",
    "checkout.type_checkbox": "Checkbox",
    // Settings - Embed
    "embed.title": "Embed Booking Widget",
    "embed.desc": "Copy this code to embed the booking flow on your website.",
    "embed.copy": "Copy Code",
    "embed.copied": "Embed code copied to clipboard!",
    // Settings - Shop
    "shopset.title": "Shop Settings",
    "shopset.save_changes": "Save changes",
    "shopset.logo": "Logo",
    "shopset.logo_desc": "Upload a logo for light and dark mode. If no logo is uploaded, your company name will be shown instead.",
    "shopset.light_logo": "Light mode logo",
    "shopset.dark_logo": "Dark mode logo",
    "shopset.logo_size": "Logo size",
    "shopset.colors": "Colors",
    "shopset.colors_desc": "Customize button, text, and background colors. Leave empty to use defaults.",
    "shopset.light_mode": "Light Mode",
    "shopset.dark_mode": "Dark Mode",
    "shopset.btn_color": "Button color",
    "shopset.btn_text_color": "Button text color",
    "shopset.text_color": "Text color",
    "shopset.bg_color": "Background color",
    "shopset.card_bg": "Card background",
    "shopset.typography": "Typography",
    "shopset.typography_desc": "Choose a font family and size for your booking page.",
    "shopset.font_family": "Font family",
    "shopset.font_size": "Font size",
    "shopset.unsaved": "You have unsaved changes",
    "shopset.discard": "Discard",
    "shopset.saved": "Shop settings saved!",
    "shopset.uploading": "Uploading…",
    "shopset.upload": "Upload",
    "shopset.logo_removed": "Logo removed.",
    "shopset.size_small": "Small",
    "shopset.size_medium": "Medium",
    "shopset.size_large": "Large",
    "shopset.size_xlarge": "Extra Large",
    "shopset.font_small": "Small (14px)",
    "shopset.font_medium": "Medium (16px)",
    "shopset.font_large": "Large (18px)",
    "shopset.preview_light": "Light mode",
    "shopset.preview_dark": "Dark mode",
    "shopset.preview_service": "Service name",
    "shopset.preview_desc": "Short description of the service.",
    "shopset.preview_book": "Book now",
    // Settings - Billing
    "billing.title": "Billing & Subscription",
    "billing.subscription": "Subscription",
    "billing.payment_failed": "Payment failed",
    "billing.payment_failed_desc": "Your last payment failed. Your account is limited to billing access only. Please update your payment method to restore full access.",
    "billing.update_payment": "Update payment method",
    "billing.status": "Status",
    "billing.free_trial": "Free trial",
    "billing.trial_expired_label": "Trial expired",
    "billing.trial_expired_title": "Your free trial has expired",
    "billing.trial_expired_desc": "Set up a subscription to continue using all features.",
    "billing.trial_ends": "Trial ends",
    "billing.days_left": "days left",
    "billing.plan": "Plan",
    "billing.next_payment": "Next payment",
    "billing.start_sub_desc": "Start your subscription now to continue using Slotify after your trial ends.",
    "billing.setup_payment": "Set up payment",
    "billing.active_msg": "Your subscription is active. Payments are processed automatically.",
    "billing.cancel_sub": "Cancel subscription",
    "billing.cancel_title": "Cancel subscription?",
    "billing.cancel_desc": "Are you sure you want to cancel your Slotify subscription? Your account will lose access to all features immediately. This action cannot be undone.",
    "billing.keep_sub": "Keep subscription",
    "billing.yes_cancel": "Yes, cancel",
    "billing.cancelled_msg": "Your subscription has been cancelled.",
    "billing.reactivate": "Reactivate subscription",
    "billing.invoices": "Invoices",
    "billing.no_invoices": "No invoices yet.",
    "billing.start_failed": "Failed to start subscription. Please try again.",
    "billing.cancel_success": "Your subscription has been cancelled.",
    "billing.cancel_failed": "Failed to cancel subscription. Please try again.",
    "billing.download_failed": "Failed to download invoice.",
    "billing.download": "Download invoice",
    // Settings - Profile
    "profile.title": "Profile Settings",
    "profile.personal": "Personal Details",
    "profile.first_name": "First name",
    "profile.last_name": "Last name",
    "profile.email": "Email",
    "profile.save_details": "Save Details",
    "profile.saved": "Profile updated!",
    "profile.change_password": "Change Password",
    "profile.current_password": "Current password",
    "profile.new_password": "New password",
    "profile.confirm_password": "Confirm new password",
    "profile.update_password": "Update Password",
    "profile.password_updated": "Password updated!",
    "profile.password_mismatch": "Passwords do not match.",
    "profile.password_short": "Password must be at least 6 characters.",
    "profile.appearance": "Appearance",
    "profile.theme": "Theme",
    "profile.theme_light": "Light",
    "profile.theme_dark": "Dark",
    "profile.theme_system": "System",
    "profile.save_appearance": "Save Appearance",
    "profile.appearance_saved": "Appearance updated!",
    "profile.saving": "Saving…",
    "profile.updating": "Updating…",
    // Landing page
    "landing.hero_badge": "✨ The modern booking platform for businesses",
    "landing.hero_title_1": "Bookings made ",
    "landing.hero_title_highlight": "effortless",
    "landing.hero_desc": "Empower your business with a beautiful booking system. Let customers schedule appointments 24/7 while you focus on what matters most — growing your business.",
    "landing.hero_cta": "Start Your Free 30-Day Trial",
    "landing.hero_pricing": "View Pricing",
    "landing.nav_features": "Features",
    "landing.nav_how": "How it Works",
    "landing.nav_testimonials": "Testimonials",
    "landing.nav_pricing": "Pricing",
    "landing.nav_extensions": "Extensions",
    "landing.nav_login": "Log in",
    "landing.nav_signup": "Start Free",
    "landing.features_label": "Features",
    "landing.features_title": "Everything you need to manage bookings",
    "landing.features_desc": "A complete toolkit designed for service businesses of any size.",
    "landing.feat_calendar": "Smart Calendar",
    "landing.feat_calendar_desc": "Full calendar view with month, week, and day views. See all your bookings at a glance and never double-book again.",
    "landing.feat_timeslots": "Flexible Time Slots",
    "landing.feat_timeslots_desc": "Configure time intervals, block specific dates or time ranges, and set custom opening hours per day.",
    "landing.feat_customers": "Customer Management",
    "landing.feat_customers_desc": "Automatically track your customers, their booking history, and preferences — all organized by email.",
    "landing.feat_shop": "Branded Shop",
    "landing.feat_shop_desc": "Each business gets a beautiful, customizable booking page with your logo, colors, and branding.",
    "landing.feat_widget": "Embeddable Widget",
    "landing.feat_widget_desc": "Embed the booking flow directly on your own website with a simple iframe. Works anywhere.",
    "landing.feat_services": "Service Management",
    "landing.feat_services_desc": "Configure services with pricing, duration, and optional add-ons. Full control over your offerings.",
    "landing.how_label": "How It Works",
    "landing.how_title": "Up and running in minutes",
    "landing.how_step1": "Sign up & set up",
    "landing.how_step1_desc": "Create your account, verify your email, and configure your business details and opening hours.",
    "landing.how_step2": "Add your services",
    "landing.how_step2_desc": "Define your services with pricing, duration, and optional add-ons. Customize your booking page.",
    "landing.how_step3": "Share your link",
    "landing.how_step3_desc": "Share your unique booking page or embed it on your website. Customers can book 24/7.",
    "landing.how_step4": "Manage & grow",
    "landing.how_step4_desc": "Track bookings, manage customers, and use insights to grow your business.",
    "landing.pricing_trial_badge": "30 days free trial",
    "landing.pricing_title": "One plan, everything included",
    "landing.pricing_desc": "No tiers, no hidden fees. Get full access to every feature from day one. Start with a free 30-day trial.",
    "landing.pricing_month": "/month",
    "landing.pricing_tax": "excl. BTW",
    "landing.pricing_cta": "Start Free Trial",
    "landing.pricing_includes": "Everything you get:",
    "landing.pricing_f1": "Unlimited bookings",
    "landing.pricing_f2": "Custom booking page",
    "landing.pricing_f3": "Embeddable widget",
    "landing.pricing_f4": "Customer management",
    "landing.pricing_f5": "Calendar & scheduling",
    "landing.pricing_f6": "Service add-ons",
    "landing.pricing_f7": "Email notifications",
    "landing.pricing_f8": "Opening hours & blocked times",
    "landing.testimonials_label": "Testimonials",
    "landing.testimonials_title": "Loved by businesses everywhere",
    "landing.testimonial1": "Slotify transformed how we handle appointments. Our no-shows dropped by 40% with the automated reminders. The booking page is beautiful and our clients love it.",
    "landing.testimonial1_role": "Owner, Luxe Hair Studio",
    "landing.testimonial2": "Setting up our services with different durations and add-ons was incredibly easy. We were up and running in under an hour. Best investment we've made.",
    "landing.testimonial2_role": "Director, PhysioCare Clinic",
    "landing.testimonial3": "The embeddable widget was a game-changer. We added it to our website and bookings increased by 60%. The calendar management is intuitive and powerful.",
    "landing.testimonial3_role": "Founder, Zen Massage Therapy",
    "landing.cta_title": "Ready to streamline your bookings?",
    "landing.cta_desc": "Join hundreds of businesses already using Slotify. Set up in minutes, no credit card required.",
    "landing.cta_button": "Get Started Free",
    "landing.footer_rights": "© 2026 Slotify. All rights reserved.",
    // Legal pages
    "legal.privacy_title": "Privacy Policy",
    "legal.terms_title": "Terms & Conditions",
    "legal.back_home": "Back to home",
    "legal.last_updated": "Last updated",
    "privacy.intro_title": "1. Introduction",
    "privacy.intro_text": "Slotify (\"we\", \"us\", or \"our\") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our online booking platform and related services.",
    "privacy.data_collect_title": "2. Information We Collect",
    "privacy.data_collect_text": "We collect information you provide directly, including: your name, email address, phone number, and business details when creating an account; booking and appointment data; payment information processed through our payment providers; and usage data such as IP addresses, browser type, and pages visited.",
    "privacy.data_use_title": "3. How We Use Your Information",
    "privacy.data_use_text": "We use the collected information to: provide and maintain our booking platform; process appointments and send confirmations and reminders; improve our services and user experience; communicate with you about updates, support, and promotional offers; comply with legal obligations; and detect and prevent fraud or abuse.",
    "privacy.cookies_title": "4. Cookies & Tracking",
    "privacy.cookies_text": "We use cookies and similar technologies to enhance your experience. Necessary cookies are always active. Analytics and marketing cookies are only activated with your explicit consent via our cookie banner. You can manage your cookie preferences at any time. We use Google Tag Manager to manage tracking scripts in compliance with Google Consent Mode v2.",
    "privacy.sharing_title": "5. Data Sharing",
    "privacy.sharing_text": "We do not sell your personal data. We may share information with: service providers who assist in operating our platform (hosting, email, payment processing); legal authorities when required by law; and business partners only with your explicit consent.",
    "privacy.retention_title": "6. Data Retention",
    "privacy.retention_text": "We retain your personal data for as long as your account is active or as needed to provide services. After account deletion, we may retain certain data for up to 30 days for backup purposes, and as required by law for accounting and legal obligations.",
    "privacy.rights_title": "7. Your Rights",
    "privacy.rights_text": "Under applicable data protection laws (including GDPR), you have the right to: access, correct, or delete your personal data; restrict or object to processing; data portability; withdraw consent at any time; and lodge a complaint with a supervisory authority. Contact us to exercise any of these rights.",
    "privacy.contact_title": "8. Contact Us",
    "privacy.contact_text": "If you have questions about this Privacy Policy or wish to exercise your data protection rights, please contact us at privacy@slotify.app.",
    "terms.acceptance_title": "1. Acceptance of Terms",
    "terms.acceptance_text": "By accessing or using Slotify's services, you agree to be bound by these Terms & Conditions. If you do not agree to these terms, please do not use our platform.",
    "terms.service_title": "2. Description of Service",
    "terms.service_text": "Slotify provides an online booking and appointment management platform for businesses. Our service includes a dashboard for managing reservations, services, customers, and business settings, as well as a public-facing booking page for your customers.",
    "terms.accounts_title": "3. User Accounts",
    "terms.accounts_text": "You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use.",
    "terms.payment_title": "4. Payments & Billing",
    "terms.payment_text": "Slotify offers a free trial period followed by a paid subscription. Payments are processed through our payment provider (Mollie). Subscriptions automatically renew unless cancelled. Refunds are handled on a case-by-case basis. Prices are exclusive of applicable taxes unless stated otherwise.",
    "terms.ip_title": "5. Intellectual Property",
    "terms.ip_text": "All content, features, and functionality of the Slotify platform are owned by us and protected by intellectual property laws. You retain ownership of all data you input into the platform. You grant us a limited license to use this data solely to provide our services.",
    "terms.liability_title": "6. Limitation of Liability",
    "terms.liability_text": "Slotify is provided \"as is\" without warranties of any kind. We are not liable for any indirect, incidental, or consequential damages arising from your use of the platform. Our total liability shall not exceed the amount you paid in the 12 months preceding the claim.",
    "terms.termination_title": "7. Termination",
    "terms.termination_text": "Either party may terminate the agreement at any time. Upon termination, your right to use the platform ceases immediately. We will retain your data for 30 days after termination, after which it will be permanently deleted unless required by law.",
    "terms.changes_title": "8. Changes to Terms",
    "terms.changes_text": "We reserve the right to modify these terms at any time. We will notify you of material changes via email or through the platform. Continued use after changes constitutes acceptance of the updated terms.",
    "terms.governing_title": "9. Governing Law",
    "terms.governing_text": "These terms are governed by the laws of the Netherlands. Any disputes shall be resolved in the competent courts of the Netherlands.",
    // Cookie banner
    "cookie.message": "We use cookies to improve your experience and analyze site traffic. You can manage your preferences below.",
    "cookie.accept_all": "Accept All",
    "cookie.decline": "Decline",
    "cookie.manage": "Manage",
    "cookie.manage_desc": "Choose which cookies you'd like to allow. Necessary cookies are always active.",
    "cookie.necessary": "Necessary",
    "cookie.necessary_desc": "Required for the site to function",
    "cookie.analytics": "Analytics",
    "cookie.analytics_desc": "Help us understand how you use the site",
    "cookie.marketing": "Marketing",
    "cookie.marketing_desc": "Used for targeted advertising",
    "cookie.save": "Save Preferences",
    "cookie.back": "Back",
    // Settings - Team
    "settings.team": "Team",
    "settings.team_desc": "Invite and manage team members",
    "team.title": "Team settings",
    "team.subtitle": "Manage your team members and send invitations.",
    "team.members": "Members",
    "team.pending_invites": "Pending invitations",
    "team.invite": "Invite member",
    "team.invite_desc": "Send an email invitation to join your organisation.",
    "team.email": "Email address",
    "team.role": "Role",
    "team.role_admin": "Admin",
    "team.role_member": "Member",
    "team.send_invite": "Send invitation",
    "team.sending": "Sending…",
    "team.invite_sent": "Invitation sent",
    "team.invite_error": "Failed to send invitation",
    "team.cancel_invite": "Cancel invitation",
    "team.invite_cancelled": "Invitation cancelled",
    "team.no_members": "No team members yet.",
    "team.no_invites": "No pending invitations.",
    "team.joined": "Joined",
    "team.invited_on": "Invited on",
    "team.you": "(you)",
    "team.already_in_org": "This user is already in your organisation",
    // Invite accept
    "invite.title": "You've been invited!",
    "invite.join_org": "Join {org}",
    "invite.accept": "Accept invitation",
    "invite.accepting": "Accepting…",
    "invite.accepted": "You've joined the organisation!",
    "invite.invalid": "This invitation is invalid or has expired.",
    "invite.login_first": "Please sign in or create an account with this email to accept the invitation.",
    "invite.email_mismatch": "Please sign in with the email address the invitation was sent to.",
    "invite.already_in_org": "You already belong to an organisation.",
    // Settings - Extensions
    "settings.extensions": "Extensions",
    "settings.extensions_desc": "Enable integrations and add-ons for your organisation",
    "ext.title": "Extensions",
    "ext.subtitle": "Enable and configure integrations for your organisation.",
    "ext.loading": "Loading…",
    "ext.enabled": "Extension enabled",
    "ext.disabled": "Extension disabled",
    "ext.configure": "Configure",
    "ext.saved": "Settings saved!",
    "ext.save_error": "Failed to save settings",
    "ext.experimental": "Experimental",
    "ext.self_service_title": "Customer Self-Service",
    "ext.self_service_desc": "Let customers cancel or rebook their appointments using their reservation number and email.",
    "ext.statistics_title": "Statistics",
    "ext.statistics_desc": "View detailed charts and insights about your reservations, revenue, and customers.",
    "nav.statistics": "Statistics",
    "nav.employees": "Employees",
    "ext.employees_title": "Employees",
    "ext.employees_desc": "Manage team members, assign services, and enable parallel bookings across employees.",
    "employees.title": "Employees",
    "employees.add": "Add employee",
    "employees.no_items": "No employees yet. Add your first employee to get started.",
    "employees.col_name": "Name",
    "employees.col_status": "Status",
    "employees.active": "Active",
    "employees.inactive": "Inactive",
    "employees.edit": "Edit",
    "employees.delete": "Delete",
    "employees.deleted": "deleted",
    "employees.delete_title": "Delete employee",
    "employees.delete_desc_pre": "Are you sure you want to delete ",
    "employees.delete_desc_post": "? This action cannot be undone.",
    "employees.cancel": "Cancel",
    "employees.back": "Back",
    "employees.edit_title": "Edit employee",
    "employees.add_title": "Add employee",
    "employees.basic_info": "Basic information",
    "employees.name": "Name",
    "employees.name_placeholder": "e.g. John Doe",
    "employees.image_url": "Photo URL",
    "employees.photo_hint": "Click the avatar to upload a photo",
    "employees.status_label": "Active",
    "employees.status_active_desc": "This employee is visible and bookable.",
    "employees.status_inactive_desc": "This employee is hidden and not bookable.",
    "employees.services": "Assigned services",
    "employees.no_services": "No services created yet.",
    "employees.working_hours": "Custom working hours",
    "employees.working_hours_desc": "Override the organisation's opening hours for this employee.",
    "employees.closed": "Free",
    "employees.saving": "Saving…",
    "employees.save_changes": "Save changes",
    "employees.create": "Create employee",
    "employees.created": "Employee created",
    "employees.updated": "Employee updated",
    "employees.ext_title": "Employees Settings",
    "employees.ext_subtitle": "Configure how employees work in your booking flow.",
    "employees.allow_customer_selection": "Allow customer to select employee",
    "employees.allow_customer_selection_desc": "Show an employee picker in the shop booking flow.",
    "employees.allow_no_preference": "Allow 'No preference' option",
    "employees.allow_no_preference_desc": "Let customers skip employee selection and get automatically assigned.",
    "employees.employee_step_before_service": "Employee selection before service",
    "employees.employee_step_before_service_desc": "Show the employee picker before the service selection step.",
    "employees.save_settings": "Save settings",
    "blocked.employee": "Employee",
    "blocked.all_employees": "All employees",
    "days.mon": "Mon",
    "days.tue": "Tue",
    "days.wed": "Wed",
    "days.thu": "Thu",
    "days.fri": "Fri",
    "days.sat": "Sat",
    "days.sun": "Sun",
    "stats.title": "Statistics",
    "stats.subtitle": "Insights into your reservations and revenue.",
    "stats.period_this_month": "This month",
    "stats.period_prev_month": "Previous month",
    "stats.period_custom": "Custom",
    "stats.period_next_month": "Next month",
    "stats.from": "From",
    "stats.to": "To",
    "stats.total_bookings": "Total bookings",
    "stats.total_revenue": "Total revenue",
    "stats.unique_customers": "Unique customers",
    "stats.cancellations": "Cancellations",
    "stats.bookings_per_day": "Bookings per day",
    "stats.revenue_per_week": "Revenue per week",
    "stats.by_service": "By service",
    "stats.by_status": "By status",
    "stats.bookings": "Bookings",
    "stats.cancelled": "Cancelled",
    "stats.revenue": "Revenue",
    "stats.no_data": "No data for this period",
    "stats.status_pending": "Pending",
    "stats.status_confirmed": "Confirmed",
    "stats.status_cancelled": "Cancelled",
    "stats.status_completed": "Completed",
    "stats.status_no_show": "No-show",
    "stats.status_awaiting_payment": "Awaiting Payment",
    "stats.status_expired": "Expired",
    "ext.ss_title": "Customer Self-Service",
    "ext.ss_subtitle": "Configure how customers can manage their own bookings.",
    "ext.ss_saved": "Self-service settings saved!",
    "ext.ss_allow_cancel": "Allow cancellations",
    "ext.ss_allow_cancel_desc": "Customers can cancel their own reservations",
    "ext.ss_cancel_deadline": "Cancellation deadline",
    "ext.ss_hours_before": "hours before the appointment",
    "ext.ss_allow_rebook": "Allow rebooking",
    "ext.ss_allow_rebook_desc": "Customers can reschedule their own reservations",
    "ext.ss_rebook_deadline": "Rebooking deadline",
    "ext.ss_visibility": "Visibility",
    "ext.ss_show_shop": "Show in shop",
    "ext.ss_show_shop_desc": "Display a manage booking link on the shop page",
    "ext.ss_show_email": "Show in email",
    "ext.ss_show_email_desc": "Include a manage booking link in the confirmation email",
    "ext.ss_show_thank_you": "Show on thank you page",
    "ext.ss_show_thank_you_desc": "Display a manage booking link on the thank you page after booking",
    "ext.ss_saving": "Saving…",
    "ext.ss_save": "Save Settings",
    "ext.ss_not_available": "This feature is not available.",
    "ext.ss_manage_title": "Manage your booking",
    "ext.ss_reservation_number": "Reservation number",
    "ext.ss_service": "Service",
    "ext.ss_datetime": "Date & Time",
    "ext.ss_status": "Status",
    "ext.ss_rebook_btn": "Rebook",
    "ext.ss_cancel_btn": "Cancel booking",
    "ext.ss_cancel_confirm_title": "Cancel this booking?",
    "ext.ss_cancel_confirm_desc": "Are you sure you want to cancel this booking? This cannot be undone.",
    "ext.ss_keep": "Keep booking",
    "ext.ss_cancelling": "Cancelling…",
    "ext.ss_confirm_cancel": "Yes, cancel",
    "ext.ss_deadline_passed": "The deadline to modify this booking has passed.",
    "ext.ss_pick_new_datetime": "Pick a new date & time",
    "ext.ss_available_times": "Available times",
    "ext.ss_rebooking": "Rebooking…",
    "ext.ss_confirm_rebook": "Confirm new time",
    "ext.ss_looking_up": "Looking up…",
    "ext.ss_lookup": "Find my booking",
    "ext.ss_not_found": "No booking found. Please check your reservation number and email.",
    "ext.ss_cancelled_title": "Booking cancelled",
    "ext.ss_cancelled_desc": "Your booking has been successfully cancelled.",
    "ext.ss_rebooked_title": "Booking rescheduled",
    "ext.ss_rebooked_desc": "Your booking has been rescheduled successfully.",
    "ext.ss_time": "Time",
    "ext.ss_duration": "Duration",
    "ext.ss_rebook_notifications": "Rebook notifications",
    "ext.ss_rebook_notifications_desc": "Send email notifications when a booking is rescheduled.",
    "ext.ss_notify_customer": "Notify customer",
    "ext.ss_notify_customer_desc": "Send the customer an email about the rescheduled booking.",
    "ext.ss_notify_organisation": "Notify organisation",
    "ext.ss_notify_organisation_desc": "Send the organisation an email about the rescheduled booking.",
    "ext.ss_cancel_notifications": "Cancellation notifications",
    "ext.ss_cancel_notifications_desc": "Send email notifications when a booking is cancelled.",
    "ext.ss_cancel_notify_customer": "Notify customer",
    "ext.ss_cancel_notify_customer_desc": "Send the customer an email when their booking is cancelled.",
    "ext.ss_cancel_notify_organisation": "Notify organisation",
    "ext.ss_cancel_notify_organisation_desc": "Send the organisation an email when a booking is cancelled.",
    "ext.ss_manage_link": "Cancel or reschedule your booking",
    "ext.ss_allowed_statuses": "Allowed statuses",
    "ext.ss_allowed_statuses_desc": "Only reservations with these statuses can be modified by the customer.",
    "ext.ss_status_not_allowed": "This reservation cannot be modified with its current status.",
    "ext.pao_card_title": "Pay at Order",
    "ext.pao_card_desc": "Require customers to pay before their booking is confirmed.",
    "ext.pao_title": "Pay at Order",
    "ext.pao_subtitle": "Configure your payment provider so customers pay when booking.",
    "ext.pao_select_psp": "Payment provider",
    "ext.pao_select_psp_desc": "Choose which payment service provider to use for processing payments.",
    "ext.pao_mollie_desc": "Popular European PSP with iDEAL, Bancontact, credit cards and more.",
    "ext.pao_stripe_desc": "Global PSP with credit cards, Apple Pay, Google Pay and more.",
    "ext.pao_test_mode": "Test mode",
    "ext.pao_test_mode_desc": "Use test/sandbox API keys. No real payments will be processed.",
    "ext.pao_api_keys": "API keys",
    "ext.pao_api_keys_desc": "Enter your API keys from your payment provider dashboard.",
    "ext.pao_mollie_api_key": "Mollie API key",
    "ext.pao_mollie_key_hint": "Find this in your Mollie dashboard under Developers → API keys.",
    "ext.pao_stripe_publishable_key": "Stripe publishable key",
    "ext.pao_stripe_secret_key": "Stripe secret key",
    "ext.pao_stripe_key_hint": "Find these in your Stripe dashboard under Developers → API keys.",
    "ext.pao_no_keys_warning": "You haven't entered your API keys yet. The extension won't work until keys are configured.",
    "ext.pao_expiry_title": "Payment expiry",
    "ext.pao_expiry_desc": "Automatically cancel reservations if payment is not completed within this time.",
    "ext.pao_expiry_unit": "minutes",
    "ext.pao_save": "Save settings",
    "ext.pao_saving": "Saving…",
    "ext.pao_saved": "Payment settings saved",
    "extpage.back_home": "Back to home",
    "extpage.features_title": "What's included",
    "extpage.ss_title": "Customer Self-Service",
    "extpage.ss_subtitle": "Empower your customers to manage their own bookings. Reduce no-shows and free up your time with automated cancellations and rebookings.",
    "extpage.ss_feat_cancel": "Customers can cancel their own appointments within a configurable deadline",
    "extpage.ss_feat_rebook": "Allow customers to reschedule to a new available time slot",
    "extpage.ss_feat_deadline": "Set custom deadlines for cancellations and rebookings",
    "extpage.ss_feat_notifications": "Automatic email notifications for cancellations and rebookings",
    "extpage.ss_feat_shop": "Show a manage booking link directly on your shop page",
    "extpage.ss_feat_email": "Include a manage booking link in confirmation emails",
    "extpage.stats_title": "Statistics & Insights",
    "extpage.stats_subtitle": "Get a complete overview of your business performance with detailed charts, KPIs, and customer analytics.",
    "extpage.stats_feat_kpi": "Key performance indicators for bookings, revenue, and customers",
    "extpage.stats_feat_charts": "Beautiful bar and pie charts for visual insights",
    "extpage.stats_feat_revenue": "Track revenue by service and time period",
    "extpage.stats_feat_customers": "Customer analytics including new vs returning",
    "extpage.stats_feat_periods": "Filter by current, previous, or next month and custom date ranges",
    "extpage.stats_feat_export": "Overview of booking statuses including no-shows and cancellations",
    "extpage.pao_title": "Pay at Order",
    "extpage.pao_subtitle": "Require customers to pay when they book. Reduce no-shows and secure your revenue with integrated online payments.",
    "extpage.pao_feat_online": "Customers pay directly during the booking process",
    "extpage.pao_feat_mollie": "Integrated with Mollie for secure payment processing",
    "extpage.pao_feat_status": "Automatic payment status tracking on each reservation",
    "extpage.pao_feat_webhook": "Real-time payment status updates via webhooks",
    "extpage.pao_feat_secure": "PCI-compliant secure payment handling",
    "extpage.pao_feat_auto": "Bookings auto-confirm after successful payment",
    "extpage.emp_title": "Employees",
    "extpage.emp_subtitle": "Assign employees to services, set individual working hours, and let customers choose their preferred team member when booking.",
    "extpage.emp_feat_assign": "Assign specific employees to individual services",
    "extpage.emp_feat_hours": "Set custom working hours per employee",
    "extpage.emp_feat_services": "Employees are only shown for services they can provide",
    "extpage.emp_feat_availability": "Automatic availability filtering based on employee schedules",
    "extpage.emp_feat_calendar": "Employees displayed on the calendar with color-coded blocks",
    "extpage.emp_feat_preference": "Customers can select their preferred employee when booking",
  },
  nl: {
    "nav.calendar": "Kalender",
    "nav.reservations": "Reserveringen",
    "nav.services": "Diensten",
    "nav.customers": "Klanten",
    "nav.blocked": "Geblokkeerde tijden",
    "nav.upsells": "Upsells",
    "nav.settings": "Instellingen",
    "shop.title": "Boek je afspraak online",
    "shop.step.service": "Dienst",
    "shop.step.employee": "Medewerker",
    "shop.choose_employee": "Kies je specialist",
    "shop.no_preference": "Geen voorkeur",
    "shop.no_preference_desc": "We wijzen de best beschikbare specialist toe.",
    "shop.no_employees_available": "Geen specialisten beschikbaar voor deze dienst.",
    "shop.step.datetime": "Datum & Tijd",
    "shop.step.details": "Gegevens",
    "shop.step.confirmed": "Bevestigd",
    "shop.choose_service": "Kies een dienst",
    "shop.add_options": "Opties toevoegen:",
    "shop.required_options": "Kies een optie (verplicht)",
    "shop.extra_cost_time": "+€{price} · +{duration} min",
    "shop.total": "Totaal",
    "shop.continue": "Doorgaan",
    "shop.back": "Terug",
    "shop.pick_datetime": "Kies een datum & tijd",
    "shop.available_times": "Beschikbare tijden voor",
    "shop.no_timeslots": "Geen beschikbare tijdsloten. Selecteer een andere datum.",
    "shop.slot_taken": "Dit tijdslot is niet meer beschikbaar. Kies een ander tijdstip.",
    "shop.your_details": "Je gegevens",
    "shop.service": "Dienst",
    "shop.options": "Opties",
    "shop.date_time": "Datum & Tijd",
    "shop.first_name": "Voornaam",
    "shop.last_name": "Achternaam",
    "shop.email": "E-mail",
    "shop.phone": "Telefoon",
    "shop.confirm_booking": "Bevestig boeking",
    "shop.booking_confirmed": "Boeking bevestigd!",
    "shop.thank_you": "Bedankt, {name}! Een bevestiging is verstuurd naar {email}.",
    "shop.reminder": "Je ontvangt een herinnering op de dag van je afspraak.",
    "calendar.add_google": "Toevoegen aan Google Agenda",
    "calendar.add_apple": "Toevoegen aan Apple Agenda",
    "shop.book_another": "Nog een afspraak boeken",
    "shop.rate_limit_error": "Je hebt te veel boekingen gedaan in korte tijd. Wacht even en probeer het opnieuw.",
    "shop.booking_error": "Boeking kon niet worden aangemaakt. Probeer het opnieuw.",
    "shop.payment_checking": "Betalingsstatus controleren...",
    "shop.payment_success_title": "Betaling geslaagd!",
    "shop.payment_success_desc": "Je boeking is bevestigd. Een bevestigingsmail is verstuurd.",
    "shop.payment_failed_title": "Betaling mislukt",
    "shop.payment_failed_desc": "Je betaling kon niet worden verwerkt. Probeer het opnieuw.",
    "shop.payment_try_again": "Opnieuw proberen",
    "shop.payment_pending_title": "Betaling wordt verwerkt",
    "shop.payment_pending_desc": "Je betaling wordt nog verwerkt. Je ontvangt een bevestigingsmail zodra deze is voltooid.",
    "shop.payment_unknown_title": "Er is iets misgegaan",
    "shop.payment_unknown_desc": "We konden je betaling niet verifiëren. Neem contact met ons op als je hulp nodig hebt.",
    "shop.pay_and_book": "Betalen & boeken",
    "shop.upsell_step": "Extra's",
    "shop.upsell_required": "Selectie verplicht",
    "shop.upsell_optional": "Optioneel",
    "shop.upsell_skip": "Overslaan",
    "shop.unavailable_title": "Shop niet beschikbaar",
    "shop.unavailable_description": "Deze boekingspagina accepteert momenteel geen nieuwe afspraken.",
    "settings.title": "Instellingen",
    "settings.back": "Terug naar Instellingen",
    "settings.organisation": "Organisatie-instellingen",
    "settings.organisation_desc": "Bedrijfsnaam, adres, contactgegevens, KvK- & BTW-nummer",
    "settings.opening_hours": "Openingstijden",
    "settings.opening_hours_desc": "Stel je weekschema en sluitingsdagen in",
    "settings.booking": "Boekingsinstellingen",
    "settings.booking_desc": "Tijdslotintervallen en afrekenveldinstellingen",
    "settings.checkout": "Afrekenvelden",
    "settings.checkout_desc": "Aangepaste velden die klanten invullen bij het afrekenen",
    "settings.embed": "Embed Widget",
    "settings.embed_desc": "Kopieer de code om boekingen op je website in te sluiten",
    "settings.shop": "Shopinstellingen",
    "settings.shop_desc": "Logo, huisstijl en uiterlijk van je shop",
    "settings.billing": "Facturatie & Abonnement",
    "settings.billing_desc": "Beheer je abonnement, betaalmethode en facturen",
    "settings.language": "Taal",
    "settings.profile": "Profielinstellingen",
    "settings.profile_desc": "Persoonlijke gegevens, wachtwoord en weergave-instellingen",
    "onboarding.org_title": "Organisatiegegevens",
    "onboarding.org_desc": "Vertel ons over je bedrijf",
    "onboarding.company_name": "Bedrijfsnaam *",
    "onboarding.company_placeholder": "Je bedrijfsnaam",
    "onboarding.email": "E-mail",
    "onboarding.phone": "Telefoon",
    "onboarding.address": "Adres",
    "onboarding.postal_code": "Postcode",
    "onboarding.city": "Stad",
    "onboarding.country": "Land",
    "onboarding.coc": "KvK-nummer",
    "onboarding.vat": "BTW-nummer",
    "onboarding.continue": "Doorgaan",
    "onboarding.hours_title": "Openingstijden",
    "onboarding.hours_desc": "Stel je weekschema in",
    "onboarding.to": "tot",
    "onboarding.closed": "Gesloten",
    "onboarding.back": "Terug",
    "onboarding.finishing": "Bezig met instellen…",
    "onboarding.finish": "Setup voltooien",
    "onboarding.name_required": "Bedrijfsnaam is verplicht.",
    "onboarding.success": "Je organisatie is opgezet!",
    "onboarding.error": "Er is iets misgegaan.",
    "onboarding.mon": "Maandag",
    "onboarding.tue": "Dinsdag",
    "onboarding.wed": "Woensdag",
    "onboarding.thu": "Donderdag",
    "onboarding.fri": "Vrijdag",
    "onboarding.sat": "Zaterdag",
    "onboarding.sun": "Zondag",
    "auth.sign_in": "Inloggen",
    "auth.sign_in_desc": "Voer je gegevens in om je dashboard te openen",
    "auth.sign_up": "Account aanmaken",
    "auth.sign_up_desc": "Meld je aan om je boekingen te beheren",
    "auth.email": "E-mail",
    "auth.password": "Wachtwoord",
    "auth.first_name": "Voornaam",
    "auth.last_name": "Achternaam",
    "auth.signing_in": "Bezig met inloggen…",
    "auth.creating": "Account aanmaken…",
    "auth.no_account": "Nog geen account?",
    "auth.have_account": "Heb je al een account?",
    "auth.welcome_back": "Welkom terug",
    "auth.welcome_back_desc": "Beheer je boekingen, diensten en klanten op één plek.",
    "auth.start_free": "Gratis starten",
    "auth.start_free_desc": "Stel je boekingssysteem in enkele minuten in. Geen creditcard nodig.",
    "auth.verify_title": "Controleer je e-mail",
    "auth.verify_desc": "We hebben je een verificatielink gestuurd. Controleer je inbox en klik op de link om je account te verifiëren.",
    "auth.back_to_login": "Terug naar Inloggen",
    "auth.error_duplicate": "Er bestaat al een account met dit e-mailadres.",
    "auth.accept_terms_pre": "Ik ga akkoord met de",
    "auth.terms_link": "Algemene Voorwaarden",
    "auth.accept_terms_and": "en het",
    "auth.privacy_link": "Privacybeleid",
    "auth.accept_terms_error": "Je moet de Algemene Voorwaarden en het Privacybeleid accepteren om een account aan te maken.",
    "auth.forgot_password": "Wachtwoord vergeten?",
    "auth.forgot_title": "Wachtwoord resetten",
    "auth.forgot_desc": "Voer je e-mailadres in en we sturen je een resetlink.",
    "auth.forgot_hero_desc": "Geen zorgen, het overkomt de besten.",
    "auth.send_reset_link": "Resetlink versturen",
    "auth.sending": "Versturen…",
    "auth.forgot_sent_title": "Controleer je e-mail",
    "auth.forgot_sent_desc": "Als er een account bestaat met dat e-mailadres, hebben we een wachtwoord-resetlink gestuurd.",
    "auth.reset_title": "Nieuw wachtwoord instellen",
    "auth.reset_desc": "Voer hieronder je nieuwe wachtwoord in.",
    "auth.reset_hero_desc": "Kies een sterk wachtwoord om je account te beveiligen.",
    "auth.new_password": "Nieuw wachtwoord",
    "auth.confirm_password": "Bevestig wachtwoord",
    "auth.update_password": "Wachtwoord bijwerken",
    "auth.updating": "Bijwerken…",
    "auth.password_updated": "Wachtwoord bijgewerkt!",
    "auth.redirecting": "Je wordt doorgestuurd naar je dashboard…",
    "auth.passwords_mismatch": "Wachtwoorden komen niet overeen.",
    "auth.password_too_short": "Wachtwoord moet minimaal 6 tekens zijn.",
    "booking.interval": "Tijdslotinterval",
    "booking.days": "Dagen",
    "booking.hours": "Uren",
    "booking.minutes_label": "Minuten",
    "booking.window_title": "Boekingsvenster",
    "booking.auto_confirm_label": "Boekingen automatisch bevestigen",
    "booking.auto_confirm_desc": "Nieuwe boekingen automatisch op bevestigd zetten in plaats van in afwachting.",
    "booking.window_desc": "Bepaal hoe ver van tevoren klanten afspraken kunnen boeken.",
    "booking.min_lead_label": "Minimale voorbereidingstijd",
    "booking.min_lead_desc": "Klanten moeten minstens zover van tevoren boeken.",
    "booking.max_advance_label": "Maximale vooruitboektijd",
    "booking.max_advance_desc": "Klanten kunnen niet verder vooruit boeken dan dit. Laat leeg voor geen limiet.",
    "booking.min_lead_snap": "Als de voorbereidingstijd een nieuwe dag bereikt, open boekingen vanaf het begin van die dag",
    "booking.max_advance_snap": "Als de vooruitboektijd op een dag eindigt, sta boekingen toe tot het einde van die dag",
    "booking.save": "Instellingen opslaan",
    // Blocked times
    "blocked.title": "Geblokkeerde tijden",
    "blocked.block_time": "Tijd blokkeren",
    "blocked.edit": "Bewerken",
    "blocked.edit_title": "Geblokkeerde tijd bewerken",
    "blocked.back": "Terug naar Geblokkeerde tijden",
    "blocked.no_items": "Nog geen geblokkeerde tijden.",
    "blocked.type_single_day": "Hele dag",
    "blocked.type_date_range": "Datumbereik",
    "blocked.type_time_range": "Tijdbereik",
    "blocked.type_datetime_range": "Datum- & tijdbereik",
    "blocked.type_daily_time_range": "Dagelijks tijdblok",
    "blocked.desc_single_day": "Blokkeer een hele dag",
    "blocked.desc_date_range": "Blokkeer meerdere opeenvolgende dagen",
    "blocked.desc_time_range": "Blokkeer specifieke uren op een dag",
    "blocked.desc_datetime_range": "Blokkeer uren over meerdere dagen",
    "blocked.desc_daily_time_range": "Blokkeer dezelfde uren op elke dag in een bereik",
    "blocked.block_type": "Bloktype",
    "blocked.select_date": "Selecteer datum",
    "blocked.select_dates": "Selecteer datums",
    "blocked.select_time": "Selecteer tijd",
    "blocked.start_time": "Starttijd",
    "blocked.end_time": "Eindtijd",
    "blocked.summary": "Samenvatting",
    "blocked.type": "Type",
    "blocked.date": "Datum",
    "blocked.start_date": "Startdatum",
    "blocked.end_date": "Einddatum",
    "blocked.time": "Tijd",
    "blocked.select_date_hint": "Selecteer een datum om de samenvatting te zien.",
    "blocked.status": "Status",
    "blocked.status_active": "Deze blokkering is momenteel actief",
    "blocked.status_inactive": "Deze blokkering is inactief en wordt niet toegepast",
    "blocked.saving": "Opslaan…",
    "blocked.save_changes": "Wijzigingen opslaan",
    "blocked.cancel": "Annuleren",
    "blocked.created": "Geblokkeerde tijd aangemaakt!",
    "blocked.updated": "Geblokkeerde tijd bijgewerkt!",
    "blocked.activated": "Geblokkeerde tijd geactiveerd",
    "blocked.deactivated": "Geblokkeerde tijd gedeactiveerd",
    "blocked.deleted": "Geblokkeerde tijd verwijderd.",
    "blocked.delete_title": "Geblokkeerde tijd verwijderen",
    "blocked.delete_desc": "Weet je zeker dat je deze geblokkeerde tijd wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
    "blocked.delete_confirm": "Verwijderen",
    // Customers
    "customers.title": "Klanten",
    "customers.total": "totaal",
    "customers.search": "Klanten zoeken...",
    "customers.no_items": "Nog geen klanten. Ze verschijnen hier zodra er boekingen binnenkomen.",
    "customers.not_found": "Klant niet gevonden.",
    "customers.back": "Terug naar Klanten",
    "customers.delete": "Verwijderen",
    "customers.delete_title": "Klant verwijderen",
    "customers.delete_desc_pre": "Weet je zeker dat je",
    "customers.delete_desc_post": " wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
    "customers.deleted": "verwijderd.",
    "customers.cancel": "Annuleren",
    "customers.total_bookings": "Totaal boekingen",
    "customers.total_spent": "Totaal besteed",
    "customers.customer_since": "Klant sinds",
    "customers.reservation_history": "Reserveringsgeschiedenis",
    "customers.no_reservations": "Geen reserveringen gevonden.",
    "customers.col_customer": "Klant",
    "customers.col_joined": "Aangemeld",
    "customers.col_actions": "Acties",
    "customers.col_service": "Dienst",
    "customers.col_date": "Datum",
    "customers.col_price": "Prijs",
    "customers.col_status": "Status",
    // Services
    "services.title": "Diensten",
    "services.add": "Dienst toevoegen",
    "services.no_items": "Nog geen diensten. Maak je eerste dienst aan om te beginnen.",
    "services.col_service": "Dienst",
    "services.col_price": "Prijs",
    "services.col_duration": "Duur",
    "services.col_options": "Opties",
    "services.col_status": "Status",
    "services.col_actions": "Acties",
    "services.active": "Actief",
    "services.inactive": "Inactief",
    "services.edit": "Bewerken",
    "services.delete": "Verwijderen",
    "services.delete_title": "Dienst verwijderen",
    "services.delete_desc_pre": "Weet je zeker dat je",
    "services.delete_desc_post": " wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
    "services.deleted": "is verwijderd.",
    "services.cancel": "Annuleren",
    "services.back": "Terug naar Diensten",
    "services.edit_title": "Dienst bewerken",
    "services.create_title": "Dienst aanmaken",
    "services.details": "Dienstgegevens",
    "services.name_label": "Naam dienst *",
    "services.name_placeholder": "bijv. Knipbeurt",
    "services.description_label": "Omschrijving",
    "services.description_placeholder": "Beschrijf wat deze dienst omvat...",
    "services.price_label": "Prijs (€) *",
    "services.duration_label": "Duur (minuten) *",
    "services.active_label": "Actief",
    "services.active_desc": "Deze dienst is zichtbaar in de shop",
    "services.inactive_desc": "Deze dienst is verborgen in de shop",
    "services.options_title": "Opties",
    "services.options_desc": "Voeg optionele extra's toe die klanten kunnen selecteren bij het boeken",
    "services.add_option": "Optie toevoegen",
    "services.selection_settings": "Selectie-instellingen",
    "services.selection_mode": "Selectiemodus",
    "services.mode_single": "Enkel — klant kiest één optie",
    "services.mode_multiple": "Meerdere — klant kiest elke combinatie",
    "services.required": "Verplicht",
    "services.required_desc": "Klant moet minstens één optie selecteren",
    "services.optional_desc": "Opties zijn optioneel",
    "services.no_options": "Nog geen opties. Voeg er een toe om extra's aan te bieden.",
    "services.option_label": "Optie",
    "services.option_name": "Naam",
    "services.option_name_placeholder": "bijv. Baard trimmen",
    "services.option_price": "Extra prijs (€)",
    "services.option_duration": "Extra duur (min)",
    "services.saving": "Opslaan…",
    "services.save_changes": "Wijzigingen opslaan",
    "services.create": "Dienst aanmaken",
    "services.updated": "Dienst bijgewerkt!",
    "services.created": "Dienst aangemaakt!",
    "services.fill_required": "Vul alle verplichte velden in.",
    // Upsells
    "upsells.title": "Upsells",
    "upsells.add": "Upsell toevoegen",
    "upsells.no_items": "Nog geen upsells. Maak herbruikbare upsell-stappen aan om aan diensten te koppelen.",
    "upsells.back": "Terug naar Upsells",
    "upsells.edit_title": "Upsell bewerken",
    "upsells.create_title": "Upsell aanmaken",
    "upsells.name_label": "Upsell naam *",
    "upsells.name_placeholder": "bijv. Haarversteviging upgrade",
    "upsells.settings": "Instellingen",
    "upsells.selection_mode": "Selectiemodus",
    "upsells.mode_single": "Enkel — klant kiest één optie",
    "upsells.mode_multiple": "Meerdere — klant kiest elke combinatie",
    "upsells.required": "Verplicht",
    "upsells.required_desc": "Klant moet minstens één optie kiezen",
    "upsells.optional_desc": "Selectie is optioneel",
    "upsells.options_title": "Opties",
    "upsells.options_desc": "Voeg opties toe waaruit klanten kunnen kiezen in deze upsell-stap",
    "upsells.add_option": "Optie toevoegen",
    "upsells.no_options": "Nog geen opties. Voeg er één toe.",
    "upsells.option_label": "Optie",
    "upsells.option_name": "Naam",
    "upsells.option_name_placeholder": "bijv. Diepte conditioner",
    "upsells.option_price": "Extra prijs (€)",
    "upsells.option_duration": "Extra duur (min)",
    "upsells.saving": "Opslaan…",
    "upsells.save_changes": "Wijzigingen opslaan",
    "upsells.create": "Upsell aanmaken",
    "upsells.updated": "Upsell bijgewerkt!",
    "upsells.created": "Upsell aangemaakt!",
    "upsells.fill_required": "Vul de upsell naam in.",
    "upsells.delete": "Verwijderen",
    "upsells.delete_title": "Upsell verwijderen",
    "upsells.delete_desc_pre": "Weet je zeker dat je",
    "upsells.delete_desc_post": " wilt verwijderen? Dit verwijdert het ook van alle gekoppelde diensten.",
    "upsells.deleted": "is verwijderd.",
    "upsells.cancel": "Annuleren",
    "upsells.col_name": "Naam",
    "upsells.col_options": "Opties",
    "upsells.col_required": "Verplicht",
    "upsells.col_mode": "Modus",
    "upsells.col_actions": "Acties",
    "upsells.edit": "Bewerken",
    "upsells.yes": "Ja",
    "upsells.no": "Nee",
    "upsells.single": "Enkel",
    "upsells.multiple": "Meerdere",
    "upsells.used_by": "Gebruikt door {count} diensten",
    "services.upsells_title": "Upsell-stappen",
    "services.upsells_desc": "Koppel herbruikbare upsell-stappen die klanten zien na het kiezen van deze dienst",
    "services.add_upsell": "Upsell toevoegen",
    "services.no_upsells": "Geen upsell-stappen gekoppeld. Voeg er één toe uit je upsell-bibliotheek.",
    "services.manage_upsells": "Upsells beheren",
    "services.select_upsell": "Selecteer een upsell om toe te voegen…",
    "services.upsell_already_added": "Deze upsell is al gekoppeld.",
    // Reservations
    "reservations.title": "Reserveringen",
    "reservations.total": "totaal",
    "reservations.search": "Reserveringen zoeken...",
    "reservations.filter_date": "Filteren op datum",
    "reservations.no_items": "Nog geen reserveringen. Ze verschijnen hier zodra klanten boeken.",
    "reservations.col_customer": "Klant",
    "reservations.col_service": "Dienst",
    "reservations.col_datetime": "Datum & Tijd",
    "reservations.col_status": "Status",
    "reservations.col_price": "Prijs",
    "reservations.col_actions": "Acties",
    "reservations.status_all": "Alle statussen",
    "reservations.status_confirmed": "Bevestigd",
    "reservations.status_pending": "In afwachting",
    "reservations.status_cancelled": "Geannuleerd",
    "reservations.status_completed": "Voltooid",
    "reservations.status_no_show": "Niet verschenen",
    "reservations.status_awaiting_payment": "Wacht op betaling",
    "reservations.status_expired": "Verlopen",
    "reservations.delete_title": "Reservering verwijderen",
    "reservations.delete_desc": "Weet je zeker dat je deze reservering wilt verwijderen voor",
    "reservations.delete_undone": "? Dit kan niet ongedaan worden gemaakt.",
    "reservations.deleted": "Reservering verwijderd.",
    "reservations.cancel": "Annuleren",
    "reservations.delete": "Verwijderen",
    "reservations.reset_filters": "Filters resetten",
    "reservations.back": "Terug naar Reserveringen",
    "reservations.not_found": "Reservering niet gevonden.",
    "reservations.booked_on": "Geboekt op",
    "reservations.at": "om",
    "reservations.reschedule": "Verplaatsen",
    "reservations.customer": "Klant",
    "reservations.view_profile": "Klantprofiel bekijken",
    "reservations.appointment": "Afspraak",
    "reservations.date": "Datum",
    "reservations.time": "Tijd",
    "reservations.duration": "Duur",
    "reservations.service": "Dienst",
    "reservations.options": "Opties",
    "reservations.payment": "Betaling",
    "reservations.total_amount": "Totaal",
    "reservations.payment_status": "Betaalstatus",
    "reservations.free": "Gratis",
    "reservations.pay_at_location": "Betalen op locatie",
    "reservations.paid_online": "Online betaald",
    "reservations.notes": "Interne notities",
    "reservations.notes_placeholder": "Voeg een notitie toe… (alleen zichtbaar voor medewerkers)",
    "reservations.no_notes": "Nog geen notities.",
    "reservations.note_added": "Notitie toegevoegd",
    "reservations.note_updated": "Notitie bijgewerkt",
    "reservations.note_deleted": "Notitie verwijderd",
    "reservations.activity": "Activiteit",
    "reservations.created_label": "Reservering aangemaakt",
    "reservations.rescheduled_label": "Reservering verplaatst",
    "reservations.rescheduled_from": "Van",
    "reservations.rescheduled_to": "naar",
    "reservations.cancelled_label": "Reservering geannuleerd",
    "reservations.booked_by": "Geboekt door",
    "reservations.for": "voor",
    "reservations.status_change_title": "Reserveringsstatus wijzigen",
    "reservations.status_change_desc_pre": "Weet je zeker dat je de status wilt wijzigen van",
    "reservations.status_change_desc_to": "naar",
    "reservations.confirm": "Bevestigen",
    "reservations.status_changed_to": "Status gewijzigd naar",
    "reservations.changed_from": "Gewijzigd van",
    "reservations.status_updated_to": "Status bijgewerkt naar",
    "reservations.note_added_label": "Interne notitie toegevoegd",
    "reservations.reschedule_title": "Reservering verplaatsen",
    "reservations.reschedule_current": "Huidige boeking:",
    "reservations.reschedule_available": "Beschikbaar op",
    "reservations.reschedule_no_slots": "Geen beschikbare tijdsloten op deze datum.",
    "reservations.reschedule_confirm": "Verplaatsing bevestigen",
    "reservations.reschedule_failed": "Verplaatsen van reservering mislukt.",
    "reservations.reschedule_success": "Reservering verplaatst naar",
    "reservations.this_customer": "deze klant",
    "calendar.title": "Kalender",
    "calendar.month": "Maand",
    "calendar.week": "Week",
    "calendar.day": "Dag",
    "calendar.today": "Vandaag",
    "calendar.mon": "Ma",
    "calendar.tue": "Di",
    "calendar.wed": "Wo",
    "calendar.thu": "Do",
    "calendar.fri": "Vr",
    "calendar.sat": "Za",
    "calendar.sun": "Zo",
    "calendar.more": "meer",
    "calendar.close": "Sluiten",
    "calendar.view_details": "Details bekijken",
    "banner.payment_failed": "Betaling mislukt — je account is beperkt.",
    "banner.update_payment": "Betaling bijwerken",
    "banner.cancelled": "Je abonnement is geannuleerd. Toegang eindigt over {days} dag(en).",
    "banner.reactivate": "Heractiveren",
    "banner.trial": "Nog {days} dag(en) in je gratis proefperiode.",
    "banner.trial_expired": "Je gratis proefperiode is verlopen.",
    "banner.setup_payment": "Betaling instellen",
    "org.title": "Organisatiegegevens",
    "org.company_name": "Bedrijfsnaam",
    "org.email": "E-mail",
    "org.phone": "Telefoon",
    "org.address": "Adres",
    "org.postal_code": "Postcode",
    "org.city": "Stad",
    "org.country": "Land",
    "org.coc_number": "KvK-nummer",
    "org.vat_number": "BTW-nummer",
    "org.saved": "Organisatiegegevens opgeslagen!",
    "org.saving": "Opslaan…",
    "org.save": "Wijzigingen opslaan",
    "hours.title": "Openingstijden",
    "hours.monday": "Maandag",
    "hours.tuesday": "Dinsdag",
    "hours.wednesday": "Woensdag",
    "hours.thursday": "Donderdag",
    "hours.friday": "Vrijdag",
    "hours.saturday": "Zaterdag",
    "hours.sunday": "Zondag",
    "hours.to": "tot",
    "hours.closed": "Gesloten",
    "hours.saved": "Openingstijden opgeslagen!",
    "hours.saving": "Opslaan…",
    "hours.save": "Uren opslaan",
    "checkout.title": "Checkout-velden",
    "checkout.desc": "Definieer extra velden die klanten invullen bij het afrekenen.",
    "checkout.fields": "Velden",
    "checkout.add_field": "Veld toevoegen",
    "checkout.no_fields": "Geen checkout-velden geconfigureerd. Voeg er een toe om extra informatie te verzamelen.",
    "checkout.field_num": "Veld",
    "checkout.required": "Verplicht",
    "checkout.label": "Label *",
    "checkout.label_placeholder": "bijv. Telefoonnummer",
    "checkout.type": "Type",
    "checkout.placeholder": "Plaatshouder",
    "checkout.placeholder_text": "Plaatshouder tekst...",
    "checkout.dropdown_options": "Dropdown-opties (kommagescheiden)",
    "checkout.dropdown_placeholder": "Optie 1, Optie 2, Optie 3",
    "checkout.label_required": "Alle velden moeten een label hebben.",
    "checkout.saved": "Checkout-velden opgeslagen!",
    "checkout.load_failed": "Laden van checkout-velden mislukt",
    "checkout.save": "Wijzigingen opslaan",
    "checkout.type_text": "Tekst",
    "checkout.type_email": "E-mail",
    "checkout.type_phone": "Telefoonnummer",
    "checkout.type_number": "Nummer",
    "checkout.type_textarea": "Lange tekst",
    "checkout.type_select": "Dropdown",
    "checkout.type_checkbox": "Selectievakje",
    "embed.title": "Boekingswidget insluiten",
    "embed.desc": "Kopieer deze code om het boekingsproces op je website in te sluiten.",
    "embed.copy": "Code kopiëren",
    "embed.copied": "Insluitcode gekopieerd!",
    "shopset.title": "Winkelinstellingen",
    "shopset.save_changes": "Wijzigingen opslaan",
    "shopset.logo": "Logo",
    "shopset.logo_desc": "Upload een logo voor lichte en donkere modus. Als er geen logo is geüpload, wordt je bedrijfsnaam weergegeven.",
    "shopset.light_logo": "Logo lichte modus",
    "shopset.dark_logo": "Logo donkere modus",
    "shopset.logo_size": "Logogrootte",
    "shopset.colors": "Kleuren",
    "shopset.colors_desc": "Pas knop-, tekst- en achtergrondkleuren aan. Laat leeg voor standaardwaarden.",
    "shopset.light_mode": "Lichte modus",
    "shopset.dark_mode": "Donkere modus",
    "shopset.btn_color": "Knopkleur",
    "shopset.btn_text_color": "Knoptekstkleur",
    "shopset.text_color": "Tekstkleur",
    "shopset.bg_color": "Achtergrondkleur",
    "shopset.card_bg": "Kaartachtergrond",
    "shopset.typography": "Typografie",
    "shopset.typography_desc": "Kies een lettertype en grootte voor je boekingspagina.",
    "shopset.font_family": "Lettertype",
    "shopset.font_size": "Lettergrootte",
    "shopset.unsaved": "Je hebt niet-opgeslagen wijzigingen",
    "shopset.discard": "Verwerpen",
    "shopset.saved": "Winkelinstellingen opgeslagen!",
    "shopset.uploading": "Uploaden…",
    "shopset.upload": "Uploaden",
    "shopset.logo_removed": "Logo verwijderd.",
    "shopset.size_small": "Klein",
    "shopset.size_medium": "Gemiddeld",
    "shopset.size_large": "Groot",
    "shopset.size_xlarge": "Extra groot",
    "shopset.font_small": "Klein (14px)",
    "shopset.font_medium": "Gemiddeld (16px)",
    "shopset.font_large": "Groot (18px)",
    "shopset.preview_light": "Lichte modus",
    "shopset.preview_dark": "Donkere modus",
    "shopset.preview_service": "Dienstnaam",
    "shopset.preview_desc": "Korte beschrijving van de dienst.",
    "shopset.preview_book": "Nu boeken",
    "billing.title": "Facturatie & Abonnement",
    "billing.subscription": "Abonnement",
    "billing.payment_failed": "Betaling mislukt",
    "billing.payment_failed_desc": "Je laatste betaling is mislukt. Je account is beperkt tot alleen facturatie. Werk je betaalmethode bij om volledige toegang te herstellen.",
    "billing.update_payment": "Betaalmethode bijwerken",
    "billing.status": "Status",
    "billing.free_trial": "Gratis proefperiode",
    "billing.trial_expired_label": "Proefperiode verlopen",
    "billing.trial_expired_title": "Je gratis proefperiode is verlopen",
    "billing.trial_expired_desc": "Stel een abonnement in om alle functies te blijven gebruiken.",
    "billing.trial_ends": "Proefperiode eindigt",
    "billing.days_left": "dagen over",
    "billing.plan": "Abonnement",
    "billing.next_payment": "Volgende betaling",
    "billing.start_sub_desc": "Start nu je abonnement om Slotify te blijven gebruiken na je proefperiode.",
    "billing.setup_payment": "Betaling instellen",
    "billing.active_msg": "Je abonnement is actief. Betalingen worden automatisch verwerkt.",
    "billing.cancel_sub": "Abonnement opzeggen",
    "billing.cancel_title": "Abonnement opzeggen?",
    "billing.cancel_desc": "Weet je zeker dat je je Slotify-abonnement wilt opzeggen? Je account verliest direct toegang tot alle functies. Dit kan niet ongedaan worden gemaakt.",
    "billing.keep_sub": "Abonnement behouden",
    "billing.yes_cancel": "Ja, opzeggen",
    "billing.cancelled_msg": "Je abonnement is opgezegd.",
    "billing.reactivate": "Abonnement heractiveren",
    "billing.invoices": "Facturen",
    "billing.no_invoices": "Nog geen facturen.",
    "billing.start_failed": "Abonnement starten mislukt. Probeer het opnieuw.",
    "billing.cancel_success": "Je abonnement is opgezegd.",
    "billing.cancel_failed": "Opzeggen mislukt. Probeer het opnieuw.",
    "billing.download_failed": "Factuur downloaden mislukt.",
    "billing.download": "Factuur downloaden",
    "profile.title": "Profielinstellingen",
    "profile.personal": "Persoonlijke gegevens",
    "profile.first_name": "Voornaam",
    "profile.last_name": "Achternaam",
    "profile.email": "E-mail",
    "profile.save_details": "Gegevens opslaan",
    "profile.saved": "Profiel bijgewerkt!",
    "profile.change_password": "Wachtwoord wijzigen",
    "profile.current_password": "Huidig wachtwoord",
    "profile.new_password": "Nieuw wachtwoord",
    "profile.confirm_password": "Bevestig nieuw wachtwoord",
    "profile.update_password": "Wachtwoord bijwerken",
    "profile.password_updated": "Wachtwoord bijgewerkt!",
    "profile.password_mismatch": "Wachtwoorden komen niet overeen.",
    "profile.password_short": "Wachtwoord moet minimaal 6 tekens bevatten.",
    "profile.appearance": "Weergave",
    "profile.theme": "Thema",
    "profile.theme_light": "Licht",
    "profile.theme_dark": "Donker",
    "profile.theme_system": "Systeem",
    "profile.save_appearance": "Weergave opslaan",
    "profile.appearance_saved": "Weergave bijgewerkt!",
    "profile.saving": "Opslaan…",
    "profile.updating": "Bijwerken…",
    // Landing page
    "landing.hero_badge": "✨ Het moderne boekingsplatform voor bedrijven",
    "landing.hero_title_1": "Boekingen ",
    "landing.hero_title_highlight": "moeiteloos",
    "landing.hero_desc": "Geef je bedrijf een boost met een prachtig boekingssysteem. Laat klanten 24/7 afspraken plannen terwijl jij je focust op wat écht telt — je bedrijf laten groeien.",
    "landing.hero_cta": "Start je gratis proefperiode",
    "landing.hero_pricing": "Bekijk prijzen",
    "landing.nav_features": "Functies",
    "landing.nav_how": "Hoe het werkt",
    "landing.nav_testimonials": "Ervaringen",
    "landing.nav_pricing": "Prijzen",
    "landing.nav_extensions": "Extensies",
    "landing.nav_login": "Inloggen",
    "landing.nav_signup": "Gratis starten",
    "landing.features_label": "Functies",
    "landing.features_title": "Alles wat je nodig hebt om boekingen te beheren",
    "landing.features_desc": "Een complete toolkit ontworpen voor dienstverlenende bedrijven van elke grootte.",
    "landing.feat_calendar": "Slimme kalender",
    "landing.feat_calendar_desc": "Volledig kalenderoverzicht met maand-, week- en dagweergave. Zie al je boekingen in één oogopslag en voorkom dubbele boekingen.",
    "landing.feat_timeslots": "Flexibele tijdsloten",
    "landing.feat_timeslots_desc": "Configureer tijdsintervallen, blokkeer specifieke data of tijdsbereiken en stel aangepaste openingstijden per dag in.",
    "landing.feat_customers": "Klantenbeheer",
    "landing.feat_customers_desc": "Houd automatisch je klanten, hun boekingsgeschiedenis en voorkeuren bij — allemaal georganiseerd op e-mail.",
    "landing.feat_shop": "Eigen boekingspagina",
    "landing.feat_shop_desc": "Elk bedrijf krijgt een mooie, aanpasbare boekingspagina met je logo, kleuren en huisstijl.",
    "landing.feat_widget": "Insluitbare widget",
    "landing.feat_widget_desc": "Integreer het boekingsproces direct op je eigen website met een simpele iframe. Werkt overal.",
    "landing.feat_services": "Dienstenbeheer",
    "landing.feat_services_desc": "Configureer diensten met prijzen, duur en optionele extra's. Volledige controle over je aanbod.",
    "landing.how_label": "Hoe het werkt",
    "landing.how_title": "In enkele minuten operationeel",
    "landing.how_step1": "Aanmelden & instellen",
    "landing.how_step1_desc": "Maak je account aan, verifieer je e-mail en configureer je bedrijfsgegevens en openingstijden.",
    "landing.how_step2": "Diensten toevoegen",
    "landing.how_step2_desc": "Definieer je diensten met prijzen, duur en optionele extra's. Pas je boekingspagina aan.",
    "landing.how_step3": "Deel je link",
    "landing.how_step3_desc": "Deel je unieke boekingspagina of integreer deze op je website. Klanten kunnen 24/7 boeken.",
    "landing.how_step4": "Beheren & groeien",
    "landing.how_step4_desc": "Volg boekingen, beheer klanten en gebruik inzichten om je bedrijf te laten groeien.",
    "landing.pricing_trial_badge": "30 dagen gratis proberen",
    "landing.pricing_title": "Eén plan, alles inbegrepen",
    "landing.pricing_desc": "Geen staffels, geen verborgen kosten. Krijg volledige toegang tot elke functie vanaf dag één. Start met een gratis proefperiode van 30 dagen.",
    "landing.pricing_month": "/maand",
    "landing.pricing_tax": "excl. BTW",
    "landing.pricing_cta": "Start gratis proefperiode",
    "landing.pricing_includes": "Alles wat je krijgt:",
    "landing.pricing_f1": "Onbeperkt boekingen",
    "landing.pricing_f2": "Eigen boekingspagina",
    "landing.pricing_f3": "Insluitbare widget",
    "landing.pricing_f4": "Klantenbeheer",
    "landing.pricing_f5": "Kalender & planning",
    "landing.pricing_f6": "Service add-ons",
    "landing.pricing_f7": "E-mailmeldingen",
    "landing.pricing_f8": "Openingstijden & geblokkeerde tijden",
    "landing.testimonials_label": "Ervaringen",
    "landing.testimonials_title": "Geliefd bij bedrijven overal",
    "landing.testimonial1": "Slotify heeft onze afsprakenstroom compleet veranderd. Het aantal no-shows daalde met 40% dankzij de automatische herinneringen. De boekingspagina is prachtig en onze klanten zijn er dol op.",
    "landing.testimonial1_role": "Eigenaar, Luxe Hair Studio",
    "landing.testimonial2": "Het opzetten van diensten met verschillende duren en extra's was ongelooflijk eenvoudig. We waren binnen een uur operationeel. De beste investering die we hebben gedaan.",
    "landing.testimonial2_role": "Directeur, PhysioCare Kliniek",
    "landing.testimonial3": "De insluitbare widget was een gamechanger. We voegden het toe aan onze website en boekingen stegen met 60%. Het kalenderbeheer is intuïtief en krachtig.",
    "landing.testimonial3_role": "Oprichter, Zen Massage Therapie",
    "landing.cta_title": "Klaar om je boekingen te stroomlijnen?",
    "landing.cta_desc": "Sluit je aan bij honderden bedrijven die Slotify al gebruiken. Binnen minuten opgezet, geen creditcard vereist.",
    "landing.cta_button": "Gratis aan de slag",
    "landing.footer_rights": "© 2026 Slotify. Alle rechten voorbehouden.",
    // Legal pages
    "legal.privacy_title": "Privacybeleid",
    "legal.terms_title": "Algemene Voorwaarden",
    "legal.back_home": "Terug naar home",
    "legal.last_updated": "Laatst bijgewerkt",
    "privacy.intro_title": "1. Inleiding",
    "privacy.intro_text": "Slotify (\"wij\", \"ons\" of \"onze\") hecht veel waarde aan de bescherming van uw privacy. Dit privacybeleid legt uit hoe wij uw gegevens verzamelen, gebruiken, openbaar maken en beschermen wanneer u ons online boekingsplatform en aanverwante diensten gebruikt.",
    "privacy.data_collect_title": "2. Gegevens die wij verzamelen",
    "privacy.data_collect_text": "Wij verzamelen informatie die u direct verstrekt, waaronder: uw naam, e-mailadres, telefoonnummer en bedrijfsgegevens bij het aanmaken van een account; boekings- en afspraakgegevens; betalingsgegevens verwerkt via onze betalingsproviders; en gebruiksgegevens zoals IP-adressen, browsertype en bezochte pagina's.",
    "privacy.data_use_title": "3. Hoe wij uw gegevens gebruiken",
    "privacy.data_use_text": "Wij gebruiken de verzamelde gegevens om: ons boekingsplatform te leveren en te onderhouden; afspraken te verwerken en bevestigingen en herinneringen te versturen; onze diensten en gebruikerservaring te verbeteren; met u te communiceren over updates, ondersteuning en promotionele aanbiedingen; te voldoen aan wettelijke verplichtingen; en fraude of misbruik te detecteren en voorkomen.",
    "privacy.cookies_title": "4. Cookies & Tracking",
    "privacy.cookies_text": "Wij gebruiken cookies en vergelijkbare technologieën om uw ervaring te verbeteren. Noodzakelijke cookies zijn altijd actief. Analytics- en marketingcookies worden alleen geactiveerd met uw expliciete toestemming via onze cookiebanner. U kunt uw cookievoorkeuren op elk moment beheren.",
    "privacy.sharing_title": "5. Gegevens delen",
    "privacy.sharing_text": "Wij verkopen uw persoonlijke gegevens niet. Wij kunnen informatie delen met: dienstverleners die helpen bij het exploiteren van ons platform (hosting, e-mail, betalingsverwerking); juridische autoriteiten wanneer wettelijk vereist; en zakenpartners alleen met uw expliciete toestemming.",
    "privacy.retention_title": "6. Gegevensretentie",
    "privacy.retention_text": "Wij bewaren uw persoonlijke gegevens zolang uw account actief is of zolang nodig is om diensten te leveren. Na verwijdering van uw account bewaren wij bepaalde gegevens maximaal 30 dagen voor back-updoeleinden, en zoals wettelijk vereist voor boekhoudkundige en juridische verplichtingen.",
    "privacy.rights_title": "7. Uw rechten",
    "privacy.rights_text": "Op grond van toepasselijke gegevensbeschermingswetten (waaronder de AVG) heeft u het recht om: uw persoonlijke gegevens in te zien, te corrigeren of te verwijderen; verwerking te beperken of er bezwaar tegen te maken; gegevensoverdraagbaarheid; toestemming op elk moment in te trekken; en een klacht in te dienen bij een toezichthoudende autoriteit.",
    "privacy.contact_title": "8. Contact",
    "privacy.contact_text": "Als u vragen heeft over dit privacybeleid of uw gegevensbeschermingsrechten wilt uitoefenen, neem dan contact met ons op via privacy@slotify.app.",
    "terms.acceptance_title": "1. Acceptatie van voorwaarden",
    "terms.acceptance_text": "Door toegang te krijgen tot of gebruik te maken van de diensten van Slotify, gaat u akkoord met deze Algemene Voorwaarden. Als u niet akkoord gaat, gebruik ons platform dan niet.",
    "terms.service_title": "2. Beschrijving van de dienst",
    "terms.service_text": "Slotify biedt een online boekings- en afspraakbeheerplatform voor bedrijven. Onze dienst omvat een dashboard voor het beheren van reserveringen, diensten, klanten en bedrijfsinstellingen, evenals een openbare boekingspagina voor uw klanten.",
    "terms.accounts_title": "3. Gebruikersaccounts",
    "terms.accounts_text": "U moet nauwkeurige en volledige informatie verstrekken bij het aanmaken van een account. U bent verantwoordelijk voor het vertrouwelijk houden van uw accountgegevens en voor alle activiteiten die onder uw account plaatsvinden.",
    "terms.payment_title": "4. Betalingen & Facturering",
    "terms.payment_text": "Slotify biedt een gratis proefperiode gevolgd door een betaald abonnement. Betalingen worden verwerkt via onze betalingsprovider (Mollie). Abonnementen worden automatisch verlengd tenzij opgezegd. Prijzen zijn exclusief toepasselijke belastingen tenzij anders vermeld.",
    "terms.ip_title": "5. Intellectueel eigendom",
    "terms.ip_text": "Alle content, functies en functionaliteit van het Slotify-platform zijn ons eigendom en beschermd door intellectuele eigendomsrechten. U behoudt eigendom over alle gegevens die u in het platform invoert.",
    "terms.liability_title": "6. Beperking van aansprakelijkheid",
    "terms.liability_text": "Slotify wordt geleverd \"zoals het is\" zonder garanties van welke aard dan ook. Wij zijn niet aansprakelijk voor indirecte, incidentele of gevolgschade voortvloeiend uit uw gebruik van het platform.",
    "terms.termination_title": "7. Beëindiging",
    "terms.termination_text": "Beide partijen kunnen de overeenkomst op elk moment beëindigen. Na beëindiging vervalt uw recht om het platform te gebruiken onmiddellijk. Wij bewaren uw gegevens 30 dagen na beëindiging.",
    "terms.changes_title": "8. Wijzigingen in voorwaarden",
    "terms.changes_text": "Wij behouden ons het recht voor deze voorwaarden op elk moment te wijzigen. Wij zullen u op de hoogte stellen van materiële wijzigingen via e-mail of via het platform.",
    "terms.governing_title": "9. Toepasselijk recht",
    "terms.governing_text": "Deze voorwaarden worden beheerst door het Nederlands recht. Eventuele geschillen worden voorgelegd aan de bevoegde rechtbank in Nederland.",
    // Cookie banner
    "cookie.message": "We gebruiken cookies om je ervaring te verbeteren en siteverkeer te analyseren. Je kunt je voorkeuren hieronder beheren.",
    "cookie.accept_all": "Alles accepteren",
    "cookie.decline": "Weigeren",
    "cookie.manage": "Beheren",
    "cookie.manage_desc": "Kies welke cookies je wilt toestaan. Noodzakelijke cookies zijn altijd actief.",
    "cookie.necessary": "Noodzakelijk",
    "cookie.necessary_desc": "Vereist voor de werking van de site",
    "cookie.analytics": "Analyse",
    "cookie.analytics_desc": "Helpt ons begrijpen hoe je de site gebruikt",
    "cookie.marketing": "Marketing",
    "cookie.marketing_desc": "Gebruikt voor gerichte advertenties",
    "cookie.save": "Voorkeuren opslaan",
    "cookie.back": "Terug",
    "settings.team": "Team",
    "settings.team_desc": "Teamleden uitnodigen en beheren",
    "team.title": "Team instellingen",
    "team.subtitle": "Beheer je teamleden en verstuur uitnodigingen.",
    "team.members": "Leden",
    "team.pending_invites": "Openstaande uitnodigingen",
    "team.invite": "Lid uitnodigen",
    "team.invite_desc": "Stuur een e-mailuitnodiging om lid te worden van je organisatie.",
    "team.email": "E-mailadres",
    "team.role": "Rol",
    "team.role_admin": "Beheerder",
    "team.role_member": "Lid",
    "team.send_invite": "Uitnodiging versturen",
    "team.sending": "Versturen…",
    "team.invite_sent": "Uitnodiging verstuurd",
    "team.invite_error": "Uitnodiging versturen mislukt",
    "team.cancel_invite": "Uitnodiging annuleren",
    "team.invite_cancelled": "Uitnodiging geannuleerd",
    "team.no_members": "Nog geen teamleden.",
    "team.no_invites": "Geen openstaande uitnodigingen.",
    "team.joined": "Toegetreden",
    "team.invited_on": "Uitgenodigd op",
    "team.you": "(jij)",
    "team.already_in_org": "Deze gebruiker zit al in je organisatie",
    "invite.title": "Je bent uitgenodigd!",
    "invite.join_org": "Word lid van {org}",
    "invite.accept": "Uitnodiging accepteren",
    "invite.accepting": "Accepteren…",
    "invite.accepted": "Je bent lid geworden van de organisatie!",
    "invite.invalid": "Deze uitnodiging is ongeldig of verlopen.",
    "invite.login_first": "Log in of maak een account aan met dit e-mailadres om de uitnodiging te accepteren.",
    "invite.email_mismatch": "Log in met het e-mailadres waarnaar de uitnodiging is verstuurd.",
    "invite.already_in_org": "Je behoort al tot een organisatie.",
    "settings.extensions": "Extensies",
    "settings.extensions_desc": "Integraties en add-ons voor je organisatie inschakelen",
    "ext.title": "Extensies",
    "ext.subtitle": "Schakel integraties in en configureer ze voor je organisatie.",
    "ext.loading": "Laden…",
    "ext.enabled": "Extensie ingeschakeld",
    "ext.disabled": "Extensie uitgeschakeld",
    "ext.configure": "Configureren",
    "ext.saved": "Instellingen opgeslagen!",
    "ext.save_error": "Instellingen opslaan mislukt",
    "ext.experimental": "Experimenteel",
    "ext.self_service_title": "Klant zelfbediening",
    "ext.self_service_desc": "Laat klanten hun afspraken annuleren of omboeken met hun reserveringsnummer en e-mail.",
    "ext.statistics_title": "Statistieken",
    "ext.statistics_desc": "Bekijk gedetailleerde grafieken en inzichten over je reserveringen, omzet en klanten.",
    "nav.statistics": "Statistieken",
    "nav.employees": "Medewerkers",
    "ext.employees_title": "Medewerkers",
    "ext.employees_desc": "Beheer teamleden, wijs diensten toe en maak parallelle boekingen per medewerker mogelijk.",
    "employees.title": "Medewerkers",
    "employees.add": "Medewerker toevoegen",
    "employees.no_items": "Nog geen medewerkers. Voeg je eerste medewerker toe.",
    "employees.col_name": "Naam",
    "employees.col_status": "Status",
    "employees.active": "Actief",
    "employees.inactive": "Inactief",
    "employees.edit": "Bewerken",
    "employees.delete": "Verwijderen",
    "employees.deleted": "verwijderd",
    "employees.delete_title": "Medewerker verwijderen",
    "employees.delete_desc_pre": "Weet je zeker dat je ",
    "employees.delete_desc_post": " wilt verwijderen? Dit kan niet ongedaan worden gemaakt.",
    "employees.cancel": "Annuleren",
    "employees.back": "Terug",
    "employees.edit_title": "Medewerker bewerken",
    "employees.add_title": "Medewerker toevoegen",
    "employees.basic_info": "Basisinformatie",
    "employees.name": "Naam",
    "employees.name_placeholder": "bijv. Jan Jansen",
    "employees.image_url": "Foto URL",
    "employees.photo_hint": "Klik op de avatar om een foto te uploaden",
    "employees.status_label": "Actief",
    "employees.status_active_desc": "Deze medewerker is zichtbaar en boekbaar.",
    "employees.status_inactive_desc": "Deze medewerker is verborgen en niet boekbaar.",
    "employees.services": "Toegewezen diensten",
    "employees.no_services": "Nog geen diensten aangemaakt.",
    "employees.working_hours": "Aangepaste werktijden",
    "employees.working_hours_desc": "Overschrijf de openingstijden van de organisatie voor deze medewerker.",
    "employees.closed": "Vrij",
    "employees.saving": "Opslaan…",
    "employees.save_changes": "Wijzigingen opslaan",
    "employees.create": "Medewerker aanmaken",
    "employees.created": "Medewerker aangemaakt",
    "employees.updated": "Medewerker bijgewerkt",
    "employees.ext_title": "Medewerkers instellingen",
    "employees.ext_subtitle": "Configureer hoe medewerkers werken in je boekingsflow.",
    "employees.allow_customer_selection": "Klant mag medewerker kiezen",
    "employees.allow_customer_selection_desc": "Toon een medewerkerkiezer in de boekingsflow.",
    "employees.allow_no_preference": "'Geen voorkeur' optie toestaan",
    "employees.allow_no_preference_desc": "Laat klanten de medewerkerselectie overslaan en automatisch toewijzen.",
    "employees.employee_step_before_service": "Medewerkerselectie vóór dienst",
    "employees.employee_step_before_service_desc": "Toon de medewerkerkiezer vóór de dienst selectie stap.",
    "employees.save_settings": "Instellingen opslaan",
    "blocked.employee": "Medewerker",
    "blocked.all_employees": "Alle medewerkers",
    "days.mon": "Ma",
    "days.tue": "Di",
    "days.wed": "Wo",
    "days.thu": "Do",
    "days.fri": "Vr",
    "days.sat": "Za",
    "days.sun": "Zo",
    "stats.title": "Statistieken",
    "stats.subtitle": "Inzichten in je reserveringen en omzet.",
    "stats.period_this_month": "Deze maand",
    "stats.period_prev_month": "Vorige maand",
    "stats.period_custom": "Aangepast",
    "stats.period_next_month": "Volgende maand",
    "stats.from": "Van",
    "stats.to": "Tot",
    "stats.total_bookings": "Totaal boekingen",
    "stats.total_revenue": "Totale omzet",
    "stats.unique_customers": "Unieke klanten",
    "stats.cancellations": "Annuleringen",
    "stats.bookings_per_day": "Boekingen per dag",
    "stats.revenue_per_week": "Omzet per week",
    "stats.by_service": "Per dienst",
    "stats.by_status": "Per status",
    "stats.bookings": "Boekingen",
    "stats.cancelled": "Geannuleerd",
    "stats.revenue": "Omzet",
    "stats.no_data": "Geen gegevens voor deze periode",
    "stats.status_pending": "In afwachting",
    "stats.status_confirmed": "Bevestigd",
    "stats.status_cancelled": "Geannuleerd",
    "stats.status_completed": "Voltooid",
    "stats.status_no_show": "No-show",
    "stats.status_awaiting_payment": "Wacht op betaling",
    "stats.status_expired": "Verlopen",
    "ext.ss_title": "Klant zelfbediening",
    "ext.ss_subtitle": "Configureer hoe klanten hun eigen boekingen kunnen beheren.",
    "ext.ss_saved": "Zelfbedieningsinstellingen opgeslagen!",
    "ext.ss_allow_cancel": "Annulering toestaan",
    "ext.ss_allow_cancel_desc": "Klanten kunnen hun eigen reserveringen annuleren",
    "ext.ss_cancel_deadline": "Annuleringsdeadline",
    "ext.ss_hours_before": "uur voor de afspraak",
    "ext.ss_allow_rebook": "Omboeken toestaan",
    "ext.ss_allow_rebook_desc": "Klanten kunnen hun eigen reserveringen verplaatsen",
    "ext.ss_rebook_deadline": "Omboekdeadline",
    "ext.ss_visibility": "Zichtbaarheid",
    "ext.ss_show_shop": "Tonen in shop",
    "ext.ss_show_shop_desc": "Toon een link om boeking te beheren op de shoppagina",
    "ext.ss_show_email": "Tonen in e-mail",
    "ext.ss_show_email_desc": "Voeg een link toe om boeking te beheren in de bevestigingsmail",
    "ext.ss_show_thank_you": "Tonen op bedankpagina",
    "ext.ss_show_thank_you_desc": "Toon een link om boeking te beheren op de bedankpagina na het boeken",
    "ext.ss_saving": "Opslaan…",
    "ext.ss_save": "Instellingen opslaan",
    "ext.ss_not_available": "Deze functie is niet beschikbaar.",
    "ext.ss_manage_title": "Beheer je boeking",
    "ext.ss_reservation_number": "Reserveringsnummer",
    "ext.ss_service": "Dienst",
    "ext.ss_datetime": "Datum & Tijd",
    "ext.ss_status": "Status",
    "ext.ss_rebook_btn": "Omboeken",
    "ext.ss_cancel_btn": "Boeking annuleren",
    "ext.ss_cancel_confirm_title": "Deze boeking annuleren?",
    "ext.ss_cancel_confirm_desc": "Weet je zeker dat je deze boeking wilt annuleren? Dit kan niet ongedaan worden gemaakt.",
    "ext.ss_keep": "Boeking behouden",
    "ext.ss_cancelling": "Annuleren…",
    "ext.ss_confirm_cancel": "Ja, annuleren",
    "ext.ss_deadline_passed": "De deadline om deze boeking te wijzigen is verstreken.",
    "ext.ss_pick_new_datetime": "Kies een nieuwe datum & tijd",
    "ext.ss_available_times": "Beschikbare tijden",
    "ext.ss_rebooking": "Omboeken…",
    "ext.ss_confirm_rebook": "Nieuwe tijd bevestigen",
    "ext.ss_looking_up": "Opzoeken…",
    "ext.ss_lookup": "Mijn boeking zoeken",
    "ext.ss_not_found": "Geen boeking gevonden. Controleer je reserveringsnummer en e-mail.",
    "ext.ss_cancelled_title": "Boeking geannuleerd",
    "ext.ss_cancelled_desc": "Je boeking is succesvol geannuleerd.",
    "ext.ss_rebooked_title": "Boeking verplaatst",
    "ext.ss_rebooked_desc": "Je boeking is succesvol verplaatst.",
    "ext.ss_time": "Tijd",
    "ext.ss_duration": "Duur",
    "ext.ss_rebook_notifications": "Omboekingsmeldingen",
    "ext.ss_rebook_notifications_desc": "Verstuur e-mailmeldingen wanneer een boeking wordt verplaatst.",
    "ext.ss_notify_customer": "Klant informeren",
    "ext.ss_notify_customer_desc": "Stuur de klant een e-mail over de verplaatste boeking.",
    "ext.ss_notify_organisation": "Organisatie informeren",
    "ext.ss_notify_organisation_desc": "Stuur de organisatie een e-mail over de verplaatste boeking.",
    "ext.ss_cancel_notifications": "Annuleringsmeldingen",
    "ext.ss_cancel_notifications_desc": "Verstuur e-mailmeldingen wanneer een boeking wordt geannuleerd.",
    "ext.ss_cancel_notify_customer": "Klant informeren",
    "ext.ss_cancel_notify_customer_desc": "Stuur de klant een e-mail wanneer de boeking wordt geannuleerd.",
    "ext.ss_cancel_notify_organisation": "Organisatie informeren",
    "ext.ss_cancel_notify_organisation_desc": "Stuur de organisatie een e-mail wanneer een boeking wordt geannuleerd.",
    "ext.ss_manage_link": "Annuleer of verplaats je boeking",
    "ext.ss_allowed_statuses": "Toegestane statussen",
    "ext.ss_allowed_statuses_desc": "Alleen reserveringen met deze statussen kunnen door de klant worden gewijzigd.",
    "ext.ss_status_not_allowed": "Deze reservering kan niet worden gewijzigd met de huidige status.",
    "ext.pao_card_title": "Betalen bij bestelling",
    "ext.pao_card_desc": "Laat klanten betalen voordat hun boeking wordt bevestigd.",
    "ext.pao_title": "Betalen bij bestelling",
    "ext.pao_subtitle": "Configureer je betaalprovider zodat klanten betalen bij het boeken.",
    "ext.pao_select_psp": "Betaalprovider",
    "ext.pao_select_psp_desc": "Kies welke betaalprovider je wilt gebruiken voor het verwerken van betalingen.",
    "ext.pao_mollie_desc": "Populaire Europese PSP met iDEAL, Bancontact, creditcards en meer.",
    "ext.pao_stripe_desc": "Wereldwijde PSP met creditcards, Apple Pay, Google Pay en meer.",
    "ext.pao_test_mode": "Testmodus",
    "ext.pao_test_mode_desc": "Gebruik test/sandbox API-sleutels. Er worden geen echte betalingen verwerkt.",
    "ext.pao_api_keys": "API-sleutels",
    "ext.pao_api_keys_desc": "Voer je API-sleutels in vanuit het dashboard van je betaalprovider.",
    "ext.pao_mollie_api_key": "Mollie API-sleutel",
    "ext.pao_mollie_key_hint": "Vind deze in je Mollie-dashboard onder Ontwikkelaars → API-sleutels.",
    "ext.pao_stripe_publishable_key": "Stripe publiceerbare sleutel",
    "ext.pao_stripe_secret_key": "Stripe geheime sleutel",
    "ext.pao_stripe_key_hint": "Vind deze in je Stripe-dashboard onder Ontwikkelaars → API-sleutels.",
    "ext.pao_no_keys_warning": "Je hebt nog geen API-sleutels ingevoerd. De extensie werkt pas als sleutels zijn geconfigureerd.",
    "ext.pao_expiry_title": "Betalingstermijn",
    "ext.pao_expiry_desc": "Annuleer reserveringen automatisch als de betaling niet binnen deze tijd is voltooid.",
    "ext.pao_expiry_unit": "minuten",
    "ext.pao_save": "Instellingen opslaan",
    "ext.pao_saving": "Opslaan…",
    "ext.pao_saved": "Betalingsinstellingen opgeslagen",
    "extpage.back_home": "Terug naar home",
    "extpage.features_title": "Wat zit erin",
    "extpage.ss_title": "Klant Self-Service",
    "extpage.ss_subtitle": "Geef je klanten de mogelijkheid om hun eigen boekingen te beheren. Verminder no-shows en bespaar tijd met automatische annuleringen en omboekingen.",
    "extpage.ss_feat_cancel": "Klanten kunnen hun eigen afspraken annuleren binnen een instelbare deadline",
    "extpage.ss_feat_rebook": "Klanten kunnen hun afspraak verplaatsen naar een nieuw beschikbaar tijdslot",
    "extpage.ss_feat_deadline": "Stel aangepaste deadlines in voor annuleringen en omboekingen",
    "extpage.ss_feat_notifications": "Automatische e-mailnotificaties bij annuleringen en omboekingen",
    "extpage.ss_feat_shop": "Toon een link om boekingen te beheren direct op je shoppagina",
    "extpage.ss_feat_email": "Voeg een link toe om boekingen te beheren in bevestigingsmails",
    "extpage.stats_title": "Statistieken & Inzichten",
    "extpage.stats_subtitle": "Krijg een compleet overzicht van je bedrijfsprestaties met gedetailleerde grafieken, KPI's en klantanalyses.",
    "extpage.stats_feat_kpi": "Belangrijke prestatie-indicatoren voor boekingen, omzet en klanten",
    "extpage.stats_feat_charts": "Mooie staaf- en cirkeldiagrammen voor visuele inzichten",
    "extpage.stats_feat_revenue": "Volg omzet per dienst en tijdsperiode",
    "extpage.stats_feat_customers": "Klantanalyses inclusief nieuw vs terugkerend",
    "extpage.stats_feat_periods": "Filter op huidige, vorige of volgende maand en aangepaste datumbereiken",
    "extpage.stats_feat_export": "Overzicht van boekingsstatussen inclusief no-shows en annuleringen",
    "extpage.pao_title": "Betalen bij bestelling",
    "extpage.pao_subtitle": "Laat klanten betalen bij het boeken. Verminder no-shows en beveilig je omzet met geïntegreerde online betalingen.",
    "extpage.pao_feat_online": "Klanten betalen direct tijdens het boekingsproces",
    "extpage.pao_feat_mollie": "Geïntegreerd met Mollie voor veilige betalingsverwerking",
    "extpage.pao_feat_status": "Automatische betalingsstatusregistratie bij elke reservering",
    "extpage.pao_feat_webhook": "Real-time betalingsstatusupdates via webhooks",
    "extpage.pao_feat_secure": "PCI-conforme veilige betalingsafhandeling",
    "extpage.pao_feat_auto": "Boekingen worden automatisch bevestigd na succesvolle betaling",
    "extpage.emp_title": "Medewerkers",
    "extpage.emp_subtitle": "Wijs medewerkers toe aan diensten, stel individuele werktijden in en laat klanten hun voorkeur kiezen bij het boeken.",
    "extpage.emp_feat_assign": "Wijs specifieke medewerkers toe aan individuele diensten",
    "extpage.emp_feat_hours": "Stel aangepaste werktijden in per medewerker",
    "extpage.emp_feat_services": "Medewerkers worden alleen getoond voor diensten die zij kunnen uitvoeren",
    "extpage.emp_feat_availability": "Automatische beschikbaarheidsfiltering op basis van medewerkersroosters",
    "extpage.emp_feat_calendar": "Medewerkers worden weergegeven op de kalender met kleurgecodeerde blokken",
    "extpage.emp_feat_preference": "Klanten kunnen hun voorkeursmedewerker kiezen bij het boeken",
  },
  fr: {
    "nav.calendar": "Calendrier",
    "nav.reservations": "Réservations",
    "nav.services": "Services",
    "nav.customers": "Clients",
    "nav.blocked": "Créneaux bloqués",
    "nav.upsells": "Upsells",
    "nav.settings": "Paramètres",
    "shop.title": "Réservez votre rendez-vous en ligne",
    "shop.step.service": "Service",
    "shop.step.datetime": "Date & Heure",
    "shop.step.details": "Détails",
    "shop.step.confirmed": "Confirmé",
    "shop.choose_service": "Choisissez un service",
    "shop.add_options": "Ajouter des options :",
    "shop.required_options": "Choisissez une option (obligatoire)",
    "shop.extra_cost_time": "+€{price} · +{duration} min",
    "shop.total": "Total",
    "shop.continue": "Continuer",
    "shop.back": "Retour",
    "shop.pick_datetime": "Choisissez une date et une heure",
    "shop.available_times": "Heures disponibles pour",
    "shop.no_timeslots": "Aucun créneau horaire disponible. Veuillez sélectionner une autre date.",
    "shop.slot_taken": "Ce créneau n'est plus disponible. Veuillez choisir un autre horaire.",
    "shop.your_details": "Vos coordonnées",
    "shop.service": "Service",
    "shop.options": "Options",
    "shop.date_time": "Date & Heure",
    "shop.first_name": "Prénom",
    "shop.last_name": "Nom",
    "shop.email": "E-mail",
    "shop.phone": "Téléphone",
    "shop.confirm_booking": "Confirmer la réservation",
    "shop.booking_confirmed": "Réservation confirmée !",
    "shop.thank_you": "Merci, {name} ! Une confirmation a été envoyée à {email}.",
    "shop.reminder": "Vous recevrez un rappel le jour de votre rendez-vous.",
    "calendar.add_google": "Ajouter à Google Agenda",
    "calendar.add_apple": "Ajouter au calendrier Apple",
    "shop.book_another": "Prendre un autre rendez-vous",
    "shop.rate_limit_error": "Vous avez effectué trop de réservations en peu de temps. Veuillez patienter et réessayer.",
    "shop.booking_error": "La réservation n'a pas pu être créée. Veuillez réessayer.",
    "shop.payment_checking": "Vérification du statut de paiement...",
    "shop.payment_success_title": "Paiement réussi !",
    "shop.payment_success_desc": "Votre réservation est confirmée. Un e-mail de confirmation a été envoyé.",
    "shop.payment_failed_title": "Échec du paiement",
    "shop.payment_failed_desc": "Votre paiement n'a pas pu être traité. Veuillez réessayer.",
    "shop.payment_try_again": "Réessayer",
    "shop.payment_pending_title": "Paiement en cours",
    "shop.payment_pending_desc": "Votre paiement est en cours de traitement. Vous recevrez un e-mail de confirmation une fois terminé.",
    "shop.payment_unknown_title": "Quelque chose s'est mal passé",
    "shop.payment_unknown_desc": "Nous n'avons pas pu vérifier votre paiement. Contactez-nous si vous avez besoin d'aide.",
    "shop.pay_and_book": "Payer et réserver",
    "shop.upsell_step": "Suppléments",
    "shop.upsell_required": "Sélection obligatoire",
    "shop.upsell_optional": "Facultatif",
    "shop.upsell_skip": "Passer",
    "shop.unavailable_title": "Boutique indisponible",
    "shop.unavailable_description": "Cette page de réservation n'accepte pas de nouveaux rendez-vous pour le moment.",
    "settings.title": "Paramètres",
    "settings.back": "Retour aux Paramètres",
    "settings.organisation": "Paramètres de l'organisation",
    "settings.organisation_desc": "Nom, adresse, coordonnées, numéro de chambre de commerce & TVA",
    "settings.opening_hours": "Heures d'ouverture",
    "settings.opening_hours_desc": "Définissez votre planning hebdomadaire et jours de fermeture",
    "settings.booking": "Paramètres de réservation",
    "settings.booking_desc": "Intervalles de créneaux et configuration des champs",
    "settings.checkout": "Champs de paiement",
    "settings.checkout_desc": "Champs personnalisés à remplir lors du paiement",
    "settings.embed": "Widget intégré",
    "settings.embed_desc": "Obtenez le code iframe pour intégrer les réservations sur votre site",
    "settings.shop": "Paramètres de la boutique",
    "settings.shop_desc": "Logo, image de marque et apparence de la boutique",
    "settings.billing": "Facturation & Abonnement",
    "settings.billing_desc": "Gérez votre abonnement, moyen de paiement et factures",
    "settings.language": "Langue",
    "settings.profile": "Paramètres du profil",
    "settings.profile_desc": "Détails personnels, mot de passe et préférences d'apparence",
    "onboarding.org_title": "Détails de l'organisation",
    "onboarding.org_desc": "Parlez-nous de votre entreprise",
    "onboarding.company_name": "Nom de l'entreprise *",
    "onboarding.company_placeholder": "Nom de votre entreprise",
    "onboarding.email": "E-mail",
    "onboarding.phone": "Téléphone",
    "onboarding.address": "Adresse",
    "onboarding.postal_code": "Code postal",
    "onboarding.city": "Ville",
    "onboarding.country": "Pays",
    "onboarding.coc": "N° chambre de commerce",
    "onboarding.vat": "N° TVA",
    "onboarding.continue": "Continuer",
    "onboarding.hours_title": "Heures d'ouverture",
    "onboarding.hours_desc": "Définissez votre planning hebdomadaire",
    "onboarding.to": "à",
    "onboarding.closed": "Fermé",
    "onboarding.back": "Retour",
    "onboarding.finishing": "Configuration…",
    "onboarding.finish": "Terminer la configuration",
    "onboarding.name_required": "Le nom de l'entreprise est requis.",
    "onboarding.success": "Votre organisation est configurée !",
    "onboarding.error": "Une erreur est survenue.",
    "onboarding.mon": "Lundi",
    "onboarding.tue": "Mardi",
    "onboarding.wed": "Mercredi",
    "onboarding.thu": "Jeudi",
    "onboarding.fri": "Vendredi",
    "onboarding.sat": "Samedi",
    "onboarding.sun": "Dimanche",
    "auth.sign_in": "Se connecter",
    "auth.sign_in_desc": "Entrez vos identifiants pour accéder à votre tableau de bord",
    "auth.sign_up": "Créer un compte",
    "auth.sign_up_desc": "Inscrivez-vous pour gérer vos réservations",
    "auth.email": "E-mail",
    "auth.password": "Mot de passe",
    "auth.first_name": "Prénom",
    "auth.last_name": "Nom",
    "auth.signing_in": "Connexion…",
    "auth.creating": "Création du compte…",
    "auth.no_account": "Pas encore de compte ?",
    "auth.have_account": "Vous avez déjà un compte ?",
    "auth.welcome_back": "Bon retour",
    "auth.welcome_back_desc": "Gérez vos réservations, services et clients en un seul endroit.",
    "auth.start_free": "Commencer gratuitement",
    "auth.start_free_desc": "Configurez votre système de réservation en quelques minutes. Aucune carte requise.",
    "auth.verify_title": "Vérifiez votre e-mail",
    "auth.verify_desc": "Nous vous avons envoyé un lien de vérification. Veuillez vérifier votre boîte de réception et cliquer sur le lien pour vérifier votre compte.",
    "auth.back_to_login": "Retour à la connexion",
    "auth.error_duplicate": "Un compte avec cet e-mail existe déjà.",
    "auth.accept_terms_pre": "J'accepte les",
    "auth.terms_link": "Conditions Générales",
    "auth.accept_terms_and": "et la",
    "auth.privacy_link": "Politique de Confidentialité",
    "auth.accept_terms_error": "Vous devez accepter les Conditions Générales et la Politique de Confidentialité pour créer un compte.",
    "auth.forgot_password": "Mot de passe oublié ?",
    "auth.forgot_title": "Réinitialiser le mot de passe",
    "auth.forgot_desc": "Entrez votre e-mail et nous vous enverrons un lien de réinitialisation.",
    "auth.forgot_hero_desc": "Ne vous inquiétez pas, ça arrive aux meilleurs.",
    "auth.send_reset_link": "Envoyer le lien",
    "auth.sending": "Envoi…",
    "auth.forgot_sent_title": "Vérifiez votre e-mail",
    "auth.forgot_sent_desc": "Si un compte existe avec cet e-mail, nous avons envoyé un lien de réinitialisation.",
    "auth.reset_title": "Nouveau mot de passe",
    "auth.reset_desc": "Entrez votre nouveau mot de passe ci-dessous.",
    "auth.reset_hero_desc": "Choisissez un mot de passe fort pour sécuriser votre compte.",
    "auth.new_password": "Nouveau mot de passe",
    "auth.confirm_password": "Confirmer le mot de passe",
    "auth.update_password": "Mettre à jour",
    "auth.updating": "Mise à jour…",
    "auth.password_updated": "Mot de passe mis à jour !",
    "auth.redirecting": "Redirection vers votre tableau de bord…",
    "auth.passwords_mismatch": "Les mots de passe ne correspondent pas.",
    "auth.password_too_short": "Le mot de passe doit comporter au moins 6 caractères.",
    "booking.interval": "Intervalle de créneaux",
    "booking.days": "Jours",
    "booking.hours": "Heures",
    "booking.minutes_label": "Minutes",
    "booking.window_title": "Fenêtre de réservation",
    "booking.auto_confirm_label": "Confirmer automatiquement les réservations",
    "booking.auto_confirm_desc": "Les nouvelles réservations seront automatiquement confirmées au lieu d'être en attente.",
    "booking.window_desc": "Contrôlez à quelle distance les clients peuvent réserver à l'avance.",
    "booking.min_lead_label": "Délai minimum",
    "booking.min_lead_desc": "Les clients doivent réserver au moins ce délai à l'avance.",
    "booking.max_advance_label": "Durée maximale à l'avance",
    "booking.max_advance_desc": "Les clients ne peuvent pas réserver plus loin que cela. Laisser vide pour aucune limite.",
    "booking.min_lead_snap": "Si le délai dépasse un nouveau jour, ouvrir les réservations dès le début de ce jour",
    "booking.max_advance_snap": "Si la durée maximale se termine un jour, autoriser les réservations jusqu'à la fin de ce jour",
    "booking.save": "Enregistrer",
    // Blocked times
    "blocked.title": "Créneaux bloqués",
    "blocked.block_time": "Bloquer un créneau",
    "blocked.edit": "Modifier",
    "blocked.edit_title": "Modifier le créneau bloqué",
    "blocked.back": "Retour aux créneaux bloqués",
    "blocked.no_items": "Aucun créneau bloqué pour le moment.",
    "blocked.type_single_day": "Journée entière",
    "blocked.type_date_range": "Plage de dates",
    "blocked.type_time_range": "Plage horaire",
    "blocked.type_datetime_range": "Plage de dates et heures",
    "blocked.type_daily_time_range": "Bloc horaire quotidien",
    "blocked.desc_single_day": "Bloquer une journée entière",
    "blocked.desc_date_range": "Bloquer plusieurs jours consécutifs",
    "blocked.desc_time_range": "Bloquer des heures spécifiques sur un jour",
    "blocked.desc_datetime_range": "Bloquer des heures sur plusieurs jours",
    "blocked.desc_daily_time_range": "Bloquer les mêmes heures chaque jour sur une période",
    "blocked.block_type": "Type de blocage",
    "blocked.select_date": "Sélectionner la date",
    "blocked.select_dates": "Sélectionner les dates",
    "blocked.select_time": "Sélectionner l'heure",
    "blocked.start_time": "Heure de début",
    "blocked.end_time": "Heure de fin",
    "blocked.summary": "Résumé",
    "blocked.type": "Type",
    "blocked.date": "Date",
    "blocked.start_date": "Date de début",
    "blocked.end_date": "Date de fin",
    "blocked.time": "Heure",
    "blocked.select_date_hint": "Sélectionnez une date pour voir le résumé.",
    "blocked.status": "Statut",
    "blocked.status_active": "Ce blocage est actuellement actif",
    "blocked.status_inactive": "Ce blocage est inactif et ne sera pas appliqué",
    "blocked.saving": "Enregistrement…",
    "blocked.save_changes": "Enregistrer les modifications",
    "blocked.cancel": "Annuler",
    "blocked.created": "Créneau bloqué créé !",
    "blocked.updated": "Créneau bloqué mis à jour !",
    "blocked.activated": "Créneau bloqué activé",
    "blocked.deactivated": "Créneau bloqué désactivé",
    "blocked.deleted": "Créneau bloqué supprimé.",
    "blocked.delete_title": "Supprimer le créneau bloqué",
    "blocked.delete_desc": "Êtes-vous sûr de vouloir supprimer ce créneau bloqué ? Cette action est irréversible.",
    "blocked.delete_confirm": "Supprimer",
    // Customers
    "customers.title": "Clients",
    "customers.total": "total",
    "customers.search": "Rechercher des clients...",
    "customers.no_items": "Aucun client pour le moment. Ils apparaîtront ici dès qu'une réservation sera effectuée.",
    "customers.not_found": "Client introuvable.",
    "customers.back": "Retour aux Clients",
    "customers.delete": "Supprimer",
    "customers.delete_title": "Supprimer le client",
    "customers.delete_desc_pre": "Êtes-vous sûr de vouloir supprimer",
    "customers.delete_desc_post": " ? Cette action est irréversible.",
    "customers.deleted": "supprimé.",
    "customers.cancel": "Annuler",
    "customers.total_bookings": "Total réservations",
    "customers.total_spent": "Total dépensé",
    "customers.customer_since": "Client depuis",
    "customers.reservation_history": "Historique des réservations",
    "customers.no_reservations": "Aucune réservation trouvée.",
    "customers.col_customer": "Client",
    "customers.col_joined": "Inscrit",
    "customers.col_actions": "Actions",
    "customers.col_service": "Service",
    "customers.col_date": "Date",
    "customers.col_price": "Prix",
    "customers.col_status": "Statut",
    // Services
    "services.title": "Services",
    "services.add": "Ajouter un service",
    "services.no_items": "Aucun service pour le moment. Créez votre premier service pour commencer.",
    "services.col_service": "Service",
    "services.col_price": "Prix",
    "services.col_duration": "Durée",
    "services.col_options": "Options",
    "services.col_status": "Statut",
    "services.col_actions": "Actions",
    "services.active": "Actif",
    "services.inactive": "Inactif",
    "services.edit": "Modifier",
    "services.delete": "Supprimer",
    "services.delete_title": "Supprimer le service",
    "services.delete_desc_pre": "Êtes-vous sûr de vouloir supprimer",
    "services.delete_desc_post": " ? Cette action est irréversible.",
    "services.deleted": "a été supprimé.",
    "services.cancel": "Annuler",
    "services.back": "Retour aux Services",
    "services.edit_title": "Modifier le service",
    "services.create_title": "Créer un service",
    "services.details": "Détails du service",
    "services.name_label": "Nom du service *",
    "services.name_placeholder": "ex. Coupe de cheveux",
    "services.description_label": "Description",
    "services.description_placeholder": "Décrivez ce que ce service comprend...",
    "services.price_label": "Prix (€) *",
    "services.duration_label": "Durée (minutes) *",
    "services.active_label": "Actif",
    "services.active_desc": "Ce service est visible dans la boutique",
    "services.inactive_desc": "Ce service est masqué dans la boutique",
    "services.options_title": "Options",
    "services.options_desc": "Ajoutez des extras que les clients peuvent sélectionner lors de la réservation",
    "services.add_option": "Ajouter une option",
    "services.selection_settings": "Paramètres de sélection",
    "services.selection_mode": "Mode de sélection",
    "services.mode_single": "Unique — le client choisit une option",
    "services.mode_multiple": "Multiple — le client choisit toute combinaison",
    "services.required": "Obligatoire",
    "services.required_desc": "Le client doit sélectionner au moins une option",
    "services.optional_desc": "Les options sont facultatives",
    "services.no_options": "Aucune option pour le moment. Ajoutez-en une pour proposer des extras.",
    "services.option_label": "Option",
    "services.option_name": "Nom",
    "services.option_name_placeholder": "ex. Taille de barbe",
    "services.option_price": "Prix supplémentaire (€)",
    "services.option_duration": "Durée supplémentaire (min)",
    "services.saving": "Enregistrement…",
    "services.save_changes": "Enregistrer",
    "services.create": "Créer le service",
    "services.updated": "Service mis à jour !",
    "services.created": "Service créé !",
    "services.fill_required": "Veuillez remplir tous les champs obligatoires.",
    // Upsells
    "upsells.title": "Upsells",
    "upsells.add": "Ajouter un upsell",
    "upsells.no_items": "Pas encore d'upsells. Créez des étapes d'upsell réutilisables à associer à vos services.",
    "upsells.back": "Retour aux Upsells",
    "upsells.edit_title": "Modifier l'upsell",
    "upsells.create_title": "Créer un upsell",
    "upsells.name_label": "Nom de l'upsell *",
    "upsells.name_placeholder": "ex. Soin capillaire premium",
    "upsells.settings": "Paramètres",
    "upsells.selection_mode": "Mode de sélection",
    "upsells.mode_single": "Unique — le client choisit une option",
    "upsells.mode_multiple": "Multiple — le client choisit toute combinaison",
    "upsells.required": "Obligatoire",
    "upsells.required_desc": "Le client doit sélectionner au moins une option",
    "upsells.optional_desc": "La sélection est optionnelle",
    "upsells.options_title": "Options",
    "upsells.options_desc": "Ajoutez les options parmi lesquelles les clients peuvent choisir",
    "upsells.add_option": "Ajouter une option",
    "upsells.no_options": "Pas encore d'options. Ajoutez-en une.",
    "upsells.option_label": "Option",
    "upsells.option_name": "Nom",
    "upsells.option_name_placeholder": "ex. Soin profond",
    "upsells.option_price": "Prix supplémentaire (€)",
    "upsells.option_duration": "Durée supplémentaire (min)",
    "upsells.saving": "Enregistrement…",
    "upsells.save_changes": "Enregistrer",
    "upsells.create": "Créer l'upsell",
    "upsells.updated": "Upsell mis à jour !",
    "upsells.created": "Upsell créé !",
    "upsells.fill_required": "Veuillez remplir le nom de l'upsell.",
    "upsells.delete": "Supprimer",
    "upsells.delete_title": "Supprimer l'upsell",
    "upsells.delete_desc_pre": "Êtes-vous sûr de vouloir supprimer",
    "upsells.delete_desc_post": " ? Il sera aussi retiré de tous les services associés.",
    "upsells.deleted": "a été supprimé.",
    "upsells.cancel": "Annuler",
    "upsells.col_name": "Nom",
    "upsells.col_options": "Options",
    "upsells.col_required": "Obligatoire",
    "upsells.col_mode": "Mode",
    "upsells.col_actions": "Actions",
    "upsells.edit": "Modifier",
    "upsells.yes": "Oui",
    "upsells.no": "Non",
    "upsells.single": "Unique",
    "upsells.multiple": "Multiple",
    "upsells.used_by": "Utilisé par {count} services",
    "services.upsells_title": "Étapes d'upsell",
    "services.upsells_desc": "Associez des étapes d'upsell réutilisables affichées après le choix du service",
    "services.add_upsell": "Ajouter un upsell",
    "services.no_upsells": "Aucune étape d'upsell. Ajoutez-en une depuis votre bibliothèque.",
    "services.manage_upsells": "Gérer les Upsells",
    "services.select_upsell": "Sélectionnez un upsell à ajouter…",
    "services.upsell_already_added": "Cet upsell est déjà associé.",
    // Reservations
    "reservations.title": "Réservations",
    "reservations.total": "total",
    "reservations.search": "Rechercher des réservations...",
    "reservations.filter_date": "Filtrer par date",
    "reservations.no_items": "Aucune réservation pour le moment. Elles apparaîtront ici dès que les clients réserveront.",
    "reservations.col_customer": "Client",
    "reservations.col_service": "Service",
    "reservations.col_datetime": "Date et heure",
    "reservations.col_status": "Statut",
    "reservations.col_price": "Prix",
    "reservations.col_actions": "Actions",
    "reservations.status_all": "Tous les statuts",
    "reservations.status_confirmed": "Confirmé",
    "reservations.status_pending": "En attente",
    "reservations.status_cancelled": "Annulé",
    "reservations.status_completed": "Terminé",
    "reservations.status_no_show": "Absent",
    "reservations.status_awaiting_payment": "En attente de paiement",
    "reservations.status_expired": "Expiré",
    "reservations.delete_title": "Supprimer la réservation",
    "reservations.delete_desc": "Êtes-vous sûr de vouloir supprimer cette réservation pour",
    "reservations.delete_undone": " ? Cette action est irréversible.",
    "reservations.deleted": "Réservation supprimée.",
    "reservations.cancel": "Annuler",
    "reservations.delete": "Supprimer",
    "reservations.reset_filters": "Réinitialiser les filtres",
    "reservations.back": "Retour aux Réservations",
    "reservations.not_found": "Réservation introuvable.",
    "reservations.booked_on": "Réservé le",
    "reservations.at": "à",
    "reservations.reschedule": "Reprogrammer",
    "reservations.customer": "Client",
    "reservations.view_profile": "Voir le profil client",
    "reservations.appointment": "Rendez-vous",
    "reservations.date": "Date",
    "reservations.time": "Heure",
    "reservations.duration": "Durée",
    "reservations.service": "Service",
    "reservations.options": "Options",
    "reservations.payment": "Paiement",
    "reservations.total_amount": "Total",
    "reservations.payment_status": "Statut du paiement",
    "reservations.free": "Gratuit",
    "reservations.pay_at_location": "Paiement sur place",
    "reservations.paid_online": "Payé en ligne",
    "reservations.notes": "Notes internes",
    "reservations.notes_placeholder": "Ajouter une note… (visible uniquement par le personnel)",
    "reservations.no_notes": "Aucune note pour le moment.",
    "reservations.note_added": "Note ajoutée",
    "reservations.note_updated": "Note mise à jour",
    "reservations.note_deleted": "Note supprimée",
    "reservations.activity": "Activité",
    "reservations.created_label": "Réservation créée",
    "reservations.rescheduled_label": "Réservation reprogrammée",
    "reservations.rescheduled_from": "De",
    "reservations.rescheduled_to": "à",
    "reservations.cancelled_label": "Réservation annulée",
    "reservations.booked_by": "Réservé par",
    "reservations.for": "pour",
    "reservations.status_change_title": "Modifier le statut",
    "reservations.status_change_desc_pre": "Êtes-vous sûr de vouloir changer le statut de",
    "reservations.status_change_desc_to": "à",
    "reservations.confirm": "Confirmer",
    "reservations.status_changed_to": "Statut changé en",
    "reservations.changed_from": "Changé de",
    "reservations.status_updated_to": "Statut mis à jour en",
    "reservations.note_added_label": "Note interne ajoutée",
    "reservations.reschedule_title": "Reprogrammer la réservation",
    "reservations.reschedule_current": "Actuellement réservé :",
    "reservations.reschedule_available": "Disponible le",
    "reservations.reschedule_no_slots": "Aucun créneau disponible à cette date.",
    "reservations.reschedule_confirm": "Confirmer la reprogrammation",
    "reservations.reschedule_failed": "Échec de la reprogrammation.",
    "reservations.reschedule_success": "Réservation reprogrammée au",
    "reservations.this_customer": "ce client",
    "calendar.title": "Calendrier",
    "calendar.month": "Mois",
    "calendar.week": "Semaine",
    "calendar.day": "Jour",
    "calendar.today": "Aujourd'hui",
    "calendar.mon": "Lun",
    "calendar.tue": "Mar",
    "calendar.wed": "Mer",
    "calendar.thu": "Jeu",
    "calendar.fri": "Ven",
    "calendar.sat": "Sam",
    "calendar.sun": "Dim",
    "calendar.more": "de plus",
    "calendar.close": "Fermer",
    "calendar.view_details": "Voir les détails",
    "banner.payment_failed": "Échec du paiement — votre compte est limité.",
    "banner.update_payment": "Mettre à jour le paiement",
    "banner.cancelled": "Votre abonnement a été annulé. L'accès prend fin dans {days} jour(s).",
    "banner.reactivate": "Réactiver",
    "banner.trial": "Il reste {days} jour(s) à votre essai gratuit.",
    "banner.trial_expired": "Votre essai gratuit a expiré.",
    "banner.setup_payment": "Configurer le paiement",
    "org.title": "Détails de l'organisation",
    "org.company_name": "Nom de l'entreprise",
    "org.email": "E-mail",
    "org.phone": "Téléphone",
    "org.address": "Adresse",
    "org.postal_code": "Code postal",
    "org.city": "Ville",
    "org.country": "Pays",
    "org.coc_number": "N° registre commerce",
    "org.vat_number": "N° TVA",
    "org.saved": "Détails de l'organisation enregistrés !",
    "org.saving": "Enregistrement…",
    "org.save": "Enregistrer",
    "hours.title": "Heures d'ouverture",
    "hours.monday": "Lundi",
    "hours.tuesday": "Mardi",
    "hours.wednesday": "Mercredi",
    "hours.thursday": "Jeudi",
    "hours.friday": "Vendredi",
    "hours.saturday": "Samedi",
    "hours.sunday": "Dimanche",
    "hours.to": "à",
    "hours.closed": "Fermé",
    "hours.saved": "Heures d'ouverture enregistrées !",
    "hours.saving": "Enregistrement…",
    "hours.save": "Enregistrer les heures",
    "checkout.title": "Champs de paiement",
    "checkout.desc": "Définissez les champs personnalisés que les clients remplissent lors du paiement.",
    "checkout.fields": "Champs",
    "checkout.add_field": "Ajouter un champ",
    "checkout.no_fields": "Aucun champ configuré. Ajoutez-en un pour collecter des informations supplémentaires.",
    "checkout.field_num": "Champ",
    "checkout.required": "Obligatoire",
    "checkout.label": "Libellé *",
    "checkout.label_placeholder": "ex. Numéro de téléphone",
    "checkout.type": "Type",
    "checkout.placeholder": "Texte indicatif",
    "checkout.placeholder_text": "Texte indicatif...",
    "checkout.dropdown_options": "Options du menu déroulant (séparées par des virgules)",
    "checkout.dropdown_placeholder": "Option 1, Option 2, Option 3",
    "checkout.label_required": "Tous les champs doivent avoir un libellé.",
    "checkout.saved": "Champs de paiement enregistrés !",
    "checkout.load_failed": "Échec du chargement des champs",
    "checkout.save": "Enregistrer",
    "checkout.type_text": "Texte",
    "checkout.type_email": "E-mail",
    "checkout.type_phone": "Numéro de téléphone",
    "checkout.type_number": "Nombre",
    "checkout.type_textarea": "Texte long",
    "checkout.type_select": "Menu déroulant",
    "checkout.type_checkbox": "Case à cocher",
    "embed.title": "Widget de réservation intégré",
    "embed.desc": "Copiez ce code pour intégrer le processus de réservation sur votre site.",
    "embed.copy": "Copier le code",
    "embed.copied": "Code d'intégration copié !",
    "shopset.title": "Paramètres de la boutique",
    "shopset.save_changes": "Enregistrer",
    "shopset.logo": "Logo",
    "shopset.logo_desc": "Téléchargez un logo pour les modes clair et sombre. Sans logo, le nom de votre entreprise sera affiché.",
    "shopset.light_logo": "Logo mode clair",
    "shopset.dark_logo": "Logo mode sombre",
    "shopset.logo_size": "Taille du logo",
    "shopset.colors": "Couleurs",
    "shopset.colors_desc": "Personnalisez les couleurs des boutons, du texte et de l'arrière-plan. Laissez vide pour les valeurs par défaut.",
    "shopset.light_mode": "Mode clair",
    "shopset.dark_mode": "Mode sombre",
    "shopset.btn_color": "Couleur du bouton",
    "shopset.btn_text_color": "Couleur du texte du bouton",
    "shopset.text_color": "Couleur du texte",
    "shopset.bg_color": "Couleur d'arrière-plan",
    "shopset.card_bg": "Arrière-plan des cartes",
    "shopset.typography": "Typographie",
    "shopset.typography_desc": "Choisissez une police et une taille pour votre page de réservation.",
    "shopset.font_family": "Police",
    "shopset.font_size": "Taille de police",
    "shopset.unsaved": "Vous avez des modifications non enregistrées",
    "shopset.discard": "Annuler",
    "shopset.saved": "Paramètres de la boutique enregistrés !",
    "shopset.uploading": "Téléchargement…",
    "shopset.upload": "Télécharger",
    "shopset.logo_removed": "Logo supprimé.",
    "shopset.size_small": "Petit",
    "shopset.size_medium": "Moyen",
    "shopset.size_large": "Grand",
    "shopset.size_xlarge": "Très grand",
    "shopset.font_small": "Petit (14px)",
    "shopset.font_medium": "Moyen (16px)",
    "shopset.font_large": "Grand (18px)",
    "shopset.preview_light": "Mode clair",
    "shopset.preview_dark": "Mode sombre",
    "shopset.preview_service": "Nom du service",
    "shopset.preview_desc": "Courte description du service.",
    "shopset.preview_book": "Réserver",
    "billing.title": "Facturation & Abonnement",
    "billing.subscription": "Abonnement",
    "billing.payment_failed": "Échec du paiement",
    "billing.payment_failed_desc": "Votre dernier paiement a échoué. Votre compte est limité à la facturation uniquement. Mettez à jour votre moyen de paiement pour restaurer l'accès complet.",
    "billing.update_payment": "Mettre à jour le paiement",
    "billing.status": "Statut",
    "billing.free_trial": "Essai gratuit",
    "billing.trial_expired_label": "Essai expiré",
    "billing.trial_expired_title": "Votre essai gratuit a expiré",
    "billing.trial_expired_desc": "Configurez un abonnement pour continuer à utiliser toutes les fonctionnalités.",
    "billing.trial_ends": "Fin de l'essai",
    "billing.days_left": "jours restants",
    "billing.plan": "Forfait",
    "billing.next_payment": "Prochain paiement",
    "billing.start_sub_desc": "Démarrez votre abonnement maintenant pour continuer à utiliser Slotify après votre essai.",
    "billing.setup_payment": "Configurer le paiement",
    "billing.active_msg": "Votre abonnement est actif. Les paiements sont traités automatiquement.",
    "billing.cancel_sub": "Annuler l'abonnement",
    "billing.cancel_title": "Annuler l'abonnement ?",
    "billing.cancel_desc": "Êtes-vous sûr de vouloir annuler votre abonnement Slotify ? Votre compte perdra l'accès à toutes les fonctionnalités immédiatement. Cette action est irréversible.",
    "billing.keep_sub": "Garder l'abonnement",
    "billing.yes_cancel": "Oui, annuler",
    "billing.cancelled_msg": "Votre abonnement a été annulé.",
    "billing.reactivate": "Réactiver l'abonnement",
    "billing.invoices": "Factures",
    "billing.no_invoices": "Aucune facture pour le moment.",
    "billing.start_failed": "Impossible de démarrer l'abonnement. Réessayez.",
    "billing.cancel_success": "Votre abonnement a été annulé.",
    "billing.cancel_failed": "Impossible d'annuler l'abonnement. Réessayez.",
    "billing.download_failed": "Échec du téléchargement de la facture.",
    "billing.download": "Télécharger la facture",
    "profile.title": "Paramètres du profil",
    "profile.personal": "Informations personnelles",
    "profile.first_name": "Prénom",
    "profile.last_name": "Nom",
    "profile.email": "E-mail",
    "profile.save_details": "Enregistrer",
    "profile.saved": "Profil mis à jour !",
    "profile.change_password": "Changer le mot de passe",
    "profile.current_password": "Mot de passe actuel",
    "profile.new_password": "Nouveau mot de passe",
    "profile.confirm_password": "Confirmer le mot de passe",
    "profile.update_password": "Mettre à jour",
    "profile.password_updated": "Mot de passe mis à jour !",
    "profile.password_mismatch": "Les mots de passe ne correspondent pas.",
    "profile.password_short": "Le mot de passe doit contenir au moins 6 caractères.",
    "profile.appearance": "Apparence",
    "profile.theme": "Thème",
    "profile.theme_light": "Clair",
    "profile.theme_dark": "Sombre",
    "profile.theme_system": "Système",
    "profile.save_appearance": "Enregistrer l'apparence",
    "profile.appearance_saved": "Apparence mise à jour !",
    "profile.saving": "Enregistrement…",
    "profile.updating": "Mise à jour…",
    // Landing page
    "landing.hero_badge": "✨ La plateforme de réservation moderne pour les entreprises",
    "landing.hero_title_1": "Des réservations ",
    "landing.hero_title_highlight": "simplifiées",
    "landing.hero_desc": "Offrez à votre entreprise un système de réservation élégant. Permettez à vos clients de prendre rendez-vous 24h/24, 7j/7 pendant que vous vous concentrez sur l'essentiel — développer votre activité.",
    "landing.hero_cta": "Essai gratuit de 30 jours",
    "landing.hero_pricing": "Voir les tarifs",
    "landing.nav_features": "Fonctionnalités",
    "landing.nav_how": "Comment ça marche",
    "landing.nav_testimonials": "Témoignages",
    "landing.nav_pricing": "Tarifs",
    "landing.nav_extensions": "Extensions",
    "landing.nav_login": "Se connecter",
    "landing.nav_signup": "Commencer gratuitement",
    "landing.features_label": "Fonctionnalités",
    "landing.features_title": "Tout ce dont vous avez besoin pour gérer vos réservations",
    "landing.features_desc": "Une boîte à outils complète conçue pour les entreprises de services de toute taille.",
    "landing.feat_calendar": "Calendrier intelligent",
    "landing.feat_calendar_desc": "Vue calendrier complète avec vues mois, semaine et jour. Visualisez toutes vos réservations et évitez les doublons.",
    "landing.feat_timeslots": "Créneaux flexibles",
    "landing.feat_timeslots_desc": "Configurez les intervalles, bloquez des dates ou plages horaires et définissez des heures d'ouverture personnalisées par jour.",
    "landing.feat_customers": "Gestion des clients",
    "landing.feat_customers_desc": "Suivez automatiquement vos clients, leur historique de réservation et leurs préférences — le tout organisé par e-mail.",
    "landing.feat_shop": "Page de réservation personnalisée",
    "landing.feat_shop_desc": "Chaque entreprise obtient une belle page de réservation personnalisable avec votre logo, vos couleurs et votre identité.",
    "landing.feat_widget": "Widget intégrable",
    "landing.feat_widget_desc": "Intégrez le flux de réservation directement sur votre site web avec un simple iframe. Fonctionne partout.",
    "landing.feat_services": "Gestion des services",
    "landing.feat_services_desc": "Configurez vos services avec tarifs, durée et options supplémentaires. Contrôle total sur votre offre.",
    "landing.how_label": "Comment ça marche",
    "landing.how_title": "Opérationnel en quelques minutes",
    "landing.how_step1": "Inscription & configuration",
    "landing.how_step1_desc": "Créez votre compte, vérifiez votre e-mail et configurez les détails de votre entreprise et vos horaires.",
    "landing.how_step2": "Ajoutez vos services",
    "landing.how_step2_desc": "Définissez vos services avec tarifs, durée et options supplémentaires. Personnalisez votre page de réservation.",
    "landing.how_step3": "Partagez votre lien",
    "landing.how_step3_desc": "Partagez votre page de réservation unique ou intégrez-la sur votre site. Les clients peuvent réserver 24h/24.",
    "landing.how_step4": "Gérez & développez",
    "landing.how_step4_desc": "Suivez les réservations, gérez les clients et utilisez les informations pour développer votre activité.",
    "landing.pricing_trial_badge": "30 jours d'essai gratuit",
    "landing.pricing_title": "Un seul plan, tout inclus",
    "landing.pricing_desc": "Pas de niveaux, pas de frais cachés. Accédez à toutes les fonctionnalités dès le premier jour. Commencez avec un essai gratuit de 30 jours.",
    "landing.pricing_month": "/mois",
    "landing.pricing_tax": "hors TVA",
    "landing.pricing_cta": "Commencer l'essai gratuit",
    "landing.pricing_includes": "Tout ce que vous obtenez :",
    "landing.pricing_f1": "Réservations illimitées",
    "landing.pricing_f2": "Page de réservation personnalisée",
    "landing.pricing_f3": "Widget intégrable",
    "landing.pricing_f4": "Gestion des clients",
    "landing.pricing_f5": "Calendrier & planification",
    "landing.pricing_f6": "Options de service",
    "landing.pricing_f7": "Notifications par e-mail",
    "landing.pricing_f8": "Horaires d'ouverture & créneaux bloqués",
    "landing.testimonials_label": "Témoignages",
    "landing.testimonials_title": "Apprécié par les entreprises partout",
    "landing.testimonial1": "Slotify a transformé notre gestion des rendez-vous. Les absences ont chuté de 40 % grâce aux rappels automatiques. La page de réservation est magnifique et nos clients l'adorent.",
    "landing.testimonial1_role": "Propriétaire, Luxe Hair Studio",
    "landing.testimonial2": "La configuration de nos services avec différentes durées et options a été incroyablement simple. Nous étions opérationnels en moins d'une heure. Le meilleur investissement.",
    "landing.testimonial2_role": "Directeur, Clinique PhysioCare",
    "landing.testimonial3": "Le widget intégrable a tout changé. Nous l'avons ajouté à notre site et les réservations ont augmenté de 60 %. La gestion du calendrier est intuitive et puissante.",
    "landing.testimonial3_role": "Fondatrice, Zen Massage Therapy",
    "landing.cta_title": "Prêt à simplifier vos réservations ?",
    "landing.cta_desc": "Rejoignez des centaines d'entreprises qui utilisent déjà Slotify. Mise en place en minutes, aucune carte bancaire requise.",
    "landing.cta_button": "Commencer gratuitement",
    "landing.footer_rights": "© 2026 Slotify. Tous droits réservés.",
    // Legal pages
    "legal.privacy_title": "Politique de Confidentialité",
    "legal.terms_title": "Conditions Générales",
    "legal.back_home": "Retour à l'accueil",
    "legal.last_updated": "Dernière mise à jour",
    "privacy.intro_title": "1. Introduction",
    "privacy.intro_text": "Slotify (\"nous\", \"notre\") s'engage à protéger votre vie privée. Cette politique de confidentialité explique comment nous collectons, utilisons, divulguons et protégeons vos informations lorsque vous utilisez notre plateforme de réservation en ligne et services associés.",
    "privacy.data_collect_title": "2. Informations collectées",
    "privacy.data_collect_text": "Nous collectons les informations que vous fournissez directement, notamment : votre nom, adresse e-mail, numéro de téléphone et coordonnées professionnelles lors de la création d'un compte ; les données de réservation et de rendez-vous ; les informations de paiement traitées par nos prestataires ; et les données d'utilisation telles que les adresses IP, le type de navigateur et les pages visitées.",
    "privacy.data_use_title": "3. Utilisation des informations",
    "privacy.data_use_text": "Nous utilisons les informations collectées pour : fournir et maintenir notre plateforme de réservation ; traiter les rendez-vous et envoyer des confirmations et rappels ; améliorer nos services et l'expérience utilisateur ; communiquer avec vous concernant les mises à jour, le support et les offres promotionnelles ; respecter les obligations légales ; et détecter et prévenir la fraude ou les abus.",
    "privacy.cookies_title": "4. Cookies & Suivi",
    "privacy.cookies_text": "Nous utilisons des cookies et technologies similaires pour améliorer votre expérience. Les cookies nécessaires sont toujours actifs. Les cookies analytiques et marketing ne sont activés qu'avec votre consentement explicite via notre bannière de cookies.",
    "privacy.sharing_title": "5. Partage des données",
    "privacy.sharing_text": "Nous ne vendons pas vos données personnelles. Nous pouvons partager des informations avec : les prestataires de services qui participent à l'exploitation de notre plateforme ; les autorités légales lorsque la loi l'exige ; et les partenaires commerciaux uniquement avec votre consentement explicite.",
    "privacy.retention_title": "6. Conservation des données",
    "privacy.retention_text": "Nous conservons vos données personnelles tant que votre compte est actif ou tant que nécessaire pour fournir nos services. Après la suppression du compte, nous pouvons conserver certaines données pendant 30 jours maximum à des fins de sauvegarde.",
    "privacy.rights_title": "7. Vos droits",
    "privacy.rights_text": "En vertu des lois applicables sur la protection des données (y compris le RGPD), vous avez le droit de : accéder, corriger ou supprimer vos données personnelles ; restreindre ou vous opposer au traitement ; la portabilité des données ; retirer votre consentement à tout moment ; et déposer une plainte auprès d'une autorité de contrôle.",
    "privacy.contact_title": "8. Nous contacter",
    "privacy.contact_text": "Si vous avez des questions sur cette politique de confidentialité ou souhaitez exercer vos droits, contactez-nous à privacy@slotify.app.",
    "terms.acceptance_title": "1. Acceptation des conditions",
    "terms.acceptance_text": "En accédant ou en utilisant les services de Slotify, vous acceptez d'être lié par ces Conditions Générales. Si vous n'êtes pas d'accord, veuillez ne pas utiliser notre plateforme.",
    "terms.service_title": "2. Description du service",
    "terms.service_text": "Slotify fournit une plateforme de réservation et de gestion de rendez-vous en ligne pour les entreprises. Notre service comprend un tableau de bord pour gérer les réservations, services, clients et paramètres, ainsi qu'une page de réservation publique pour vos clients.",
    "terms.accounts_title": "3. Comptes utilisateurs",
    "terms.accounts_text": "Vous devez fournir des informations exactes et complètes lors de la création d'un compte. Vous êtes responsable de la confidentialité de vos identifiants et de toutes les activités sous votre compte.",
    "terms.payment_title": "4. Paiements & Facturation",
    "terms.payment_text": "Slotify offre une période d'essai gratuite suivie d'un abonnement payant. Les paiements sont traités par notre prestataire (Mollie). Les abonnements se renouvellent automatiquement sauf annulation. Les prix s'entendent hors taxes applicables sauf indication contraire.",
    "terms.ip_title": "5. Propriété intellectuelle",
    "terms.ip_text": "Tout le contenu, les fonctionnalités et la fonctionnalité de la plateforme Slotify nous appartiennent et sont protégés par les lois sur la propriété intellectuelle. Vous conservez la propriété de toutes les données que vous saisissez dans la plateforme.",
    "terms.liability_title": "6. Limitation de responsabilité",
    "terms.liability_text": "Slotify est fourni \"tel quel\" sans garantie d'aucune sorte. Nous ne sommes pas responsables des dommages indirects, accessoires ou consécutifs découlant de votre utilisation de la plateforme.",
    "terms.termination_title": "7. Résiliation",
    "terms.termination_text": "Chaque partie peut résilier l'accord à tout moment. À la résiliation, votre droit d'utiliser la plateforme cesse immédiatement. Nous conserverons vos données pendant 30 jours après la résiliation.",
    "terms.changes_title": "8. Modifications des conditions",
    "terms.changes_text": "Nous nous réservons le droit de modifier ces conditions à tout moment. Nous vous informerons des modifications importantes par e-mail ou via la plateforme.",
    "terms.governing_title": "9. Droit applicable",
    "terms.governing_text": "Ces conditions sont régies par le droit néerlandais. Tout litige sera soumis aux tribunaux compétents des Pays-Bas.",
    // Cookie banner
    "cookie.message": "Nous utilisons des cookies pour améliorer votre expérience et analyser le trafic. Vous pouvez gérer vos préférences ci-dessous.",
    "cookie.accept_all": "Tout accepter",
    "cookie.decline": "Refuser",
    "cookie.manage": "Gérer",
    "cookie.manage_desc": "Choisissez les cookies que vous souhaitez autoriser. Les cookies nécessaires sont toujours actifs.",
    "cookie.necessary": "Nécessaires",
    "cookie.necessary_desc": "Requis pour le fonctionnement du site",
    "cookie.analytics": "Analyse",
    "cookie.analytics_desc": "Nous aident à comprendre comment vous utilisez le site",
    "cookie.marketing": "Marketing",
    "cookie.marketing_desc": "Utilisés pour la publicité ciblée",
    "cookie.save": "Enregistrer les préférences",
    "cookie.back": "Retour",
    "settings.team": "Équipe",
    "settings.team_desc": "Inviter et gérer les membres de l'équipe",
    "team.title": "Paramètres de l'équipe",
    "team.subtitle": "Gérez les membres de votre équipe et envoyez des invitations.",
    "team.members": "Membres",
    "team.pending_invites": "Invitations en attente",
    "team.invite": "Inviter un membre",
    "team.invite_desc": "Envoyez une invitation par e-mail pour rejoindre votre organisation.",
    "team.email": "Adresse e-mail",
    "team.role": "Rôle",
    "team.role_admin": "Administrateur",
    "team.role_member": "Membre",
    "team.send_invite": "Envoyer l'invitation",
    "team.sending": "Envoi…",
    "team.invite_sent": "Invitation envoyée",
    "team.invite_error": "Échec de l'envoi de l'invitation",
    "team.cancel_invite": "Annuler l'invitation",
    "team.invite_cancelled": "Invitation annulée",
    "team.no_members": "Aucun membre pour le moment.",
    "team.no_invites": "Aucune invitation en attente.",
    "team.joined": "Rejoint",
    "team.invited_on": "Invité le",
    "team.you": "(vous)",
    "team.already_in_org": "Cet utilisateur fait déjà partie de votre organisation",
    "invite.title": "Vous êtes invité !",
    "invite.join_org": "Rejoindre {org}",
    "invite.accept": "Accepter l'invitation",
    "invite.accepting": "Acceptation…",
    "invite.accepted": "Vous avez rejoint l'organisation !",
    "invite.invalid": "Cette invitation est invalide ou a expiré.",
    "invite.login_first": "Connectez-vous ou créez un compte avec cet e-mail pour accepter l'invitation.",
    "invite.email_mismatch": "Connectez-vous avec l'adresse e-mail à laquelle l'invitation a été envoyée.",
    "invite.already_in_org": "Vous appartenez déjà à une organisation.",
    "settings.extensions": "Extensions",
    "settings.extensions_desc": "Activer les intégrations et modules complémentaires pour votre organisation",
    "ext.title": "Extensions",
    "ext.subtitle": "Activez et configurez les intégrations pour votre organisation.",
    "ext.loading": "Chargement…",
    "ext.enabled": "Extension activée",
    "ext.disabled": "Extension désactivée",
    "ext.configure": "Configurer",
    "ext.saved": "Paramètres enregistrés !",
    "ext.save_error": "Échec de l'enregistrement des paramètres",
    "ext.experimental": "Expérimental",
    "ext.self_service_title": "Libre-service client",
    "ext.self_service_desc": "Permettez aux clients d'annuler ou de reprogrammer leurs rendez-vous avec leur numéro de réservation et e-mail.",
    "ext.statistics_title": "Statistiques",
    "ext.statistics_desc": "Consultez des graphiques détaillés et des informations sur vos réservations, revenus et clients.",
    "nav.statistics": "Statistiques",
    "nav.employees": "Employés",
    "ext.employees_title": "Employés",
    "ext.employees_desc": "Gérez les membres de l'équipe, attribuez des services et activez les réservations parallèles.",
    "employees.title": "Employés",
    "employees.add": "Ajouter un employé",
    "employees.no_items": "Aucun employé. Ajoutez votre premier employé.",
    "employees.col_name": "Nom",
    "employees.col_status": "Statut",
    "employees.active": "Actif",
    "employees.inactive": "Inactif",
    "employees.edit": "Modifier",
    "employees.delete": "Supprimer",
    "employees.deleted": "supprimé",
    "employees.delete_title": "Supprimer l'employé",
    "employees.delete_desc_pre": "Êtes-vous sûr de vouloir supprimer ",
    "employees.delete_desc_post": " ? Cette action est irréversible.",
    "employees.cancel": "Annuler",
    "employees.back": "Retour",
    "employees.edit_title": "Modifier l'employé",
    "employees.add_title": "Ajouter un employé",
    "employees.basic_info": "Informations de base",
    "employees.name": "Nom",
    "employees.name_placeholder": "ex. Jean Dupont",
    "employees.image_url": "URL de la photo",
    "employees.photo_hint": "Cliquez sur l'avatar pour télécharger une photo",
    "employees.status_label": "Actif",
    "employees.status_active_desc": "Cet employé est visible et réservable.",
    "employees.status_inactive_desc": "Cet employé est masqué et non réservable.",
    "employees.services": "Services attribués",
    "employees.no_services": "Aucun service créé.",
    "employees.working_hours": "Horaires personnalisés",
    "employees.working_hours_desc": "Remplacer les heures d'ouverture de l'organisation pour cet employé.",
    "employees.closed": "Libre",
    "employees.saving": "Enregistrement…",
    "employees.save_changes": "Enregistrer",
    "employees.create": "Créer l'employé",
    "employees.created": "Employé créé",
    "employees.updated": "Employé mis à jour",
    "employees.ext_title": "Paramètres des employés",
    "employees.ext_subtitle": "Configurez le fonctionnement des employés dans votre flux de réservation.",
    "employees.allow_customer_selection": "Permettre au client de choisir l'employé",
    "employees.allow_customer_selection_desc": "Afficher un sélecteur d'employé dans le flux de réservation.",
    "employees.allow_no_preference": "Autoriser l'option 'Pas de préférence'",
    "employees.allow_no_preference_desc": "Permettre aux clients de passer la sélection et d'être attribué automatiquement.",
    "employees.employee_step_before_service": "Sélection de l'employé avant le service",
    "employees.employee_step_before_service_desc": "Afficher le sélecteur d'employé avant l'étape de sélection du service.",
    "employees.save_settings": "Enregistrer les paramètres",
    "blocked.employee": "Employé",
    "blocked.all_employees": "Tous les employés",
    "days.mon": "Lun",
    "days.tue": "Mar",
    "days.wed": "Mer",
    "days.thu": "Jeu",
    "days.fri": "Ven",
    "days.sat": "Sam",
    "days.sun": "Dim",
    "stats.title": "Statistiques",
    "stats.subtitle": "Aperçu de vos réservations et revenus.",
    "stats.period_this_month": "Ce mois-ci",
    "stats.period_prev_month": "Mois précédent",
    "stats.period_custom": "Personnalisé",
    "stats.period_next_month": "Mois prochain",
    "stats.from": "Du",
    "stats.to": "Au",
    "stats.total_bookings": "Total réservations",
    "stats.total_revenue": "Revenu total",
    "stats.unique_customers": "Clients uniques",
    "stats.cancellations": "Annulations",
    "stats.bookings_per_day": "Réservations par jour",
    "stats.revenue_per_week": "Revenus par semaine",
    "stats.by_service": "Par service",
    "stats.by_status": "Par statut",
    "stats.bookings": "Réservations",
    "stats.cancelled": "Annulées",
    "stats.revenue": "Revenus",
    "stats.no_data": "Aucune donnée pour cette période",
    "stats.status_pending": "En attente",
    "stats.status_confirmed": "Confirmé",
    "stats.status_cancelled": "Annulé",
    "stats.status_completed": "Terminé",
    "stats.status_no_show": "Non présenté",
    "stats.status_awaiting_payment": "En attente de paiement",
    "stats.status_expired": "Expiré",
    "ext.ss_title": "Libre-service client",
    "ext.ss_subtitle": "Configurez la façon dont les clients peuvent gérer leurs réservations.",
    "ext.ss_saved": "Paramètres de libre-service enregistrés !",
    "ext.ss_allow_cancel": "Autoriser les annulations",
    "ext.ss_allow_cancel_desc": "Les clients peuvent annuler leurs propres réservations",
    "ext.ss_cancel_deadline": "Date limite d'annulation",
    "ext.ss_hours_before": "heures avant le rendez-vous",
    "ext.ss_allow_rebook": "Autoriser la reprogrammation",
    "ext.ss_allow_rebook_desc": "Les clients peuvent reprogrammer leurs propres réservations",
    "ext.ss_rebook_deadline": "Date limite de reprogrammation",
    "ext.ss_visibility": "Visibilité",
    "ext.ss_show_shop": "Afficher dans la boutique",
    "ext.ss_show_shop_desc": "Afficher un lien de gestion de réservation sur la page de la boutique",
    "ext.ss_show_email": "Afficher dans l'e-mail",
    "ext.ss_show_email_desc": "Inclure un lien de gestion de réservation dans l'e-mail de confirmation",
    "ext.ss_show_thank_you": "Afficher sur la page de remerciement",
    "ext.ss_show_thank_you_desc": "Afficher un lien de gestion de réservation sur la page de remerciement après la réservation",
    "ext.ss_saving": "Enregistrement…",
    "ext.ss_save": "Enregistrer les paramètres",
    "ext.ss_not_available": "Cette fonctionnalité n'est pas disponible.",
    "ext.ss_manage_title": "Gérer votre réservation",
    "ext.ss_reservation_number": "Numéro de réservation",
    "ext.ss_service": "Service",
    "ext.ss_datetime": "Date & Heure",
    "ext.ss_status": "Statut",
    "ext.ss_rebook_btn": "Reprogrammer",
    "ext.ss_cancel_btn": "Annuler la réservation",
    "ext.ss_cancel_confirm_title": "Annuler cette réservation ?",
    "ext.ss_cancel_confirm_desc": "Êtes-vous sûr de vouloir annuler cette réservation ? Cette action est irréversible.",
    "ext.ss_keep": "Garder la réservation",
    "ext.ss_cancelling": "Annulation…",
    "ext.ss_confirm_cancel": "Oui, annuler",
    "ext.ss_deadline_passed": "Le délai pour modifier cette réservation est dépassé.",
    "ext.ss_pick_new_datetime": "Choisissez une nouvelle date et heure",
    "ext.ss_available_times": "Heures disponibles",
    "ext.ss_rebooking": "Reprogrammation…",
    "ext.ss_confirm_rebook": "Confirmer le nouvel horaire",
    "ext.ss_looking_up": "Recherche…",
    "ext.ss_lookup": "Trouver ma réservation",
    "ext.ss_not_found": "Aucune réservation trouvée. Vérifiez votre numéro de réservation et votre e-mail.",
    "ext.ss_cancelled_title": "Réservation annulée",
    "ext.ss_cancelled_desc": "Votre réservation a été annulée avec succès.",
    "ext.ss_rebooked_title": "Réservation reprogrammée",
    "ext.ss_rebooked_desc": "Votre réservation a été reprogrammée avec succès.",
    "ext.ss_time": "Heure",
    "ext.ss_duration": "Durée",
    "ext.ss_rebook_notifications": "Notifications de reprogrammation",
    "ext.ss_rebook_notifications_desc": "Envoyer des notifications par e-mail lorsqu'une réservation est reprogrammée.",
    "ext.ss_notify_customer": "Notifier le client",
    "ext.ss_notify_customer_desc": "Envoyer un e-mail au client concernant la réservation reprogrammée.",
    "ext.ss_notify_organisation": "Notifier l'organisation",
    "ext.ss_notify_organisation_desc": "Envoyer un e-mail à l'organisation concernant la réservation reprogrammée.",
    "ext.ss_cancel_notifications": "Notifications d'annulation",
    "ext.ss_cancel_notifications_desc": "Envoyer des notifications par e-mail lorsqu'une réservation est annulée.",
    "ext.ss_cancel_notify_customer": "Notifier le client",
    "ext.ss_cancel_notify_customer_desc": "Envoyer un e-mail au client lorsque sa réservation est annulée.",
    "ext.ss_cancel_notify_organisation": "Notifier l'organisation",
    "ext.ss_cancel_notify_organisation_desc": "Envoyer un e-mail à l'organisation lorsqu'une réservation est annulée.",
    "ext.ss_manage_link": "Annuler ou reprogrammer votre réservation",
    "ext.ss_allowed_statuses": "Statuts autorisés",
    "ext.ss_allowed_statuses_desc": "Seules les réservations avec ces statuts peuvent être modifiées par le client.",
    "ext.ss_status_not_allowed": "Cette réservation ne peut pas être modifiée avec son statut actuel.",
    "ext.pao_card_title": "Paiement à la commande",
    "ext.pao_card_desc": "Exigez que les clients paient avant que leur réservation ne soit confirmée.",
    "ext.pao_title": "Paiement à la commande",
    "ext.pao_subtitle": "Configurez votre fournisseur de paiement pour que les clients paient lors de la réservation.",
    "ext.pao_select_psp": "Fournisseur de paiement",
    "ext.pao_select_psp_desc": "Choisissez le fournisseur de services de paiement à utiliser.",
    "ext.pao_mollie_desc": "PSP européen populaire avec iDEAL, Bancontact, cartes de crédit et plus.",
    "ext.pao_stripe_desc": "PSP mondial avec cartes de crédit, Apple Pay, Google Pay et plus.",
    "ext.pao_test_mode": "Mode test",
    "ext.pao_test_mode_desc": "Utilisez les clés API test/sandbox. Aucun paiement réel ne sera traité.",
    "ext.pao_api_keys": "Clés API",
    "ext.pao_api_keys_desc": "Entrez vos clés API depuis le tableau de bord de votre fournisseur de paiement.",
    "ext.pao_mollie_api_key": "Clé API Mollie",
    "ext.pao_mollie_key_hint": "Trouvez-la dans votre tableau de bord Mollie sous Développeurs → Clés API.",
    "ext.pao_stripe_publishable_key": "Clé publique Stripe",
    "ext.pao_stripe_secret_key": "Clé secrète Stripe",
    "ext.pao_stripe_key_hint": "Trouvez-les dans votre tableau de bord Stripe sous Développeurs → Clés API.",
    "ext.pao_no_keys_warning": "Vous n'avez pas encore entré vos clés API. L'extension ne fonctionnera pas tant que les clés ne seront pas configurées.",
    "ext.pao_expiry_title": "Expiration du paiement",
    "ext.pao_expiry_desc": "Annuler automatiquement les réservations si le paiement n'est pas effectué dans ce délai.",
    "ext.pao_expiry_unit": "minutes",
    "ext.pao_save": "Enregistrer les paramètres",
    "ext.pao_saving": "Enregistrement…",
    "ext.pao_saved": "Paramètres de paiement enregistrés",
    "extpage.back_home": "Retour à l'accueil",
    "extpage.features_title": "Ce qui est inclus",
    "extpage.ss_title": "Libre-service client",
    "extpage.ss_subtitle": "Permettez à vos clients de gérer leurs propres réservations. Réduisez les absences et libérez votre temps grâce aux annulations et reprogrammations automatisées.",
    "extpage.ss_feat_cancel": "Les clients peuvent annuler leurs rendez-vous dans un délai configurable",
    "extpage.ss_feat_rebook": "Permettez aux clients de reprogrammer vers un créneau disponible",
    "extpage.ss_feat_deadline": "Définissez des délais personnalisés pour les annulations et reprogrammations",
    "extpage.ss_feat_notifications": "Notifications automatiques par e-mail pour les annulations et reprogrammations",
    "extpage.ss_feat_shop": "Affichez un lien de gestion de réservation directement sur votre page boutique",
    "extpage.ss_feat_email": "Incluez un lien de gestion de réservation dans les e-mails de confirmation",
    "extpage.stats_title": "Statistiques & Aperçus",
    "extpage.stats_subtitle": "Obtenez un aperçu complet de la performance de votre entreprise avec des graphiques détaillés, des KPI et des analyses clients.",
    "extpage.stats_feat_kpi": "Indicateurs clés de performance pour les réservations, le chiffre d'affaires et les clients",
    "extpage.stats_feat_charts": "Beaux graphiques en barres et circulaires pour des aperçus visuels",
    "extpage.stats_feat_revenue": "Suivez le chiffre d'affaires par service et période",
    "extpage.stats_feat_customers": "Analyses clients incluant nouveaux vs récurrents",
    "extpage.stats_feat_periods": "Filtrez par mois actuel, précédent ou suivant et plages de dates personnalisées",
    "extpage.stats_feat_export": "Aperçu des statuts de réservation incluant absences et annulations",
    "extpage.pao_title": "Paiement à la commande",
    "extpage.pao_subtitle": "Exigez que les clients paient lors de la réservation. Réduisez les absences et sécurisez votre chiffre d'affaires avec des paiements en ligne intégrés.",
    "extpage.pao_feat_online": "Les clients paient directement pendant le processus de réservation",
    "extpage.pao_feat_mollie": "Intégré avec Mollie pour un traitement sécurisé des paiements",
    "extpage.pao_feat_status": "Suivi automatique du statut de paiement sur chaque réservation",
    "extpage.pao_feat_webhook": "Mises à jour en temps réel du statut de paiement via webhooks",
    "extpage.pao_feat_secure": "Traitement des paiements sécurisé conforme PCI",
    "extpage.pao_feat_auto": "Les réservations sont confirmées automatiquement après un paiement réussi",
    "extpage.emp_title": "Employés",
    "extpage.emp_subtitle": "Assignez des employés aux services, définissez des horaires individuels et laissez les clients choisir leur membre d'équipe préféré lors de la réservation.",
    "extpage.emp_feat_assign": "Assignez des employés spécifiques à des services individuels",
    "extpage.emp_feat_hours": "Définissez des horaires de travail personnalisés par employé",
    "extpage.emp_feat_services": "Les employés ne sont affichés que pour les services qu'ils peuvent fournir",
    "extpage.emp_feat_availability": "Filtrage automatique de la disponibilité basé sur les plannings des employés",
    "extpage.emp_feat_calendar": "Les employés sont affichés sur le calendrier avec des blocs colorés",
    "extpage.emp_feat_preference": "Les clients peuvent sélectionner leur employé préféré lors de la réservation",
  },
  de: {
    "nav.calendar": "Kalender",
    "nav.reservations": "Reservierungen",
    "nav.services": "Dienstleistungen",
    "nav.customers": "Kunden",
    "nav.blocked": "Gesperrte Zeiten",
    "nav.upsells": "Upsells",
    "nav.settings": "Einstellungen",
    "shop.title": "Buchen Sie Ihren Termin online",
    "shop.step.service": "Service",
    "shop.step.datetime": "Datum & Uhrzeit",
    "shop.step.details": "Details",
    "shop.step.confirmed": "Bestätigt",
    "shop.choose_service": "Wählen Sie einen Service",
    "shop.add_options": "Optionen hinzufügen:",
    "shop.required_options": "Wählen Sie eine Option (erforderlich)",
    "shop.extra_cost_time": "+€{price} · +{duration} min",
    "shop.total": "Gesamt",
    "shop.continue": "Weiter",
    "shop.back": "Zurück",
    "shop.pick_datetime": "Wählen Sie Datum & Uhrzeit",
    "shop.available_times": "Verfügbare Zeiten für",
    "shop.no_timeslots": "Keine verfügbaren Zeitfenster. Bitte wählen Sie ein anderes Datum.",
    "shop.slot_taken": "Dieses Zeitfenster ist nicht mehr verfügbar. Bitte wählen Sie eine andere Zeit.",
    "shop.your_details": "Ihre Angaben",
    "shop.service": "Service",
    "shop.options": "Optionen",
    "shop.date_time": "Datum & Uhrzeit",
    "shop.first_name": "Vorname",
    "shop.last_name": "Nachname",
    "shop.email": "E-Mail",
    "shop.phone": "Telefon",
    "shop.confirm_booking": "Buchung bestätigen",
    "shop.booking_confirmed": "Buchung bestätigt!",
    "shop.thank_you": "Danke, {name}! Eine Bestätigung wurde an {email} gesendet.",
    "shop.reminder": "Sie erhalten am Tag Ihres Termins eine Erinnerung.",
    "calendar.add_google": "Zu Google Kalender hinzufügen",
    "calendar.add_apple": "Zum Apple Kalender hinzufügen",
    "shop.book_another": "Einen weiteren Termin buchen",
    "shop.rate_limit_error": "Sie haben in kurzer Zeit zu viele Buchungen vorgenommen. Bitte warten Sie einen Moment und versuchen Sie es erneut.",
    "shop.booking_error": "Buchung konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
    "shop.payment_checking": "Zahlungsstatus wird überprüft...",
    "shop.payment_success_title": "Zahlung erfolgreich!",
    "shop.payment_success_desc": "Ihre Buchung wurde bestätigt. Eine Bestätigungsmail wurde gesendet.",
    "shop.payment_failed_title": "Zahlung fehlgeschlagen",
    "shop.payment_failed_desc": "Ihre Zahlung konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.",
    "shop.payment_try_again": "Erneut versuchen",
    "shop.payment_pending_title": "Zahlung wird verarbeitet",
    "shop.payment_pending_desc": "Ihre Zahlung wird noch verarbeitet. Sie erhalten eine Bestätigungsmail, sobald sie abgeschlossen ist.",
    "shop.payment_unknown_title": "Etwas ist schiefgelaufen",
    "shop.payment_unknown_desc": "Wir konnten Ihre Zahlung nicht überprüfen. Kontaktieren Sie uns, wenn Sie Hilfe benötigen.",
    "shop.pay_and_book": "Bezahlen & buchen",
    "shop.upsell_step": "Extras",
    "shop.upsell_required": "Auswahl erforderlich",
    "shop.upsell_optional": "Optional",
    "shop.upsell_skip": "Überspringen",
    "shop.unavailable_title": "Shop nicht verfügbar",
    "shop.unavailable_description": "Diese Buchungsseite nimmt derzeit keine neuen Termine an.",
    "settings.title": "Einstellungen",
    "settings.back": "Zurück zu Einstellungen",
    "settings.organisation": "Organisationseinstellungen",
    "settings.organisation_desc": "Firmenname, Adresse, Kontaktdaten, Handelsregister- & USt-Nummer",
    "settings.opening_hours": "Öffnungszeiten",
    "settings.opening_hours_desc": "Wochenplan und Ruhetage festlegen",
    "settings.booking": "Buchungseinstellungen",
    "settings.booking_desc": "Zeitslot-Intervalle und Checkout-Feldkonfiguration",
    "settings.checkout": "Checkout-Felder",
    "settings.checkout_desc": "Benutzerdefinierte Felder, die Kunden beim Checkout ausfüllen",
    "settings.embed": "Embed-Widget",
    "settings.embed_desc": "Iframe-Code zum Einbetten von Buchungen auf Ihrer Website",
    "settings.shop": "Shop-Einstellungen",
    "settings.shop_desc": "Logo, Branding und Shop-Erscheinungsbild",
    "settings.billing": "Abrechnung & Abonnement",
    "settings.billing_desc": "Abonnement, Zahlungsmethode und Rechnungen verwalten",
    "settings.language": "Sprache",
    "settings.profile": "Profileinstellungen",
    "settings.profile_desc": "Persönliche Daten, Passwort und Darstellungseinstellungen",
    "onboarding.org_title": "Organisationsdetails",
    "onboarding.org_desc": "Erzählen Sie uns von Ihrem Unternehmen",
    "onboarding.company_name": "Firmenname *",
    "onboarding.company_placeholder": "Ihr Firmenname",
    "onboarding.email": "E-Mail",
    "onboarding.phone": "Telefon",
    "onboarding.address": "Adresse",
    "onboarding.postal_code": "Postleitzahl",
    "onboarding.city": "Stadt",
    "onboarding.country": "Land",
    "onboarding.coc": "Handelsregisternummer",
    "onboarding.vat": "USt-IdNr.",
    "onboarding.continue": "Weiter",
    "onboarding.hours_title": "Öffnungszeiten",
    "onboarding.hours_desc": "Legen Sie Ihren Wochenplan fest",
    "onboarding.to": "bis",
    "onboarding.closed": "Geschlossen",
    "onboarding.back": "Zurück",
    "onboarding.finishing": "Wird eingerichtet…",
    "onboarding.finish": "Einrichtung abschließen",
    "onboarding.name_required": "Firmenname ist erforderlich.",
    "onboarding.success": "Ihre Organisation ist eingerichtet!",
    "onboarding.error": "Etwas ist schiefgelaufen.",
    "onboarding.mon": "Montag",
    "onboarding.tue": "Dienstag",
    "onboarding.wed": "Mittwoch",
    "onboarding.thu": "Donnerstag",
    "onboarding.fri": "Freitag",
    "onboarding.sat": "Samstag",
    "onboarding.sun": "Sonntag",
    "auth.sign_in": "Anmelden",
    "auth.sign_in_desc": "Geben Sie Ihre Zugangsdaten ein, um auf Ihr Dashboard zuzugreifen",
    "auth.sign_up": "Konto erstellen",
    "auth.sign_up_desc": "Registrieren Sie sich, um Ihre Buchungen zu verwalten",
    "auth.email": "E-Mail",
    "auth.password": "Passwort",
    "auth.first_name": "Vorname",
    "auth.last_name": "Nachname",
    "auth.signing_in": "Anmeldung…",
    "auth.creating": "Konto wird erstellt…",
    "auth.no_account": "Noch kein Konto?",
    "auth.have_account": "Haben Sie bereits ein Konto?",
    "auth.welcome_back": "Willkommen zurück",
    "auth.welcome_back_desc": "Verwalten Sie Ihre Buchungen, Dienstleistungen und Kunden an einem Ort.",
    "auth.start_free": "Kostenlos starten",
    "auth.start_free_desc": "Richten Sie Ihr Buchungssystem in wenigen Minuten ein. Keine Kreditkarte erforderlich.",
    "auth.verify_title": "Überprüfen Sie Ihre E-Mail",
    "auth.verify_desc": "Wir haben Ihnen einen Bestätigungslink gesendet. Bitte überprüfen Sie Ihren Posteingang und klicken Sie auf den Link, um Ihr Konto zu bestätigen.",
    "auth.back_to_login": "Zurück zur Anmeldung",
    "auth.error_duplicate": "Es existiert bereits ein Konto mit dieser E-Mail-Adresse.",
    "auth.accept_terms_pre": "Ich stimme den",
    "auth.terms_link": "AGB",
    "auth.accept_terms_and": "und der",
    "auth.privacy_link": "Datenschutzerklärung",
    "auth.accept_terms_error": "Sie müssen die AGB und die Datenschutzerklärung akzeptieren, um ein Konto zu erstellen.",
    "auth.forgot_password": "Passwort vergessen?",
    "auth.forgot_title": "Passwort zurücksetzen",
    "auth.forgot_desc": "Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Reset-Link.",
    "auth.forgot_hero_desc": "Keine Sorge, das passiert den Besten.",
    "auth.send_reset_link": "Reset-Link senden",
    "auth.sending": "Wird gesendet…",
    "auth.forgot_sent_title": "Überprüfen Sie Ihre E-Mail",
    "auth.forgot_sent_desc": "Wenn ein Konto mit dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen des Passworts gesendet.",
    "auth.reset_title": "Neues Passwort festlegen",
    "auth.reset_desc": "Geben Sie unten Ihr neues Passwort ein.",
    "auth.reset_hero_desc": "Wählen Sie ein starkes Passwort, um Ihr Konto zu schützen.",
    "auth.new_password": "Neues Passwort",
    "auth.confirm_password": "Passwort bestätigen",
    "auth.update_password": "Passwort aktualisieren",
    "auth.updating": "Wird aktualisiert…",
    "auth.password_updated": "Passwort aktualisiert!",
    "auth.redirecting": "Weiterleitung zu Ihrem Dashboard…",
    "auth.passwords_mismatch": "Passwörter stimmen nicht überein.",
    "auth.password_too_short": "Das Passwort muss mindestens 6 Zeichen lang sein.",
    "booking.interval": "Zeitslot-Intervall",
    "booking.days": "Tage",
    "booking.hours": "Stunden",
    "booking.minutes_label": "Minuten",
    "booking.window_title": "Buchungsfenster",
    "booking.auto_confirm_label": "Buchungen automatisch bestätigen",
    "booking.auto_confirm_desc": "Neue Buchungen werden automatisch auf bestätigt gesetzt statt auf ausstehend.",
    "booking.window_desc": "Legen Sie fest, wie weit im Voraus Kunden Termine buchen können.",
    "booking.min_lead_label": "Mindestvorlaufzeit",
    "booking.min_lead_desc": "Kunden müssen mindestens so weit im Voraus buchen.",
    "booking.max_advance_label": "Maximale Vorlaufzeit",
    "booking.max_advance_desc": "Kunden können nicht weiter im Voraus buchen als dies. Leer lassen für keine Begrenzung.",
    "booking.min_lead_snap": "Wenn die Vorlaufzeit einen neuen Tag erreicht, Buchungen ab Tagesbeginn öffnen",
    "booking.max_advance_snap": "Wenn die maximale Vorlaufzeit an einem Tag endet, Buchungen bis Tagesende erlauben",
    "booking.save": "Einstellungen speichern",
    // Blocked times
    "blocked.title": "Gesperrte Zeiten",
    "blocked.block_time": "Zeit sperren",
    "blocked.edit": "Bearbeiten",
    "blocked.edit_title": "Gesperrte Zeit bearbeiten",
    "blocked.back": "Zurück zu Gesperrte Zeiten",
    "blocked.no_items": "Noch keine gesperrten Zeiten.",
    "blocked.type_single_day": "Ganzer Tag",
    "blocked.type_date_range": "Datumsbereich",
    "blocked.type_time_range": "Zeitbereich",
    "blocked.type_datetime_range": "Datums- & Zeitbereich",
    "blocked.type_daily_time_range": "Täglicher Zeitblock",
    "blocked.desc_single_day": "Einen ganzen Tag sperren",
    "blocked.desc_date_range": "Mehrere aufeinanderfolgende Tage sperren",
    "blocked.desc_time_range": "Bestimmte Stunden an einem Tag sperren",
    "blocked.desc_datetime_range": "Stunden über mehrere Tage sperren",
    "blocked.desc_daily_time_range": "Gleiche Stunden an jedem Tag in einem Bereich sperren",
    "blocked.block_type": "Sperrtyp",
    "blocked.select_date": "Datum auswählen",
    "blocked.select_dates": "Daten auswählen",
    "blocked.select_time": "Uhrzeit auswählen",
    "blocked.start_time": "Startzeit",
    "blocked.end_time": "Endzeit",
    "blocked.summary": "Zusammenfassung",
    "blocked.type": "Typ",
    "blocked.date": "Datum",
    "blocked.start_date": "Startdatum",
    "blocked.end_date": "Enddatum",
    "blocked.time": "Uhrzeit",
    "blocked.select_date_hint": "Wählen Sie ein Datum, um die Zusammenfassung zu sehen.",
    "blocked.status": "Status",
    "blocked.status_active": "Diese Sperrung ist derzeit aktiv",
    "blocked.status_inactive": "Diese Sperrung ist inaktiv und wird nicht angewendet",
    "blocked.saving": "Speichern…",
    "blocked.save_changes": "Änderungen speichern",
    "blocked.cancel": "Abbrechen",
    "blocked.created": "Gesperrte Zeit erstellt!",
    "blocked.updated": "Gesperrte Zeit aktualisiert!",
    "blocked.activated": "Gesperrte Zeit aktiviert",
    "blocked.deactivated": "Gesperrte Zeit deaktiviert",
    "blocked.deleted": "Gesperrte Zeit gelöscht.",
    "blocked.delete_title": "Gesperrte Zeit löschen",
    "blocked.delete_desc": "Sind Sie sicher, dass Sie diese gesperrte Zeit löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
    "blocked.delete_confirm": "Löschen",
    // Customers
    "customers.title": "Kunden",
    "customers.total": "gesamt",
    "customers.search": "Kunden suchen...",
    "customers.no_items": "Noch keine Kunden. Sie erscheinen hier, sobald Buchungen eingehen.",
    "customers.not_found": "Kunde nicht gefunden.",
    "customers.back": "Zurück zu Kunden",
    "customers.delete": "Löschen",
    "customers.delete_title": "Kunden löschen",
    "customers.delete_desc_pre": "Sind Sie sicher, dass Sie",
    "customers.delete_desc_post": " löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
    "customers.deleted": "gelöscht.",
    "customers.cancel": "Abbrechen",
    "customers.total_bookings": "Buchungen gesamt",
    "customers.total_spent": "Gesamtausgaben",
    "customers.customer_since": "Kunde seit",
    "customers.reservation_history": "Reservierungsverlauf",
    "customers.no_reservations": "Keine Reservierungen gefunden.",
    "customers.col_customer": "Kunde",
    "customers.col_joined": "Beigetreten",
    "customers.col_actions": "Aktionen",
    "customers.col_service": "Service",
    "customers.col_date": "Datum",
    "customers.col_price": "Preis",
    "customers.col_status": "Status",
    // Services
    "services.title": "Dienstleistungen",
    "services.add": "Dienst hinzufügen",
    "services.no_items": "Noch keine Dienste. Erstellen Sie Ihren ersten Dienst, um loszulegen.",
    "services.col_service": "Dienst",
    "services.col_price": "Preis",
    "services.col_duration": "Dauer",
    "services.col_options": "Optionen",
    "services.col_status": "Status",
    "services.col_actions": "Aktionen",
    "services.active": "Aktiv",
    "services.inactive": "Inaktiv",
    "services.edit": "Bearbeiten",
    "services.delete": "Löschen",
    "services.delete_title": "Dienst löschen",
    "services.delete_desc_pre": "Sind Sie sicher, dass Sie",
    "services.delete_desc_post": " löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
    "services.deleted": "wurde gelöscht.",
    "services.cancel": "Abbrechen",
    "services.back": "Zurück zu Diensten",
    "services.edit_title": "Dienst bearbeiten",
    "services.create_title": "Dienst erstellen",
    "services.details": "Dienstdetails",
    "services.name_label": "Dienstname *",
    "services.name_placeholder": "z.B. Haarschnitt",
    "services.description_label": "Beschreibung",
    "services.description_placeholder": "Beschreiben Sie, was dieser Dienst beinhaltet...",
    "services.price_label": "Preis (€) *",
    "services.duration_label": "Dauer (Minuten) *",
    "services.active_label": "Aktiv",
    "services.active_desc": "Dieser Dienst ist im Shop sichtbar",
    "services.inactive_desc": "Dieser Dienst ist im Shop ausgeblendet",
    "services.options_title": "Optionen",
    "services.options_desc": "Fügen Sie optionale Extras hinzu, die Kunden bei der Buchung auswählen können",
    "services.add_option": "Option hinzufügen",
    "services.selection_settings": "Auswahleinstellungen",
    "services.selection_mode": "Auswahlmodus",
    "services.mode_single": "Einzeln — Kunde wählt eine Option",
    "services.mode_multiple": "Mehrfach — Kunde wählt beliebige Kombination",
    "services.required": "Erforderlich",
    "services.required_desc": "Kunde muss mindestens eine Option auswählen",
    "services.optional_desc": "Optionen sind optional",
    "services.no_options": "Noch keine Optionen. Fügen Sie eine hinzu, um Extras anzubieten.",
    "services.option_label": "Option",
    "services.option_name": "Name",
    "services.option_name_placeholder": "z.B. Bart trimmen",
    "services.option_price": "Aufpreis (€)",
    "services.option_duration": "Zusätzliche Dauer (Min)",
    "services.saving": "Speichern…",
    "services.save_changes": "Änderungen speichern",
    "services.create": "Dienst erstellen",
    "services.updated": "Dienst aktualisiert!",
    "services.created": "Dienst erstellt!",
    "services.fill_required": "Bitte füllen Sie alle Pflichtfelder aus.",
    // Upsells
    "upsells.title": "Upsells",
    "upsells.add": "Upsell hinzufügen",
    "upsells.no_items": "Noch keine Upsells. Erstellen Sie wiederverwendbare Upsell-Schritte für Ihre Dienste.",
    "upsells.back": "Zurück zu Upsells",
    "upsells.edit_title": "Upsell bearbeiten",
    "upsells.create_title": "Upsell erstellen",
    "upsells.name_label": "Upsell-Name *",
    "upsells.name_placeholder": "z.B. Haarpflege-Upgrade",
    "upsells.settings": "Einstellungen",
    "upsells.selection_mode": "Auswahlmodus",
    "upsells.mode_single": "Einzeln — Kunde wählt eine Option",
    "upsells.mode_multiple": "Mehrfach — Kunde wählt beliebige Kombination",
    "upsells.required": "Erforderlich",
    "upsells.required_desc": "Kunde muss mindestens eine Option auswählen",
    "upsells.optional_desc": "Auswahl ist optional",
    "upsells.options_title": "Optionen",
    "upsells.options_desc": "Fügen Sie Optionen hinzu, aus denen Kunden wählen können",
    "upsells.add_option": "Option hinzufügen",
    "upsells.no_options": "Noch keine Optionen. Fügen Sie eine hinzu.",
    "upsells.option_label": "Option",
    "upsells.option_name": "Name",
    "upsells.option_name_placeholder": "z.B. Tiefenpflege",
    "upsells.option_price": "Zusatzpreis (€)",
    "upsells.option_duration": "Zusatzdauer (Min)",
    "upsells.saving": "Speichern…",
    "upsells.save_changes": "Änderungen speichern",
    "upsells.create": "Upsell erstellen",
    "upsells.updated": "Upsell aktualisiert!",
    "upsells.created": "Upsell erstellt!",
    "upsells.fill_required": "Bitte geben Sie den Upsell-Namen ein.",
    "upsells.delete": "Löschen",
    "upsells.delete_title": "Upsell löschen",
    "upsells.delete_desc_pre": "Sind Sie sicher, dass Sie",
    "upsells.delete_desc_post": " löschen möchten? Es wird auch von allen verknüpften Diensten entfernt.",
    "upsells.deleted": "wurde gelöscht.",
    "upsells.cancel": "Abbrechen",
    "upsells.col_name": "Name",
    "upsells.col_options": "Optionen",
    "upsells.col_required": "Erforderlich",
    "upsells.col_mode": "Modus",
    "upsells.col_actions": "Aktionen",
    "upsells.edit": "Bearbeiten",
    "upsells.yes": "Ja",
    "upsells.no": "Nein",
    "upsells.single": "Einzeln",
    "upsells.multiple": "Mehrfach",
    "upsells.used_by": "Verwendet von {count} Diensten",
    "services.upsells_title": "Upsell-Schritte",
    "services.upsells_desc": "Verknüpfen Sie wiederverwendbare Upsell-Schritte, die nach der Dienstauswahl angezeigt werden",
    "services.add_upsell": "Upsell hinzufügen",
    "services.no_upsells": "Keine Upsell-Schritte verknüpft. Fügen Sie einen aus Ihrer Bibliothek hinzu.",
    "services.manage_upsells": "Upsells verwalten",
    "services.select_upsell": "Upsell zum Hinzufügen auswählen…",
    "services.upsell_already_added": "Dieser Upsell ist bereits verknüpft.",
    // Reservations
    "reservations.title": "Reservierungen",
    "reservations.total": "gesamt",
    "reservations.search": "Reservierungen suchen...",
    "reservations.filter_date": "Nach Datum filtern",
    "reservations.no_items": "Noch keine Reservierungen. Sie erscheinen hier, sobald Kunden buchen.",
    "reservations.col_customer": "Kunde",
    "reservations.col_service": "Dienst",
    "reservations.col_datetime": "Datum & Uhrzeit",
    "reservations.col_status": "Status",
    "reservations.col_price": "Preis",
    "reservations.col_actions": "Aktionen",
    "reservations.status_all": "Alle Status",
    "reservations.status_confirmed": "Bestätigt",
    "reservations.status_pending": "Ausstehend",
    "reservations.status_cancelled": "Storniert",
    "reservations.status_completed": "Abgeschlossen",
    "reservations.status_no_show": "Nicht erschienen",
    "reservations.status_awaiting_payment": "Warten auf Zahlung",
    "reservations.status_expired": "Abgelaufen",
    "reservations.delete_title": "Reservierung löschen",
    "reservations.delete_desc": "Sind Sie sicher, dass Sie diese Reservierung löschen möchten für",
    "reservations.delete_undone": "? Diese Aktion kann nicht rückgängig gemacht werden.",
    "reservations.deleted": "Reservierung gelöscht.",
    "reservations.cancel": "Abbrechen",
    "reservations.delete": "Löschen",
    "reservations.reset_filters": "Filter zurücksetzen",
    "reservations.back": "Zurück zu Reservierungen",
    "reservations.not_found": "Reservierung nicht gefunden.",
    "reservations.booked_on": "Gebucht am",
    "reservations.at": "um",
    "reservations.reschedule": "Umplanen",
    "reservations.customer": "Kunde",
    "reservations.view_profile": "Kundenprofil anzeigen",
    "reservations.appointment": "Termin",
    "reservations.date": "Datum",
    "reservations.time": "Uhrzeit",
    "reservations.duration": "Dauer",
    "reservations.service": "Dienst",
    "reservations.options": "Optionen",
    "reservations.payment": "Zahlung",
    "reservations.total_amount": "Gesamt",
    "reservations.payment_status": "Zahlungsstatus",
    "reservations.free": "Kostenlos",
    "reservations.pay_at_location": "Zahlung vor Ort",
    "reservations.paid_online": "Online bezahlt",
    "reservations.notes": "Interne Notizen",
    "reservations.notes_placeholder": "Notiz hinzufügen… (nur für Mitarbeiter sichtbar)",
    "reservations.no_notes": "Noch keine Notizen.",
    "reservations.note_added": "Notiz hinzugefügt",
    "reservations.note_updated": "Notiz aktualisiert",
    "reservations.note_deleted": "Notiz gelöscht",
    "reservations.activity": "Aktivität",
    "reservations.created_label": "Reservierung erstellt",
    "reservations.rescheduled_label": "Reservierung umgebucht",
    "reservations.rescheduled_from": "Von",
    "reservations.rescheduled_to": "nach",
    "reservations.cancelled_label": "Reservierung storniert",
    "reservations.booked_by": "Gebucht von",
    "reservations.for": "für",
    "reservations.status_change_title": "Reservierungsstatus ändern",
    "reservations.status_change_desc_pre": "Sind Sie sicher, dass Sie den Status ändern möchten von",
    "reservations.status_change_desc_to": "zu",
    "reservations.confirm": "Bestätigen",
    "reservations.status_changed_to": "Status geändert zu",
    "reservations.changed_from": "Geändert von",
    "reservations.status_updated_to": "Status aktualisiert auf",
    "reservations.note_added_label": "Interne Notiz hinzugefügt",
    "reservations.reschedule_title": "Reservierung umplanen",
    "reservations.reschedule_current": "Aktuell gebucht:",
    "reservations.reschedule_available": "Verfügbar am",
    "reservations.reschedule_no_slots": "Keine verfügbaren Zeitfenster an diesem Datum.",
    "reservations.reschedule_confirm": "Umplanung bestätigen",
    "reservations.reschedule_failed": "Umplanung der Reservierung fehlgeschlagen.",
    "reservations.reschedule_success": "Reservierung umgeplant auf",
    "reservations.this_customer": "diesen Kunden",
    "calendar.title": "Kalender",
    "calendar.month": "Monat",
    "calendar.week": "Woche",
    "calendar.day": "Tag",
    "calendar.today": "Heute",
    "calendar.mon": "Mo",
    "calendar.tue": "Di",
    "calendar.wed": "Mi",
    "calendar.thu": "Do",
    "calendar.fri": "Fr",
    "calendar.sat": "Sa",
    "calendar.sun": "So",
    "calendar.more": "mehr",
    "calendar.close": "Schließen",
    "calendar.view_details": "Details anzeigen",
    "banner.payment_failed": "Zahlung fehlgeschlagen — Ihr Konto ist eingeschränkt.",
    "banner.update_payment": "Zahlung aktualisieren",
    "banner.cancelled": "Ihr Abonnement wurde gekündigt. Zugang endet in {days} Tag(en).",
    "banner.reactivate": "Reaktivieren",
    "banner.trial": "Noch {days} Tag(e) in Ihrer kostenlosen Testphase.",
    "banner.trial_expired": "Ihre kostenlose Testphase ist abgelaufen.",
    "banner.setup_payment": "Zahlung einrichten",
    "org.title": "Organisationsdetails",
    "org.company_name": "Firmenname",
    "org.email": "E-Mail",
    "org.phone": "Telefon",
    "org.address": "Adresse",
    "org.postal_code": "Postleitzahl",
    "org.city": "Stadt",
    "org.country": "Land",
    "org.coc_number": "Handelsregisternr.",
    "org.vat_number": "USt-IdNr.",
    "org.saved": "Organisationsdetails gespeichert!",
    "org.saving": "Speichern…",
    "org.save": "Änderungen speichern",
    "hours.title": "Öffnungszeiten",
    "hours.monday": "Montag",
    "hours.tuesday": "Dienstag",
    "hours.wednesday": "Mittwoch",
    "hours.thursday": "Donnerstag",
    "hours.friday": "Freitag",
    "hours.saturday": "Samstag",
    "hours.sunday": "Sonntag",
    "hours.to": "bis",
    "hours.closed": "Geschlossen",
    "hours.saved": "Öffnungszeiten gespeichert!",
    "hours.saving": "Speichern…",
    "hours.save": "Zeiten speichern",
    "checkout.title": "Checkout-Felder",
    "checkout.desc": "Definieren Sie benutzerdefinierte Felder, die Kunden beim Checkout ausfüllen.",
    "checkout.fields": "Felder",
    "checkout.add_field": "Feld hinzufügen",
    "checkout.no_fields": "Keine Checkout-Felder konfiguriert. Fügen Sie eines hinzu, um zusätzliche Infos zu sammeln.",
    "checkout.field_num": "Feld",
    "checkout.required": "Pflichtfeld",
    "checkout.label": "Bezeichnung *",
    "checkout.label_placeholder": "z.B. Telefonnummer",
    "checkout.type": "Typ",
    "checkout.placeholder": "Platzhalter",
    "checkout.placeholder_text": "Platzhaltertext...",
    "checkout.dropdown_options": "Dropdown-Optionen (kommagetrennt)",
    "checkout.dropdown_placeholder": "Option 1, Option 2, Option 3",
    "checkout.label_required": "Alle Felder müssen eine Bezeichnung haben.",
    "checkout.saved": "Checkout-Felder gespeichert!",
    "checkout.load_failed": "Checkout-Felder konnten nicht geladen werden",
    "checkout.save": "Änderungen speichern",
    "checkout.type_text": "Text",
    "checkout.type_email": "E-Mail",
    "checkout.type_phone": "Telefonnummer",
    "checkout.type_number": "Nummer",
    "checkout.type_textarea": "Langer Text",
    "checkout.type_select": "Dropdown",
    "checkout.type_checkbox": "Kontrollkästchen",
    "embed.title": "Buchungs-Widget einbetten",
    "embed.desc": "Kopieren Sie diesen Code, um den Buchungsablauf auf Ihrer Website einzubetten.",
    "embed.copy": "Code kopieren",
    "embed.copied": "Einbettungscode kopiert!",
    "shopset.title": "Shop-Einstellungen",
    "shopset.save_changes": "Änderungen speichern",
    "shopset.logo": "Logo",
    "shopset.logo_desc": "Laden Sie ein Logo für hellen und dunklen Modus hoch. Ohne Logo wird Ihr Firmenname angezeigt.",
    "shopset.light_logo": "Logo heller Modus",
    "shopset.dark_logo": "Logo dunkler Modus",
    "shopset.logo_size": "Logogröße",
    "shopset.colors": "Farben",
    "shopset.colors_desc": "Passen Sie Button-, Text- und Hintergrundfarben an. Leer lassen für Standardwerte.",
    "shopset.light_mode": "Heller Modus",
    "shopset.dark_mode": "Dunkler Modus",
    "shopset.btn_color": "Buttonfarbe",
    "shopset.btn_text_color": "Button-Textfarbe",
    "shopset.text_color": "Textfarbe",
    "shopset.bg_color": "Hintergrundfarbe",
    "shopset.card_bg": "Kartenhintergrund",
    "shopset.typography": "Typografie",
    "shopset.typography_desc": "Wählen Sie Schriftart und -größe für Ihre Buchungsseite.",
    "shopset.font_family": "Schriftart",
    "shopset.font_size": "Schriftgröße",
    "shopset.unsaved": "Sie haben ungespeicherte Änderungen",
    "shopset.discard": "Verwerfen",
    "shopset.saved": "Shop-Einstellungen gespeichert!",
    "shopset.uploading": "Hochladen…",
    "shopset.upload": "Hochladen",
    "shopset.logo_removed": "Logo entfernt.",
    "shopset.size_small": "Klein",
    "shopset.size_medium": "Mittel",
    "shopset.size_large": "Groß",
    "shopset.size_xlarge": "Sehr groß",
    "shopset.font_small": "Klein (14px)",
    "shopset.font_medium": "Mittel (16px)",
    "shopset.font_large": "Groß (18px)",
    "shopset.preview_light": "Heller Modus",
    "shopset.preview_dark": "Dunkler Modus",
    "shopset.preview_service": "Dienstname",
    "shopset.preview_desc": "Kurze Beschreibung des Dienstes.",
    "shopset.preview_book": "Jetzt buchen",
    "billing.title": "Abrechnung & Abonnement",
    "billing.subscription": "Abonnement",
    "billing.payment_failed": "Zahlung fehlgeschlagen",
    "billing.payment_failed_desc": "Ihre letzte Zahlung ist fehlgeschlagen. Ihr Konto ist auf die Abrechnung beschränkt. Bitte aktualisieren Sie Ihre Zahlungsmethode.",
    "billing.update_payment": "Zahlungsmethode aktualisieren",
    "billing.status": "Status",
    "billing.free_trial": "Kostenlose Testphase",
    "billing.trial_expired_label": "Testphase abgelaufen",
    "billing.trial_expired_title": "Ihre kostenlose Testphase ist abgelaufen",
    "billing.trial_expired_desc": "Richten Sie ein Abonnement ein, um alle Funktionen weiterhin nutzen zu können.",
    "billing.trial_ends": "Testphase endet",
    "billing.days_left": "Tage übrig",
    "billing.plan": "Tarif",
    "billing.next_payment": "Nächste Zahlung",
    "billing.start_sub_desc": "Starten Sie jetzt Ihr Abonnement, um Slotify nach der Testphase weiter zu nutzen.",
    "billing.setup_payment": "Zahlung einrichten",
    "billing.active_msg": "Ihr Abonnement ist aktiv. Zahlungen werden automatisch verarbeitet.",
    "billing.cancel_sub": "Abonnement kündigen",
    "billing.cancel_title": "Abonnement kündigen?",
    "billing.cancel_desc": "Sind Sie sicher, dass Sie Ihr Slotify-Abonnement kündigen möchten? Ihr Konto verliert sofort den Zugang zu allen Funktionen. Dies kann nicht rückgängig gemacht werden.",
    "billing.keep_sub": "Abonnement behalten",
    "billing.yes_cancel": "Ja, kündigen",
    "billing.cancelled_msg": "Ihr Abonnement wurde gekündigt.",
    "billing.reactivate": "Abonnement reaktivieren",
    "billing.invoices": "Rechnungen",
    "billing.no_invoices": "Noch keine Rechnungen.",
    "billing.start_failed": "Abonnement konnte nicht gestartet werden. Versuchen Sie es erneut.",
    "billing.cancel_success": "Ihr Abonnement wurde gekündigt.",
    "billing.cancel_failed": "Kündigung fehlgeschlagen. Versuchen Sie es erneut.",
    "billing.download_failed": "Rechnung konnte nicht heruntergeladen werden.",
    "billing.download": "Rechnung herunterladen",
    "profile.title": "Profileinstellungen",
    "profile.personal": "Persönliche Daten",
    "profile.first_name": "Vorname",
    "profile.last_name": "Nachname",
    "profile.email": "E-Mail",
    "profile.save_details": "Daten speichern",
    "profile.saved": "Profil aktualisiert!",
    "profile.change_password": "Passwort ändern",
    "profile.current_password": "Aktuelles Passwort",
    "profile.new_password": "Neues Passwort",
    "profile.confirm_password": "Passwort bestätigen",
    "profile.update_password": "Passwort aktualisieren",
    "profile.password_updated": "Passwort aktualisiert!",
    "profile.password_mismatch": "Passwörter stimmen nicht überein.",
    "profile.password_short": "Passwort muss mindestens 6 Zeichen lang sein.",
    "profile.appearance": "Darstellung",
    "profile.theme": "Design",
    "profile.theme_light": "Hell",
    "profile.theme_dark": "Dunkel",
    "profile.theme_system": "System",
    "profile.save_appearance": "Darstellung speichern",
    "profile.appearance_saved": "Darstellung aktualisiert!",
    "profile.saving": "Speichern…",
    "profile.updating": "Aktualisieren…",
    // Landing page
    "landing.hero_badge": "✨ Die moderne Buchungsplattform für Unternehmen",
    "landing.hero_title_1": "Buchungen ",
    "landing.hero_title_highlight": "mühelos",
    "landing.hero_desc": "Stärken Sie Ihr Unternehmen mit einem eleganten Buchungssystem. Lassen Sie Kunden rund um die Uhr Termine buchen, während Sie sich auf das Wesentliche konzentrieren — Ihr Geschäft auszubauen.",
    "landing.hero_cta": "Kostenlos testen",
    "landing.hero_pricing": "Preise ansehen",
    "landing.nav_features": "Funktionen",
    "landing.nav_how": "So funktioniert's",
    "landing.nav_testimonials": "Erfahrungen",
    "landing.nav_pricing": "Preise",
    "landing.nav_extensions": "Erweiterungen",
    "landing.nav_login": "Anmelden",
    "landing.nav_signup": "Kostenlos starten",
    "landing.features_label": "Funktionen",
    "landing.features_title": "Alles, was Sie für die Buchungsverwaltung brauchen",
    "landing.features_desc": "Ein komplettes Toolkit für Dienstleistungsunternehmen jeder Größe.",
    "landing.feat_calendar": "Intelligenter Kalender",
    "landing.feat_calendar_desc": "Vollständige Kalenderansicht mit Monats-, Wochen- und Tagesansicht. Sehen Sie alle Buchungen auf einen Blick und vermeiden Sie Doppelbuchungen.",
    "landing.feat_timeslots": "Flexible Zeitfenster",
    "landing.feat_timeslots_desc": "Konfigurieren Sie Zeitintervalle, sperren Sie bestimmte Daten oder Zeitbereiche und legen Sie individuelle Öffnungszeiten pro Tag fest.",
    "landing.feat_customers": "Kundenverwaltung",
    "landing.feat_customers_desc": "Verfolgen Sie automatisch Ihre Kunden, deren Buchungshistorie und Präferenzen — alles nach E-Mail organisiert.",
    "landing.feat_shop": "Eigene Buchungsseite",
    "landing.feat_shop_desc": "Jedes Unternehmen erhält eine schöne, anpassbare Buchungsseite mit Ihrem Logo, Ihren Farben und Ihrem Branding.",
    "landing.feat_widget": "Einbettbares Widget",
    "landing.feat_widget_desc": "Betten Sie den Buchungsablauf direkt auf Ihrer Website mit einem einfachen iframe ein. Funktioniert überall.",
    "landing.feat_services": "Dienstverwaltung",
    "landing.feat_services_desc": "Konfigurieren Sie Dienste mit Preisen, Dauer und optionalen Extras. Volle Kontrolle über Ihr Angebot.",
    "landing.how_label": "So funktioniert's",
    "landing.how_title": "In wenigen Minuten einsatzbereit",
    "landing.how_step1": "Registrieren & einrichten",
    "landing.how_step1_desc": "Erstellen Sie Ihr Konto, verifizieren Sie Ihre E-Mail und konfigurieren Sie Ihre Geschäftsdaten und Öffnungszeiten.",
    "landing.how_step2": "Dienste hinzufügen",
    "landing.how_step2_desc": "Definieren Sie Ihre Dienste mit Preisen, Dauer und optionalen Extras. Passen Sie Ihre Buchungsseite an.",
    "landing.how_step3": "Link teilen",
    "landing.how_step3_desc": "Teilen Sie Ihre einzigartige Buchungsseite oder betten Sie sie auf Ihrer Website ein. Kunden können 24/7 buchen.",
    "landing.how_step4": "Verwalten & wachsen",
    "landing.how_step4_desc": "Verfolgen Sie Buchungen, verwalten Sie Kunden und nutzen Sie Erkenntnisse, um Ihr Geschäft auszubauen.",
    "landing.pricing_trial_badge": "30 Tage kostenlos testen",
    "landing.pricing_title": "Ein Plan, alles inklusive",
    "landing.pricing_desc": "Keine Stufen, keine versteckten Gebühren. Erhalten Sie ab Tag eins vollen Zugriff auf alle Funktionen. Starten Sie mit einer kostenlosen 30-Tage-Testphase.",
    "landing.pricing_month": "/Monat",
    "landing.pricing_tax": "zzgl. MwSt.",
    "landing.pricing_cta": "Kostenlose Testphase starten",
    "landing.pricing_includes": "Alles, was Sie bekommen:",
    "landing.pricing_f1": "Unbegrenzte Buchungen",
    "landing.pricing_f2": "Eigene Buchungsseite",
    "landing.pricing_f3": "Einbettbares Widget",
    "landing.pricing_f4": "Kundenverwaltung",
    "landing.pricing_f5": "Kalender & Terminplanung",
    "landing.pricing_f6": "Service-Extras",
    "landing.pricing_f7": "E-Mail-Benachrichtigungen",
    "landing.pricing_f8": "Öffnungszeiten & gesperrte Zeiten",
    "landing.testimonials_label": "Erfahrungen",
    "landing.testimonials_title": "Von Unternehmen überall geschätzt",
    "landing.testimonial1": "Slotify hat unsere Terminverwaltung komplett verändert. Die No-Shows sanken um 40 % dank der automatischen Erinnerungen. Die Buchungsseite ist wunderschön und unsere Kunden lieben sie.",
    "landing.testimonial1_role": "Inhaberin, Luxe Hair Studio",
    "landing.testimonial2": "Die Einrichtung unserer Dienste mit verschiedenen Dauern und Extras war unglaublich einfach. Wir waren in unter einer Stunde einsatzbereit. Die beste Investition.",
    "landing.testimonial2_role": "Direktor, PhysioCare Klinik",
    "landing.testimonial3": "Das einbettbare Widget war ein Gamechanger. Wir fügten es unserer Website hinzu und die Buchungen stiegen um 60 %. Die Kalenderverwaltung ist intuitiv und leistungsstark.",
    "landing.testimonial3_role": "Gründerin, Zen Massage Therapy",
    "landing.cta_title": "Bereit, Ihre Buchungen zu optimieren?",
    "landing.cta_desc": "Schließen Sie sich Hunderten von Unternehmen an, die Slotify bereits nutzen. In Minuten eingerichtet, keine Kreditkarte erforderlich.",
    "landing.cta_button": "Kostenlos loslegen",
    "landing.footer_rights": "© 2026 Slotify. Alle Rechte vorbehalten.",
    // Legal pages
    "legal.privacy_title": "Datenschutzerklärung",
    "legal.terms_title": "Allgemeine Geschäftsbedingungen",
    "legal.back_home": "Zurück zur Startseite",
    "legal.last_updated": "Zuletzt aktualisiert",
    "privacy.intro_title": "1. Einleitung",
    "privacy.intro_text": "Slotify (\"wir\", \"uns\" oder \"unser\") verpflichtet sich zum Schutz Ihrer Privatsphäre. Diese Datenschutzerklärung erläutert, wie wir Ihre Informationen sammeln, verwenden, offenlegen und schützen, wenn Sie unsere Online-Buchungsplattform und zugehörige Dienste nutzen.",
    "privacy.data_collect_title": "2. Erhobene Daten",
    "privacy.data_collect_text": "Wir erfassen Informationen, die Sie direkt angeben, darunter: Ihren Namen, Ihre E-Mail-Adresse, Telefonnummer und Geschäftsdaten bei der Kontoerstellung; Buchungs- und Termindaten; Zahlungsinformationen, die über unsere Zahlungsanbieter verarbeitet werden; und Nutzungsdaten wie IP-Adressen, Browsertyp und besuchte Seiten.",
    "privacy.data_use_title": "3. Verwendung Ihrer Daten",
    "privacy.data_use_text": "Wir verwenden die gesammelten Informationen, um: unsere Buchungsplattform bereitzustellen und zu warten; Termine zu verarbeiten und Bestätigungen und Erinnerungen zu senden; unsere Dienste und Benutzererfahrung zu verbessern; mit Ihnen über Updates, Support und Werbeangebote zu kommunizieren; gesetzlichen Verpflichtungen nachzukommen; und Betrug oder Missbrauch zu erkennen und zu verhindern.",
    "privacy.cookies_title": "4. Cookies & Tracking",
    "privacy.cookies_text": "Wir verwenden Cookies und ähnliche Technologien, um Ihre Erfahrung zu verbessern. Notwendige Cookies sind immer aktiv. Analyse- und Marketing-Cookies werden nur mit Ihrer ausdrücklichen Zustimmung über unser Cookie-Banner aktiviert.",
    "privacy.sharing_title": "5. Datenweitergabe",
    "privacy.sharing_text": "Wir verkaufen Ihre persönlichen Daten nicht. Wir können Informationen teilen mit: Dienstleistern, die beim Betrieb unserer Plattform helfen; Rechtsbehörden, wenn gesetzlich vorgeschrieben; und Geschäftspartnern nur mit Ihrer ausdrücklichen Zustimmung.",
    "privacy.retention_title": "6. Datenspeicherung",
    "privacy.retention_text": "Wir bewahren Ihre persönlichen Daten auf, solange Ihr Konto aktiv ist oder soweit zur Erbringung von Dienstleistungen erforderlich. Nach Kontolöschung können wir bestimmte Daten bis zu 30 Tage für Sicherungszwecke aufbewahren.",
    "privacy.rights_title": "7. Ihre Rechte",
    "privacy.rights_text": "Nach geltendem Datenschutzrecht (einschließlich DSGVO) haben Sie das Recht auf: Zugang, Berichtigung oder Löschung Ihrer persönlichen Daten; Einschränkung oder Widerspruch gegen die Verarbeitung; Datenübertragbarkeit; jederzeitigen Widerruf der Einwilligung; und Beschwerde bei einer Aufsichtsbehörde.",
    "privacy.contact_title": "8. Kontakt",
    "privacy.contact_text": "Wenn Sie Fragen zu dieser Datenschutzerklärung haben oder Ihre Datenschutzrechte ausüben möchten, kontaktieren Sie uns unter privacy@slotify.app.",
    "terms.acceptance_title": "1. Annahme der Bedingungen",
    "terms.acceptance_text": "Durch den Zugriff auf oder die Nutzung der Dienste von Slotify stimmen Sie diesen Allgemeinen Geschäftsbedingungen zu. Wenn Sie nicht einverstanden sind, nutzen Sie unsere Plattform bitte nicht.",
    "terms.service_title": "2. Beschreibung des Dienstes",
    "terms.service_text": "Slotify bietet eine Online-Buchungs- und Terminverwaltungsplattform für Unternehmen. Unser Service umfasst ein Dashboard zur Verwaltung von Reservierungen, Dienstleistungen, Kunden und Einstellungen sowie eine öffentliche Buchungsseite für Ihre Kunden.",
    "terms.accounts_title": "3. Benutzerkonten",
    "terms.accounts_text": "Sie müssen bei der Kontoerstellung genaue und vollständige Angaben machen. Sie sind für die Vertraulichkeit Ihrer Zugangsdaten und alle Aktivitäten unter Ihrem Konto verantwortlich.",
    "terms.payment_title": "4. Zahlungen & Abrechnung",
    "terms.payment_text": "Slotify bietet eine kostenlose Testphase gefolgt von einem kostenpflichtigen Abonnement. Zahlungen werden über unseren Zahlungsanbieter (Mollie) abgewickelt. Abonnements verlängern sich automatisch, sofern nicht gekündigt. Preise verstehen sich zuzüglich anfallender Steuern, sofern nicht anders angegeben.",
    "terms.ip_title": "5. Geistiges Eigentum",
    "terms.ip_text": "Alle Inhalte, Funktionen und Funktionalitäten der Slotify-Plattform sind unser Eigentum und durch Gesetze zum geistigen Eigentum geschützt. Sie behalten das Eigentum an allen Daten, die Sie in die Plattform eingeben.",
    "terms.liability_title": "6. Haftungsbeschränkung",
    "terms.liability_text": "Slotify wird \"wie besehen\" ohne jegliche Garantien bereitgestellt. Wir haften nicht für indirekte, beiläufige oder Folgeschäden, die aus Ihrer Nutzung der Plattform entstehen.",
    "terms.termination_title": "7. Kündigung",
    "terms.termination_text": "Jede Partei kann die Vereinbarung jederzeit kündigen. Nach Kündigung erlischt Ihr Recht zur Nutzung der Plattform sofort. Wir bewahren Ihre Daten 30 Tage nach Kündigung auf.",
    "terms.changes_title": "8. Änderungen der Bedingungen",
    "terms.changes_text": "Wir behalten uns das Recht vor, diese Bedingungen jederzeit zu ändern. Wir werden Sie über wesentliche Änderungen per E-Mail oder über die Plattform informieren.",
    "terms.governing_title": "9. Anwendbares Recht",
    "terms.governing_text": "Diese Bedingungen unterliegen dem niederländischen Recht. Streitigkeiten werden vor den zuständigen Gerichten der Niederlande verhandelt.",
    // Cookie banner
    "cookie.message": "Wir verwenden Cookies, um Ihre Erfahrung zu verbessern und den Seitenverkehr zu analysieren. Sie können Ihre Einstellungen unten verwalten.",
    "cookie.accept_all": "Alle akzeptieren",
    "cookie.decline": "Ablehnen",
    "cookie.manage": "Verwalten",
    "cookie.manage_desc": "Wählen Sie, welche Cookies Sie zulassen möchten. Notwendige Cookies sind immer aktiv.",
    "cookie.necessary": "Notwendig",
    "cookie.necessary_desc": "Erforderlich für die Funktion der Website",
    "cookie.analytics": "Analyse",
    "cookie.analytics_desc": "Helfen uns zu verstehen, wie Sie die Website nutzen",
    "cookie.marketing": "Marketing",
    "cookie.marketing_desc": "Für gezielte Werbung verwendet",
    "cookie.save": "Einstellungen speichern",
    "cookie.back": "Zurück",
    "settings.team": "Team",
    "settings.team_desc": "Teammitglieder einladen und verwalten",
    "team.title": "Team-Einstellungen",
    "team.subtitle": "Verwalten Sie Ihre Teammitglieder und senden Sie Einladungen.",
    "team.members": "Mitglieder",
    "team.pending_invites": "Ausstehende Einladungen",
    "team.invite": "Mitglied einladen",
    "team.invite_desc": "Senden Sie eine E-Mail-Einladung, um Ihrer Organisation beizutreten.",
    "team.email": "E-Mail-Adresse",
    "team.role": "Rolle",
    "team.role_admin": "Administrator",
    "team.role_member": "Mitglied",
    "team.send_invite": "Einladung senden",
    "team.sending": "Senden…",
    "team.invite_sent": "Einladung gesendet",
    "team.invite_error": "Einladung konnte nicht gesendet werden",
    "team.cancel_invite": "Einladung abbrechen",
    "team.invite_cancelled": "Einladung abgebrochen",
    "team.no_members": "Noch keine Teammitglieder.",
    "team.no_invites": "Keine ausstehenden Einladungen.",
    "team.joined": "Beigetreten",
    "team.invited_on": "Eingeladen am",
    "team.you": "(Sie)",
    "team.already_in_org": "Dieser Benutzer ist bereits in Ihrer Organisation",
    "invite.title": "Sie wurden eingeladen!",
    "invite.join_org": "{org} beitreten",
    "invite.accept": "Einladung annehmen",
    "invite.accepting": "Annehmen…",
    "invite.accepted": "Sie sind der Organisation beigetreten!",
    "invite.invalid": "Diese Einladung ist ungültig oder abgelaufen.",
    "invite.login_first": "Melden Sie sich an oder erstellen Sie ein Konto mit dieser E-Mail-Adresse, um die Einladung anzunehmen.",
    "invite.email_mismatch": "Melden Sie sich mit der E-Mail-Adresse an, an die die Einladung gesendet wurde.",
    "invite.already_in_org": "Sie gehören bereits einer Organisation an.",
    "settings.extensions": "Erweiterungen",
    "settings.extensions_desc": "Integrationen und Add-ons für Ihre Organisation aktivieren",
    "ext.title": "Erweiterungen",
    "ext.subtitle": "Aktivieren und konfigurieren Sie Integrationen für Ihre Organisation.",
    "ext.loading": "Laden…",
    "ext.enabled": "Erweiterung aktiviert",
    "ext.disabled": "Erweiterung deaktiviert",
    "ext.saved": "Einstellungen gespeichert!",
    "ext.save_error": "Einstellungen konnten nicht gespeichert werden",
    "ext.configure": "Konfigurieren",
    "ext.experimental": "Experimentell",
    "ext.self_service_title": "Kunden-Selbstbedienung",
    "ext.self_service_desc": "Ermöglichen Sie Kunden, ihre Termine mit Reservierungsnummer und E-Mail zu stornieren oder umzubuchen.",
    "ext.statistics_title": "Statistiken",
    "ext.statistics_desc": "Sehen Sie detaillierte Diagramme und Einblicke zu Ihren Reservierungen, Umsätzen und Kunden.",
    "nav.statistics": "Statistiken",
    "nav.employees": "Mitarbeiter",
    "ext.employees_title": "Mitarbeiter",
    "ext.employees_desc": "Verwalten Sie Teammitglieder, weisen Sie Dienste zu und ermöglichen Sie parallele Buchungen.",
    "employees.title": "Mitarbeiter",
    "employees.add": "Mitarbeiter hinzufügen",
    "employees.no_items": "Noch keine Mitarbeiter. Fügen Sie Ihren ersten Mitarbeiter hinzu.",
    "employees.col_name": "Name",
    "employees.col_status": "Status",
    "employees.active": "Aktiv",
    "employees.inactive": "Inaktiv",
    "employees.edit": "Bearbeiten",
    "employees.delete": "Löschen",
    "employees.deleted": "gelöscht",
    "employees.delete_title": "Mitarbeiter löschen",
    "employees.delete_desc_pre": "Sind Sie sicher, dass Sie ",
    "employees.delete_desc_post": " löschen möchten? Dies kann nicht rückgängig gemacht werden.",
    "employees.cancel": "Abbrechen",
    "employees.back": "Zurück",
    "employees.edit_title": "Mitarbeiter bearbeiten",
    "employees.add_title": "Mitarbeiter hinzufügen",
    "employees.basic_info": "Basisinformationen",
    "employees.name": "Name",
    "employees.name_placeholder": "z.B. Max Mustermann",
    "employees.image_url": "Foto-URL",
    "employees.photo_hint": "Klicken Sie auf den Avatar, um ein Foto hochzuladen",
    "employees.status_label": "Aktiv",
    "employees.status_active_desc": "Dieser Mitarbeiter ist sichtbar und buchbar.",
    "employees.status_inactive_desc": "Dieser Mitarbeiter ist verborgen und nicht buchbar.",
    "employees.services": "Zugewiesene Dienste",
    "employees.no_services": "Noch keine Dienste erstellt.",
    "employees.working_hours": "Benutzerdefinierte Arbeitszeiten",
    "employees.working_hours_desc": "Überschreiben Sie die Öffnungszeiten der Organisation für diesen Mitarbeiter.",
    "employees.closed": "Frei",
    "employees.saving": "Speichern…",
    "employees.save_changes": "Änderungen speichern",
    "employees.create": "Mitarbeiter erstellen",
    "employees.created": "Mitarbeiter erstellt",
    "employees.updated": "Mitarbeiter aktualisiert",
    "employees.ext_title": "Mitarbeiter-Einstellungen",
    "employees.ext_subtitle": "Konfigurieren Sie, wie Mitarbeiter in Ihrem Buchungsablauf funktionieren.",
    "employees.allow_customer_selection": "Kunden dürfen Mitarbeiter wählen",
    "employees.allow_customer_selection_desc": "Zeigen Sie einen Mitarbeiter-Auswähler im Buchungsablauf an.",
    "employees.allow_no_preference": "'Keine Präferenz' Option erlauben",
    "employees.allow_no_preference_desc": "Lassen Sie Kunden die Auswahl überspringen und automatisch zuweisen.",
    "employees.employee_step_before_service": "Mitarbeiterauswahl vor dem Service",
    "employees.employee_step_before_service_desc": "Zeigen Sie den Mitarbeiter-Auswähler vor der Service-Auswahl an.",
    "employees.save_settings": "Einstellungen speichern",
    "blocked.employee": "Mitarbeiter",
    "blocked.all_employees": "Alle Mitarbeiter",
    "days.mon": "Mo",
    "days.tue": "Di",
    "days.wed": "Mi",
    "days.thu": "Do",
    "days.fri": "Fr",
    "days.sat": "Sa",
    "days.sun": "So",
    "stats.title": "Statistiken",
    "stats.subtitle": "Einblicke in Ihre Reservierungen und Umsätze.",
    "stats.period_this_month": "Dieser Monat",
    "stats.period_prev_month": "Vorheriger Monat",
    "stats.period_custom": "Benutzerdefiniert",
    "stats.period_next_month": "Nächster Monat",
    "stats.from": "Von",
    "stats.to": "Bis",
    "stats.total_bookings": "Gesamtbuchungen",
    "stats.total_revenue": "Gesamtumsatz",
    "stats.unique_customers": "Einzigartige Kunden",
    "stats.cancellations": "Stornierungen",
    "stats.bookings_per_day": "Buchungen pro Tag",
    "stats.revenue_per_week": "Umsatz pro Woche",
    "stats.by_service": "Nach Service",
    "stats.by_status": "Nach Status",
    "stats.bookings": "Buchungen",
    "stats.cancelled": "Storniert",
    "stats.revenue": "Umsatz",
    "stats.no_data": "Keine Daten für diesen Zeitraum",
    "stats.status_pending": "Ausstehend",
    "stats.status_confirmed": "Bestätigt",
    "stats.status_cancelled": "Storniert",
    "stats.status_completed": "Abgeschlossen",
    "stats.status_no_show": "Nicht erschienen",
    "stats.status_awaiting_payment": "Warten auf Zahlung",
    "stats.status_expired": "Abgelaufen",
    "ext.ss_title": "Kunden-Selbstbedienung",
    "ext.ss_subtitle": "Konfigurieren Sie, wie Kunden ihre eigenen Buchungen verwalten können.",
    "ext.ss_saved": "Selbstbedienungseinstellungen gespeichert!",
    "ext.ss_allow_cancel": "Stornierungen erlauben",
    "ext.ss_allow_cancel_desc": "Kunden können ihre eigenen Reservierungen stornieren",
    "ext.ss_cancel_deadline": "Stornierungsfrist",
    "ext.ss_hours_before": "Stunden vor dem Termin",
    "ext.ss_allow_rebook": "Umbuchung erlauben",
    "ext.ss_allow_rebook_desc": "Kunden können ihre eigenen Reservierungen umbuchen",
    "ext.ss_rebook_deadline": "Umbuchungsfrist",
    "ext.ss_visibility": "Sichtbarkeit",
    "ext.ss_show_shop": "Im Shop anzeigen",
    "ext.ss_show_shop_desc": "Link zur Buchungsverwaltung auf der Shop-Seite anzeigen",
    "ext.ss_show_email": "In E-Mail anzeigen",
    "ext.ss_show_email_desc": "Link zur Buchungsverwaltung in der Bestätigungs-E-Mail einfügen",
    "ext.ss_show_thank_you": "Auf Dankesseite anzeigen",
    "ext.ss_show_thank_you_desc": "Link zur Buchungsverwaltung auf der Dankesseite nach der Buchung anzeigen",
    "ext.ss_saving": "Speichern…",
    "ext.ss_save": "Einstellungen speichern",
    "ext.ss_not_available": "Diese Funktion ist nicht verfügbar.",
    "ext.ss_manage_title": "Buchung verwalten",
    "ext.ss_reservation_number": "Reservierungsnummer",
    "ext.ss_service": "Service",
    "ext.ss_datetime": "Datum & Uhrzeit",
    "ext.ss_status": "Status",
    "ext.ss_rebook_btn": "Umbuchen",
    "ext.ss_cancel_btn": "Buchung stornieren",
    "ext.ss_cancel_confirm_title": "Diese Buchung stornieren?",
    "ext.ss_cancel_confirm_desc": "Sind Sie sicher, dass Sie diese Buchung stornieren möchten? Dies kann nicht rückgängig gemacht werden.",
    "ext.ss_keep": "Buchung behalten",
    "ext.ss_cancelling": "Stornierung…",
    "ext.ss_confirm_cancel": "Ja, stornieren",
    "ext.ss_deadline_passed": "Die Frist zur Änderung dieser Buchung ist abgelaufen.",
    "ext.ss_pick_new_datetime": "Neues Datum & Uhrzeit wählen",
    "ext.ss_available_times": "Verfügbare Zeiten",
    "ext.ss_rebooking": "Umbuchung…",
    "ext.ss_confirm_rebook": "Neue Zeit bestätigen",
    "ext.ss_looking_up": "Suche…",
    "ext.ss_lookup": "Meine Buchung finden",
    "ext.ss_not_found": "Keine Buchung gefunden. Bitte überprüfen Sie Ihre Reservierungsnummer und E-Mail.",
    "ext.ss_cancelled_title": "Buchung storniert",
    "ext.ss_cancelled_desc": "Ihre Buchung wurde erfolgreich storniert.",
    "ext.ss_rebooked_title": "Buchung umgebucht",
    "ext.ss_rebooked_desc": "Ihre Buchung wurde erfolgreich umgebucht.",
    "ext.ss_time": "Uhrzeit",
    "ext.ss_duration": "Dauer",
    "ext.ss_rebook_notifications": "Umbuchungsbenachrichtigungen",
    "ext.ss_rebook_notifications_desc": "E-Mail-Benachrichtigungen senden, wenn eine Buchung umgebucht wird.",
    "ext.ss_notify_customer": "Kunden benachrichtigen",
    "ext.ss_notify_customer_desc": "Dem Kunden eine E-Mail über die umgebuchte Buchung senden.",
    "ext.ss_notify_organisation": "Organisation benachrichtigen",
    "ext.ss_notify_organisation_desc": "Der Organisation eine E-Mail über die umgebuchte Buchung senden.",
    "ext.ss_cancel_notifications": "Stornierungsbenachrichtigungen",
    "ext.ss_cancel_notifications_desc": "E-Mail-Benachrichtigungen senden, wenn eine Buchung storniert wird.",
    "ext.ss_cancel_notify_customer": "Kunden benachrichtigen",
    "ext.ss_cancel_notify_customer_desc": "Dem Kunden eine E-Mail senden, wenn die Buchung storniert wird.",
    "ext.ss_cancel_notify_organisation": "Organisation benachrichtigen",
    "ext.ss_cancel_notify_organisation_desc": "Der Organisation eine E-Mail senden, wenn eine Buchung storniert wird.",
    "ext.ss_manage_link": "Buchung stornieren oder umbuchen",
    "ext.ss_allowed_statuses": "Erlaubte Status",
    "ext.ss_allowed_statuses_desc": "Nur Reservierungen mit diesen Status können vom Kunden geändert werden.",
    "ext.ss_status_not_allowed": "Diese Reservierung kann mit ihrem aktuellen Status nicht geändert werden.",
    "ext.pao_card_title": "Bezahlung bei Bestellung",
    "ext.pao_card_desc": "Kunden müssen bezahlen, bevor ihre Buchung bestätigt wird.",
    "ext.pao_title": "Bezahlung bei Bestellung",
    "ext.pao_subtitle": "Konfigurieren Sie Ihren Zahlungsanbieter, damit Kunden bei der Buchung bezahlen.",
    "ext.pao_select_psp": "Zahlungsanbieter",
    "ext.pao_select_psp_desc": "Wählen Sie den Zahlungsanbieter für die Zahlungsabwicklung.",
    "ext.pao_mollie_desc": "Beliebter europäischer PSP mit iDEAL, Bancontact, Kreditkarten und mehr.",
    "ext.pao_stripe_desc": "Globaler PSP mit Kreditkarten, Apple Pay, Google Pay und mehr.",
    "ext.pao_test_mode": "Testmodus",
    "ext.pao_test_mode_desc": "Verwenden Sie Test/Sandbox-API-Schlüssel. Es werden keine echten Zahlungen verarbeitet.",
    "ext.pao_api_keys": "API-Schlüssel",
    "ext.pao_api_keys_desc": "Geben Sie Ihre API-Schlüssel aus dem Dashboard Ihres Zahlungsanbieters ein.",
    "ext.pao_mollie_api_key": "Mollie API-Schlüssel",
    "ext.pao_mollie_key_hint": "Finden Sie diesen in Ihrem Mollie-Dashboard unter Entwickler → API-Schlüssel.",
    "ext.pao_stripe_publishable_key": "Stripe öffentlicher Schlüssel",
    "ext.pao_stripe_secret_key": "Stripe geheimer Schlüssel",
    "ext.pao_stripe_key_hint": "Finden Sie diese in Ihrem Stripe-Dashboard unter Entwickler → API-Schlüssel.",
    "ext.pao_no_keys_warning": "Sie haben noch keine API-Schlüssel eingegeben. Die Erweiterung funktioniert erst, wenn Schlüssel konfiguriert sind.",
    "ext.pao_expiry_title": "Zahlungsfrist",
    "ext.pao_expiry_desc": "Reservierungen automatisch stornieren, wenn die Zahlung nicht innerhalb dieser Zeit abgeschlossen wird.",
    "ext.pao_expiry_unit": "Minuten",
    "ext.pao_save": "Einstellungen speichern",
    "ext.pao_saving": "Speichern…",
    "ext.pao_saved": "Zahlungseinstellungen gespeichert",
    "extpage.back_home": "Zurück zur Startseite",
    "extpage.features_title": "Was enthalten ist",
    "extpage.ss_title": "Kunden-Self-Service",
    "extpage.ss_subtitle": "Ermöglichen Sie Ihren Kunden, ihre eigenen Buchungen zu verwalten. Reduzieren Sie No-Shows und sparen Sie Zeit mit automatischen Stornierungen und Umbuchungen.",
    "extpage.ss_feat_cancel": "Kunden können ihre Termine innerhalb einer konfigurierbaren Frist stornieren",
    "extpage.ss_feat_rebook": "Kunden können auf einen neuen verfügbaren Zeitslot umbuchen",
    "extpage.ss_feat_deadline": "Legen Sie individuelle Fristen für Stornierungen und Umbuchungen fest",
    "extpage.ss_feat_notifications": "Automatische E-Mail-Benachrichtigungen bei Stornierungen und Umbuchungen",
    "extpage.ss_feat_shop": "Zeigen Sie einen Link zur Buchungsverwaltung direkt auf Ihrer Shop-Seite",
    "extpage.ss_feat_email": "Fügen Sie einen Link zur Buchungsverwaltung in Bestätigungs-E-Mails ein",
    "extpage.stats_title": "Statistiken & Einblicke",
    "extpage.stats_subtitle": "Erhalten Sie einen vollständigen Überblick über Ihre Geschäftsleistung mit detaillierten Diagrammen, KPIs und Kundenanalysen.",
    "extpage.stats_feat_kpi": "Wichtige Leistungskennzahlen für Buchungen, Umsatz und Kunden",
    "extpage.stats_feat_charts": "Schöne Balken- und Kreisdiagramme für visuelle Einblicke",
    "extpage.stats_feat_revenue": "Umsatz nach Service und Zeitraum verfolgen",
    "extpage.stats_feat_customers": "Kundenanalysen einschließlich neu vs wiederkehrend",
    "extpage.stats_feat_periods": "Nach aktuellem, vorherigem oder nächstem Monat und benutzerdefinierten Zeiträumen filtern",
    "extpage.stats_feat_export": "Übersicht der Buchungsstatus einschließlich No-Shows und Stornierungen",
    "extpage.pao_title": "Bezahlung bei Bestellung",
    "extpage.pao_subtitle": "Verlangen Sie von Kunden, bei der Buchung zu bezahlen. Reduzieren Sie No-Shows und sichern Sie Ihren Umsatz mit integrierten Online-Zahlungen.",
    "extpage.pao_feat_online": "Kunden bezahlen direkt während des Buchungsprozesses",
    "extpage.pao_feat_mollie": "Integriert mit Mollie für sichere Zahlungsabwicklung",
    "extpage.pao_feat_status": "Automatische Zahlungsstatusverfolgung bei jeder Reservierung",
    "extpage.pao_feat_webhook": "Echtzeit-Zahlungsstatusupdates über Webhooks",
    "extpage.pao_feat_secure": "PCI-konforme sichere Zahlungsabwicklung",
    "extpage.pao_feat_auto": "Buchungen werden nach erfolgreicher Zahlung automatisch bestätigt",
    "extpage.emp_title": "Mitarbeiter",
    "extpage.emp_subtitle": "Weisen Sie Mitarbeiter Dienstleistungen zu, legen Sie individuelle Arbeitszeiten fest und lassen Sie Kunden bei der Buchung ihren bevorzugten Mitarbeiter wählen.",
    "extpage.emp_feat_assign": "Weisen Sie bestimmte Mitarbeiter einzelnen Dienstleistungen zu",
    "extpage.emp_feat_hours": "Legen Sie individuelle Arbeitszeiten pro Mitarbeiter fest",
    "extpage.emp_feat_services": "Mitarbeiter werden nur für Dienstleistungen angezeigt, die sie erbringen können",
    "extpage.emp_feat_availability": "Automatische Verfügbarkeitsfilterung basierend auf Mitarbeiterplänen",
    "extpage.emp_feat_calendar": "Mitarbeiter werden im Kalender mit farbcodierten Blöcken angezeigt",
    "extpage.emp_feat_preference": "Kunden können bei der Buchung ihren bevorzugten Mitarbeiter auswählen",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const getDefaultLanguage = (): Language => {
    const browserLang = navigator.language?.split("-")[0] || "";
    const supported: Language[] = ["nl", "en", "fr", "de"];
    return supported.includes(browserLang as Language) ? (browserLang as Language) : "nl";
  };

  const [language, setLanguage] = useState<Language>(getDefaultLanguage);

  const t = (key: string) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
